// App.tsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { fetchProposalSections } from "./proposalSections"; // <-- add this import near the top
import {
  AppUserSettings,
  DEFAULT_SETTINGS,
  fetchSettings,
  readCachedSettings,
  upsertSettings,
  writeCachedSettings,
} from "./settings";
import { isProposalTextEmpty, type ProposalText } from "./shared/proposalText";
import PricingAdmin from "./PricingAdmin";
import { usePricingRefreshTrigger } from "./usePricingRefreshTrigger";
import ProposalPage from "./ProposalPage";
import SettingsPage from "./SettingsPage";
import "./styles.css";
import "./theme.css";
import { supabase } from "./supabaseClient";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "./supabaseClient";
import { isAuthRetryableFetchError } from "@supabase/supabase-js";
import AnalyticsPage from "./AnalyticsPage";
import HomePage from "./HomePage";
import {
  getRecents,
  pushRecent,
  clearRecents,
  type RecentFile,
} from "./shared/recents";
import ReviewProposalPage from "./ReviewProposalPage";
import ResetPasswordPage from "./ResetPasswordPage";
import AcceptInvitePage from "./AcceptInvitePage";
import { uid } from "./utils/uid";
import UsersLicensesPage from "./UsersLicensesPage";
import AuthPage from "./AuthPage";
import CreateOrgPage from "./CreateOrgPage";
import { saveSignatureCopy } from "./components/Contract/SignatureImage";
import {
  absorbAutoContract,
  differsFromBaseline,
  estimateFingerprint,
  FILE_RECORD_KEY,
  hasUnsavedWork,
  lateDraftNeedsQuestion,
  makeFileRecord,
  parseFileRecord,
  snapshotHasScopeContent,
  type Fingerprint,
} from "./shared/dirtyBaseline";
import ExpressionNumberInput, {
  isNumberFieldEditing,
} from "./components/ExpressionNumberInput";
import ContractPage from "./ContractPage";
import AccessRevoked from "./AccessRevoked";
import { membershipLookupAnswered, wipeLocalCompanyData } from "./shared/accessWipe";
import VisualLibraryPage from "./VisualLibraryPage";
import {
  listVisualsForOrg,
  resolveProposalVisualsFrom,
  type ProposalVisual,
  type VisualLibraryRecord,
} from "./visualLibrary";
import {
  computeEstimate,
  normalizeName,
  normalizeCat,
  isBenchCategory,
  isConstructionTypeCategory,
  getConstructionTypeLabel,
  getConstructionAdjustment,
  getPermitTierForTotal,
  getSmallJobTierForTotal,
  computeEffectiveStairsRate,
  computeEffectiveSkirtingRate,
  findBenchBaseRow,
  addItemOptionsForRow as addItemOptionsForRowPure,
  buildAddItemCategories,
  excludeHiddenMainStairs,
  mergeFrozenWithLive,
  CONSTRUCTION_TYPES,
  BENCH_TYPES,
} from "./shared/pricing";
import type {
  PricingItemRow,
  PricingCategoryRow,
  AddItemRow,
  BenchTypeValue,
} from "./shared/pricing";
import {
  loadCloudDraftWithStatus,
  saveCloudDraft,
  makeDebouncer,
  type DraftSnapshot,
} from "./shared/draft";
import {
  findMissingSections,
  missingSignature,
  type MissingSection,
} from "./shared/completeness";
import {
  EST_EXT,
  defaultFileName,
  normalizeFileName,
  downloadTextFile,
} from "./shared/estimateFile";
import { dlog } from "./debugLog";
import {
  buildEmailHtml,
  buildEmailText,
  buildEmailVars,
  buildProposalPayload,
  buildReviewLink,
  emailFooterFromSettings,
  fetchProposalSectionsSnapshot,
  invokeSendProposalEmail,
  isSenderInfoComplete,
  renderTemplate,
  renderEmailBody,
  resolveProposalLineItems,
  saveOrUpdateProposal,
  SENDER_INFO_MISSING_MESSAGE,
} from "./shared/proposalSend";
import { formatCustomerPhone } from "./shared/format";
import { APP_VERSION } from "./version";
import { useAppDialog } from "./appDialog";
import { applyUpdate as applySwUpdate } from "./serviceWorkerRegistration";
import { subscribeSwUpdate, getSwUpdateReady } from "./swUpdate";
import { canImportFromZoho as canImportFromZohoEmail } from "./shared/zohoImport";
import { clearLeadList, type LeadContact } from "./shared/leadList";
import { useLeadList } from "./shared/useLeadList";
import LeadPickerModal from "./components/LeadPickerModal";
import { pricesAsOfLabel, snapshotDateForSave } from "./shared/pricesAsOf";

// Shown when a previously logged-in user is offline AND their 3-day grace
// has lapsed — an honest "reconnect & sign in" instead of a login form that
// can't possibly work without a connection.
function OfflineExpiredScreen() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-8">
      <div className="max-w-xl rounded-2xl border border-slate-800 bg-slate-900/70 p-8 text-center">
        <div className="text-xl font-semibold mb-2">Offline access expired</div>
        <p className="text-sm text-slate-300">
          Please reconnect to the internet and sign in to continue. Offline
          access lasts 3 days.
        </p>
        <button
          className="mt-4 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-900"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    </div>
  );
}

function BootScreen({ label = "Loading…" }: { label?: string }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(180deg, #f3f4f6 0%, #eef2ff 100%)",
        fontFamily: "system-ui",
      }}
    >
      <div
        style={{
          width: 420,
          maxWidth: "92vw",
          padding: 22,
          borderRadius: 16,
          background: "white",
          border: "1px solid rgba(0,0,0,0.10)",
          boxShadow: "0 18px 45px rgba(0,0,0,0.12)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "#111",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 900,
              letterSpacing: 0.5,
            }}
          >
            DU
          </div>

          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 900 }}>Deck Estimator</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>Decks Unique</div>
          </div>
        </div>

        <div
          style={{
            marginTop: 16,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: 999,
              border: "3px solid rgba(0,0,0,0.12)",
              borderTopColor: "rgba(0,0,0,0.65)",
              animation: "duSpin 0.9s linear infinite",
            }}
          />
          <div style={{ fontWeight: 800, opacity: 0.8 }}>{label}</div>
        </div>

        <style>
          {`@keyframes duSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
        </style>
      </div>
    </div>
  );
}

type SidebarNavItemProps = {
  label: string;
  isActive: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
};

function SidebarNavItem({ label, isActive, onClick, icon }: SidebarNavItemProps) {
  return (
    <button
      className={`sidebar-nav-item ${isActive ? "is-active" : ""}`}
      onClick={onClick}
      type="button"
    >
      {icon ? icon : <span className="sidebar-nav-dot" />}
      <span>{label}</span>
    </button>
  );
}

// Sidebar nav icons — stroke="currentColor" so theme color tokens drive them.
const NAV_ICONS = {
  home: (
    <svg className="sidebar-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V21h14V9.5" />
    </svg>
  ),
  estimator: (
    <svg className="sidebar-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 9h6v6H9z" />
    </svg>
  ),
  proposal: (
    <svg className="sidebar-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  visualLibrary: (
    <svg className="sidebar-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
    </svg>
  ),
  contract: (
    <svg className="sidebar-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M9 13h6M9 17h6M9 9h1" />
    </svg>
  ),
  pricing: (
    <svg className="sidebar-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  analytics: (
    <svg className="sidebar-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  settings: (
    <svg className="sidebar-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  users: (
    <svg className="sidebar-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
    </svg>
  ),
};

// Theme toggle: flips data-theme on <html>, persists to localStorage.
function ThemeToggle() {
  const handleClick = () => {
    const html = document.documentElement;
    const current = html.getAttribute("data-theme") === "light" ? "light" : "dark";
    const next = current === "dark" ? "light" : "dark";
    html.setAttribute("data-theme", next);
    try {
      localStorage.setItem("theme", next);
    } catch (e) {
      // ignore
    }
  };

  return (
    <button
      type="button"
      className="tesla-btn tesla-btn-icon theme-toggle"
      onClick={handleClick}
      title="Toggle theme"
      aria-label="Toggle theme"
    >
      <svg className="moon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
      </svg>
      <svg className="sun" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
        <circle cx="12" cy="12" r="4" />
        <line x1="12" y1="2" x2="12" y2="4" />
        <line x1="12" y1="20" x2="12" y2="22" />
        <line x1="4.93" y1="4.93" x2="6.34" y2="6.34" />
        <line x1="17.66" y1="17.66" x2="19.07" y2="19.07" />
        <line x1="2" y1="12" x2="4" y2="12" />
        <line x1="20" y1="12" x2="22" y2="12" />
        <line x1="4.93" y1="19.07" x2="6.34" y2="17.66" />
        <line x1="17.66" y1="6.34" x2="19.07" y2="4.93" />
      </svg>
    </button>
  );
}

// ===============================
// RECENT FILES (localStorage)
// ===============================
const PRICING_ITEMS_CACHE_KEY = "du_cache::pricing_items2_v1";
const PRICING_CATS_CACHE_KEY = "du_cache::pricing_categories_v1";
const PRICING_CACHE_TS_KEY = "du_cache::pricing_ts_v1";

/** pricing_items2 rows as the Estimator expects them (boot load + refresh). */
function cleanPricingRows(itemsRaw: any[]) {
  return (itemsRaw || []).map((r: any) => ({
    ...r,
    active: r.active !== false,
    deleted_at: r.deleted_at ?? null,
    category: r.category ?? null,
    category2: r.category2 ?? null,
  }));
}
const OFFLINE_ORG_KEY = "du_offline_org_id";
const OFFLINE_LAST_KEY = "du_last_online";
const OFFLINE_ROLE_KEY = "du_offline_role";
const OFFLINE_USER_KEY = "du_offline_user_id";
const OFFLINE_EMAIL_KEY = "du_offline_email";
const AUTH_SESSION_HINT_KEY = "du_auth_session_hint";
const AUTH_BOOTSTRAP_TIMEOUT_MS = 2000;
const AUTH_URL_PARSE_TIMEOUT_MS = 1500;
const ORG_LOOKUP_TIMEOUT_MS = 2500;
const STARTUP_FETCH_TIMEOUT_MS = 3500;
// How long a device may work offline after its last SUCCESSFUL membership
// check (every launch with internet renews it). Also the longest a removed
// person keeps offline access on a laptop that never reconnects. 24h (Jay,
// 2026-09-24): a day in the field without signal works; a second doesn't.
const OFFLINE_GRACE_MS = 24 * 60 * 60 * 1000;
const LOCAL_DESKTOP_ORG_ID = "local-desktop";

// ============================================================
// AUTH SKIP — DEV LOCALHOST ONLY (deliberate distinction)
// ------------------------------------------------------------
// We skip the login screen ONLY when running against a localhost
// dev server (e.g. the VPS test server on :3001), purely for
// testing convenience.
//
// The REAL packaged desktop app (Electron) must NOT skip login:
// it loads from the app:/file: protocol (hostname is empty), so
// this returns false there and the normal Supabase auth +
// org_members access check runs — the same path the web app uses,
// so disabling a user in Users/Licenses locks them out of desktop.
//
// NOTE: window.estimator.isDesktop is intentionally NOT consulted
// here. Desktop detection still exists (see BUILD_PLATFORM and the
// preload bridge) and is kept for desktop-only features like file
// I/O and the auto-updater — we only stopped using it to skip auth.
// ============================================================
/** The "replace what's on screen?" question asked before opening a file. */
const OPEN_ANYWAY_DIALOG = {
  title: "You have unsaved changes.",
  message: "Open another file anyway? Your changes will be lost.",
  confirmLabel: "Open anyway",
};

function isDevLocalhostSkip() {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname || "";
  return hostname === "localhost" || hostname === "127.0.0.1";
}

function createLocalDesktopSession() {
  return {
    user: {
      id: "local-desktop-user",
      email: "local@desktop.estimator",
    },
  };
}

function startupLog(step: string, detail?: unknown) {
  const now = new Date().toISOString();
  if (detail === undefined) {
    dlog(`[startup ${now}] ${step}`);
    return;
  }
  dlog(`[startup ${now}] ${step}`, detail);
}

/** Last folder used by "Save to Dropbox", so repeat saves reopen there
 *  instead of at the Dropbox root. Desktop-only; harmless if absent. */
const DROPBOX_DIR_KEY = "du_dropbox_last_dir";

function storageGet(key: string): string | null {
  if (typeof window === "undefined") return null;
  return (
    (window as any).estimatorStore?.get(key) ?? window.localStorage.getItem(key)
  );
}

function storageSet(key: string, value: string) {
  if (typeof window === "undefined") return;
  (window as any).estimatorStore?.set(key, value) ??
    window.localStorage.setItem(key, value);
}

function storageRemove(key: string) {
  if (typeof window === "undefined") return;
  (window as any).estimatorStore?.remove?.(key) ?? window.localStorage.removeItem(key);
}

function isOfflineAdminCached() {
  return String(storageGet(OFFLINE_ROLE_KEY) || "").toLowerCase() === "admin";
}

// Snapshot of the durable offline cache, used to let a previously
// logged-in user keep working offline within the grace window even when
// the Supabase token has expired and can't be refreshed (no network).
function readOfflineCache() {
  const orgId = storageGet(OFFLINE_ORG_KEY);
  const lastOnlineMs = Number(storageGet(OFFLINE_LAST_KEY) || 0);
  const withinGrace =
    !!lastOnlineMs && Date.now() - lastOnlineMs <= OFFLINE_GRACE_MS;
  return {
    orgId,
    isAdmin: isOfflineAdminCached(),
    userId: storageGet(OFFLINE_USER_KEY),
    email: storageGet(OFFLINE_EMAIL_KEY),
    lastOnlineMs,
    withinGrace,
  };
}

/**
 * Can we reach the login server right now? Decides what an unexpected
 * sign-out means: reachable → the server refused (login blocked, session
 * revoked) → normal login screen, no offline pass; unreachable → no network →
 * offline mode. Any HTTP answer counts as reachable.
 */
async function authServerReachable(): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
  const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = ctl ? setTimeout(() => ctl.abort(), 4000) : null;
  try {
    const r = await fetch(`${SUPABASE_URL}/auth/v1/health`, {
      headers: { apikey: SUPABASE_ANON_KEY },
      signal: ctl?.signal,
    });
    return r.status > 0;
  } catch {
    return false;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// Wipe every durable offline marker. Called on a DELIBERATE logout so an
// explicitly signed-out user can never re-enter via the offline path. The
// offline lead list (customer contact details) goes with it.
function clearOfflineCache() {
  storageRemove(OFFLINE_ORG_KEY);
  storageRemove(OFFLINE_LAST_KEY);
  storageRemove(OFFLINE_ROLE_KEY);
  storageRemove(OFFLINE_USER_KEY);
  storageRemove(OFFLINE_EMAIL_KEY);
  storageRemove(AUTH_SESSION_HINT_KEY);
  clearLeadList();
}

async function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// Resolve the proposal Visual Appendix photos for the current selections from
// a set of visual_library records. Pure (no React state) so both the live
// preview memo and the send path can call it — the send path passes a freshly
// fetched record set so a photo uploaded mid-session is frozen into the sent
// proposal without an app reload. Decking/Railing/Lattice match on the stable
// pricing_items2 id (rename-safe); Skirting uses its single id-less style card.
async function saveProposal(proposalData: any, orgId: string | null) {
  // ✅ fetch snapshot at save-time so public review always has blocks
  let proposalSectionsSnapshot: any[] = [];
  try {
    if (orgId) {
      const rows = await fetchProposalSections(orgId);
      proposalSectionsSnapshot = Array.isArray(rows) ? rows : [];
    }
  } catch (e) {
    console.warn("Could not fetch proposal sections snapshot", e);
  }

  const proposalWithSnapshot = {
    ...proposalData,
    proposalSectionsSnapshot,
  };

  const { data, error } = await supabase
    .from("proposals")
    .insert([{ data: proposalWithSnapshot }])
    .select("id")
    .single();

  if (error) {
    console.error("Failed to save proposal", error);
    alert("Failed to save proposal");
    return null;
  }

  return data.id;
}

// Build stamp shown in the top-bar badge and sidebar footer. Format is
// "{PLATFORM}-{SHORTHASH}" so the desktop .exe and the live web app can be
// visually compared — matching hashes mean both are running the same code.
// PLATFORM is "DT" inside the Electron wrapper, "WEB" in the browser; the
// detection mirrors the beforeunload guard's isDesktop check. SHORTHASH
// comes from REACT_APP_GIT_SHA (Cloudflare injects CF_PAGES_COMMIT_SHA,
// GitHub Actions injects github.sha; both are read by scripts/build.js).
// Falls back to "dev" for local `npm start` without a build env.
const BUILD_PLATFORM =
  typeof window !== "undefined" &&
  (!!(window as any).estimator?.isDesktop ||
    (typeof navigator !== "undefined" &&
      navigator.userAgent.includes("Electron")))
    ? "DT"
    : "WEB";

const BUILD_HASH =
  (process.env.REACT_APP_GIT_SHA || "").toString().trim().slice(0, 7) || "dev";

const BUILD_STAMP = `${BUILD_PLATFORM}-${BUILD_HASH}`;

// Live US-phone formatter for the CUSTOMER phone field — now shared with
// mobile in src/shared/format.ts (formatCustomerPhone), imported above.

// ============================================================
// PICK A LEAD (Zoho → Customer fields, admin-only)
// ------------------------------------------------------------
// The "Pick a lead" button on the Customer card is gated to the shared
// ZOHO_IMPORT_ALLOWED_EMAILS allow-list (src/shared/zohoImport.ts, via
// canImportFromZohoEmail) AND to the edge function's own ZOHO_ALLOWED_EMAILS
// check, so it only appears/works for the allowed user — or for a device that
// already holds that user's saved list (offline before the email is known).
// ============================================================

function App() {
  const path = window.location.pathname;

  // Public route: proposal review (NO hooks here)
  if (path.startsWith("/review/")) {
    return <ReviewProposalPage />;
  }

  // Public route: password-recovery landing for the email link.
  if (path === "/reset-password" || path.startsWith("/reset-password/")) {
    return <ResetPasswordPage />;
  }

  // Public route: the emailed invite link (set a password, join the company).
  if (path === "/accept-invite" || path.startsWith("/accept-invite/")) {
    return <AcceptInvitePage />;
  }

  // Everything else uses auth
  return <AuthedApp />;
}
function AuthedApp() {
  const devSkipAuth = useMemo(() => isDevLocalhostSkip(), []);
  const [session, setSession] = useState<any>(
    devSkipAuth ? createLocalDesktopSession() : null
  );
  const [authResolved, setAuthResolved] = useState(devSkipAuth);
  const [hasSessionHint, setHasSessionHint] = useState<boolean>(() => {
    return storageGet(AUTH_SESSION_HINT_KEY) === "1";
  });
  // How the auth bootstrap resolved:
  //   'pending'    – still checking
  //   'live'       – a real Supabase session is present
  //   'signedout'  – definitively no session (clean null or revoked token)
  //   'unconfirmed'– couldn't confirm (offline / network error / timeout);
  //                  a previously logged-in user may continue offline.
  const [authOutcome, setAuthOutcome] = useState<
    "pending" | "live" | "signedout" | "unconfirmed"
  >(devSkipAuth ? "live" : "pending");
  // Whether the login server answered the last reachability check. The login
  // screen offers "use offline" ONLY when it didn't: with the server
  // reachable, the person can simply log in — or has been refused, and must
  // not get an offline pass. null = not checked yet.
  const [authReachable, setAuthReachable] = useState<boolean | null>(null);
  useEffect(() => {
    if (session || !authResolved) return;
    let stop = false;
    authServerReachable().then((r) => {
      if (!stop) setAuthReachable(r);
    });
    return () => {
      stop = true;
    };
  }, [session, authResolved, authOutcome]);
  // User explicitly chose "Continue offline" on the login screen.
  const [offlineOverride, setOfflineOverride] = useState(false);
  // Set true only for a deliberate logout, so the SIGNED_OUT event can be
  // distinguished from a spurious/library-driven sign-out (e.g. a flaky
  // refresh) that must NOT strand the user.
  const deliberateLogoutRef = useRef(false);

  const [isAdmin, setIsAdmin] = useState(devSkipAuth);
  const [orgId, setOrgId] = useState<string | null>(
    devSkipAuth ? LOCAL_DESKTOP_ORG_ID : null
  );
  // The server ANSWERED that this signed-in user belongs to no company (an
  // admin removed them, or they never had one). Final: this device's copy of
  // company data is wiped and the app shows "Access removed" — never the
  // cached company. (Only a lookup that FAILS falls back to the cache.)
  const [accessRemoved, setAccessRemoved] = useState(false);
  const [orgLoading, setOrgLoading] = useState(!devSkipAuth);
  const [orgResolved, setOrgResolved] = useState(devSkipAuth);
  const [adminEmail, setAdminEmail] = useState<string | null>(null);
  const [startupNotice, setStartupNotice] = useState<string | null>(null);
  useEffect(() => {
    if (orgId) dlog("APP_ORG_ID", orgId);
  }, [orgId]);

  const email = (session?.user?.email || "").toLowerCase();

  // Defined early so the offline-entry render path (which has no live
  // session) can also use it. A DELIBERATE logout wipes the durable offline
  // cache so a signed-out user can never re-enter via the offline grace.
  const handleLogout = useCallback(async () => {
    if (devSkipAuth) {
      setStartupNotice("Dev localhost stays unlocked for testing.");
      return;
    }
    deliberateLogoutRef.current = true;
    clearOfflineCache();
    setHasSessionHint(false);
    try {
      await supabase.auth.signOut();
    } catch {
      /* offline: signOut may fail to reach the server; local clear is enough */
    }
    setSession(null);
    setOfflineOverride(false);
    setAuthOutcome("signedout");
  }, [devSkipAuth]);

  // Invites are accepted where the invitee lands (AcceptInvitePage), and as a
  // fallback in loadOrgForUser below when a signed-in user has no membership.
  // (This used to run only "once orgId is known" — which an invitee, having no
  // org yet, never was; so no invite was ever accepted.)
  // 0) ✅ Finalize Supabase auth after invite/magic links (prevents blank spinning page)
  useEffect(() => {
    if (devSkipAuth) return;

    const hasAuthParams =
      Boolean(window.location.hash) ||
      /[?&](access_token|refresh_token|code|token_hash)=/i.test(
        window.location.search || ""
      );

    if (hasAuthParams) {
      withTimeout(
        Promise.resolve(supabase.auth.getSession()),
        AUTH_URL_PARSE_TIMEOUT_MS,
        "auth url parse"
      ).catch((error) => {
        startupLog("auth url parse skipped", {
          message: (error as any)?.message || String(error),
        });
      });
    }

    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        // Clean up the URL after auth completes
        if (window.location.hash || window.location.search) {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
        }
      }
    });

    return () => {
      sub.subscription.unsubscribe();
    };
  }, [devSkipAuth]);

  // 1) Auth session bootstrap
  useEffect(() => {
    if (devSkipAuth) return;

    let cancelled = false;

    const applySession = (
      nextSession: any,
      outcome?: "live" | "signedout" | "unconfirmed"
    ) => {
      if (cancelled || nextSession === undefined) return;
      const resolvedSession = nextSession ?? null;
      setSession(resolvedSession);
      setAuthResolved(true);
      if (outcome) setAuthOutcome(outcome);
      // Only WRITE the session hint when we actually have a session.
      // We intentionally never clear it on a null session here — an
      // offline relaunch with an expired/unrefreshable token resolves to
      // a null session, and clearing the hint would forget that this
      // device ever logged in, blocking offline re-entry. The hint is
      // cleared ONLY on a deliberate logout (clearOfflineCache).
      if (resolvedSession) {
        setHasSessionHint(true);
        storageSet(AUTH_SESSION_HINT_KEY, "1");
      }
    };

    const fallbackTimer = window.setTimeout(() => {
      startupLog("auth bootstrap still pending", {
        online: typeof navigator === "undefined" ? undefined : navigator.onLine,
      });
      if (!cancelled) setAuthResolved(true);
    }, AUTH_BOOTSTRAP_TIMEOUT_MS);

    withTimeout(
      Promise.resolve(supabase.auth.getSession()),
      AUTH_BOOTSTRAP_TIMEOUT_MS,
      "auth session bootstrap"
    )
      .then(({ data, error }: any) => {
        window.clearTimeout(fallbackTimer);
        if (cancelled) return;
        if (data?.session) {
          applySession(data.session, "live");
          return;
        }
        if (error) {
          // getSession only surfaces an error when it tried to refresh an
          // expired token and failed. A network/retryable failure means we
          // simply couldn't confirm (let a cached user work offline); a
          // non-retryable failure (revoked/invalid token) is a real logout.
          applySession(
            null,
            isAuthRetryableFetchError(error) ? "unconfirmed" : "signedout"
          );
          return;
        }
        // Clean null, no error: no stored session. If we're offline, keep
        // it unconfirmed so the offline cache can still admit the user.
        const offline =
          typeof navigator !== "undefined" && !navigator.onLine;
        applySession(null, offline ? "unconfirmed" : "signedout");
      })
      .catch((error) => {
        window.clearTimeout(fallbackTimer);
        if (cancelled) return;
        startupLog("auth bootstrap failed", {
          message: (error as any)?.message || String(error),
        });
        // Timed out or threw — we couldn't confirm anything. Treat as
        // unconfirmed so a previously logged-in user isn't dumped to the
        // login screen when the network is flaky or absent.
        setSession(null);
        setAuthResolved(true);
        setAuthOutcome("unconfirmed");
      });

    const { data: listener } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        window.clearTimeout(fallbackTimer);
        if (cancelled) return;
        if (newSession) {
          deliberateLogoutRef.current = false;
          applySession(newSession, "live");
          return;
        }
        if (event === "SIGNED_OUT") {
          if (deliberateLogoutRef.current) {
            applySession(null, "signedout");
          } else {
            // Not the user's choice. Either the network dropped mid-refresh,
            // or the server REFUSED to renew — e.g. an admin used "Remove
            // access", which blocks the login. Ask which: if the login server
            // answers, it was its decision → normal login screen and no
            // offline pass; if it doesn't, keep the field user working offline.
            setSession(null);
            setAuthResolved(true);
            setAuthOutcome("pending");
            authServerReachable().then((reachable) => {
              if (cancelled) return;
              if (reachable) {
                clearOfflineCache();
                setAuthOutcome("signedout");
              } else {
                setAuthOutcome("unconfirmed");
              }
            });
          }
          return;
        }
        applySession(null);
      }
    );

    return () => {
      cancelled = true;
      window.clearTimeout(fallbackTimer);
      listener?.subscription.unsubscribe();
    };
  }, [devSkipAuth]);

  // Re-validate when connectivity returns: a user who entered offline gets
  // a live token once back online (or, if their token was revoked, the
  // library's refresh/SIGNED_OUT path cuts them off).
  useEffect(() => {
    if (devSkipAuth) return;
    const onOnline = () => {
      Promise.resolve(supabase.auth.getSession())
        .then(({ data }) => {
          if (data?.session) {
            setSession(data.session);
            setAuthResolved(true);
            setAuthOutcome("live");
            setHasSessionHint(true);
            storageSet(AUTH_SESSION_HINT_KEY, "1");
          }
        })
        .catch(() => {});
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [devSkipAuth]);

  useEffect(() => {
    if (devSkipAuth) {
      setOrgId(LOCAL_DESKTOP_ORG_ID);
      setIsAdmin(true);
      setOrgLoading(false);
      setOrgResolved(true);
      setStartupNotice("Dev localhost: login skipped for testing only.");
      return;
    }

    let cancelled = false;

    // At most one invite-accept attempt per sign-in (it re-runs the lookup).
    let triedInviteAccept = false;

    async function loadOrgForUser() {
      if (!session?.user?.id) return;

      const userId = session.user.id;
      // Cache identity so offline mode (no live session) can still show who
      // is signed in and associate locally-saved work with the right user.
      storageSet(OFFLINE_USER_KEY, userId);
      if (session.user.email) storageSet(OFFLINE_EMAIL_KEY, session.user.email);
      const cachedOrgId = storageGet(OFFLINE_ORG_KEY);
      const cachedIsAdmin = isOfflineAdminCached();
      const startedAt = performance.now();

      setOrgLoading(true);
      setOrgResolved(false);
      setStartupNotice(null);
      setAccessRemoved(false);

      if (cachedOrgId) {
        startupLog("org bootstrap from cache", { cachedOrgId });
        setOrgId(cachedOrgId);
        setOrgLoading(false);
        setOrgResolved(true);
      }

      if (typeof navigator !== "undefined" && !navigator.onLine) {
        startupLog("org lookup skipped: offline", { cachedOrgId, cachedIsAdmin });
        if (!cancelled) {
          setOrgId(cachedOrgId || null);
          setIsAdmin(cachedIsAdmin);
          setOrgLoading(false);
          setOrgResolved(true);
        }
        return;
      }

      try {
        const [q1, q2] = await Promise.all([
          withTimeout(
            Promise.resolve(
              supabase
                .from("org_members")
                .select("org_id, role")
                .eq("user_id", userId)
                .maybeSingle()
            ),
            ORG_LOOKUP_TIMEOUT_MS,
            "org lookup"
          ).catch((error) => ({ error } as any)),
          withTimeout(
            Promise.resolve(
              supabase
                .from("org_members")
                .select("account_id, role")
                .eq("user_id", userId)
                .maybeSingle()
            ),
            ORG_LOOKUP_TIMEOUT_MS,
            "org account lookup"
          ).catch((error) => ({ error } as any)),
        ]);

        if (!q1?.error && q1?.data?.org_id) {
          if (cancelled) return;
          const nextIsAdmin = String(q1.data.role || "").toLowerCase() === "admin";
          setOrgId(q1.data.org_id);
          storageSet(OFFLINE_ORG_KEY, q1.data.org_id);
          storageSet(OFFLINE_LAST_KEY, String(Date.now()));
          storageSet(OFFLINE_ROLE_KEY, nextIsAdmin ? "admin" : "user");
          startupLog("org lookup success", {
            orgId: q1.data.org_id,
            elapsedMs: Math.round(performance.now() - startedAt),
            isAdmin: nextIsAdmin,
          });
          dlog("APP_ORG_ID", q1.data.org_id);
          setIsAdmin(nextIsAdmin);
          return;
        }

        const msg = (q1?.error?.message || "").toLowerCase();
        const missingOrgId =
          msg.includes("column") &&
          msg.includes("org_id") &&
          (msg.includes("does not exist") || msg.includes("not found"));

        if (!missingOrgId && q1?.error) {
          console.warn("org lookup error:", q1.error);
        }

        if (!q2?.error && q2?.data?.account_id) {
          if (cancelled) return;
          const nextIsAdmin = String(q2.data.role || "").toLowerCase() === "admin";
          setOrgId(q2.data.account_id);
          storageSet(OFFLINE_ORG_KEY, q2.data.account_id);
          storageSet(OFFLINE_LAST_KEY, String(Date.now()));
          storageSet(OFFLINE_ROLE_KEY, nextIsAdmin ? "admin" : "user");
          startupLog("org account lookup success", {
            orgId: q2.data.account_id,
            elapsedMs: Math.round(performance.now() - startedAt),
            isAdmin: nextIsAdmin,
          });
          setIsAdmin(nextIsAdmin);
          return;
        }

        if (q2?.error) {
          console.warn("org account lookup error:", q2.error);
        }

        if (cancelled) return;

        // No membership. Someone invited who closed the set-password page
        // before finishing still has an open invite: accept it by their
        // verified email and look again. accept-invite never grants a second
        // membership and ignores revoked invites.
        if (!triedInviteAccept && navigator.onLine !== false) {
          triedInviteAccept = true;
          try {
            const { data } = await supabase.functions.invoke("accept-invite", {
              body: {},
            });
            if (!cancelled && (data as any)?.accepted) {
              startupLog("accepted a pending invite; re-running org lookup", {});
              await loadOrgForUser();
              return;
            }
          } catch (inviteErr) {
            console.warn("accept-invite fallback failed:", inviteErr);
          }
          if (cancelled) return;
        }

        // Did the server actually ANSWER? (Both lookups returned, or failed
        // only because that column doesn't exist in this schema.) Then "no
        // membership" is final. A lookup that errored or timed out is NOT an
        // answer — keep working on the cached company, e.g. in the field.
        const lookupAnswered = membershipLookupAnswered(q1, q2);

        if (lookupAnswered) {
          startupLog("org lookup: server says no membership — access removed", {
            elapsedMs: Math.round(performance.now() - startedAt),
            hadCachedOrg: !!cachedOrgId,
          });
          wipeLocalCompanyData([cachedOrgId]);
          setOrgId(null);
          setIsAdmin(false);
          setAccessRemoved(true);
          return;
        }

        startupLog("org lookup could not be completed — using cached org", {
          elapsedMs: Math.round(performance.now() - startedAt),
        });
        setOrgId(cachedOrgId || null);
        setIsAdmin(cachedIsAdmin);
        if (cachedOrgId) {
          setStartupNotice("Using cached organization data. Reconnect to verify account access.");
        }
      } catch (error: any) {
        startupLog("org lookup fallback", {
          message: error?.message || String(error),
          elapsedMs: Math.round(performance.now() - startedAt),
          cachedOrgId,
        });
        if (cancelled) return;
        setOrgId(cachedOrgId || null);
        setIsAdmin(cachedIsAdmin);
        if (cachedOrgId) {
          setStartupNotice("Startup sync failed. Using cached organization data until connection recovers.");
        }
      } finally {
        if (cancelled) return;
        setOrgLoading(false);
        setOrgResolved(true);
      }
    }

    loadOrgForUser();

    return () => {
      cancelled = true;
    };
  }, [devSkipAuth, session?.user?.id]);

  // 3) Render gates (IMPORTANT ORDER)
  // Offline-grace snapshot, evaluated BEFORE the no-session gate so a
  // previously logged-in user is never dumped to the login screen just
  // because their token expired and can't be refreshed without a network.
  const offlineCache = readOfflineCache();
  const canOfflineEnter =
    !!offlineCache.orgId && offlineCache.withinGrace && hasSessionHint;
  const isOffline = typeof navigator !== "undefined" && !navigator.onLine;

  const renderOfflineApp = () => (
    <AppShell
      isAdmin={offlineCache.isAdmin}
      orgId={offlineCache.orgId}
      userId={offlineCache.userId}
      onLogout={handleLogout}
      userEmail={offlineCache.email || email}
      startupNotice={
        startupNotice ??
        "Working offline — sign back in when you reconnect to sync."
      }
    />
  );

  if (!session) {
    // Still checking, and we have reason to believe a session should exist.
    if (authOutcome === "pending" && (hasSessionHint || !!offlineCache.orgId)) {
      return <BootScreen label="Restoring your session…" />;
    }

    // Couldn't confirm the session (offline / flaky network / expired token
    // that can't refresh), OR the user explicitly chose to continue offline.
    // A previously logged-in user keeps working within the grace window.
    if ((authOutcome === "unconfirmed" || offlineOverride) && canOfflineEnter) {
      return renderOfflineApp();
    }

    // Offline, previously logged in, but the 3-day grace has lapsed.
    if (isOffline && hasSessionHint && !canOfflineEnter) {
      return <OfflineExpiredScreen />;
    }

    return (
      <AuthPage
        onUseOffline={
          canOfflineEnter && authReachable === false
            ? () => setOfflineOverride(true)
            : undefined
        }
      />
    );
  }

  if (orgLoading && !orgId) return <BootScreen label="Loading your organization…" />;

  if (typeof window !== "undefined" && !navigator.onLine) {
    const lastOnline = Number(storageGet(OFFLINE_LAST_KEY) || 0);
    if (!lastOnline || Date.now() - lastOnline > OFFLINE_GRACE_MS) {
      return <OfflineExpiredScreen />;
    }
  }

  // The server said: no company. Never fall back to cached data here.
  if (accessRemoved) {
    return <AccessRevoked adminEmail={adminEmail} removed onLogout={handleLogout} />;
  }

  // ✅ Guard: user has no org
  if (orgResolved && !orgId) {
    const cachedOfflineOrg = storageGet(OFFLINE_ORG_KEY) || null;
    const cachedOfflineAdmin = isOfflineAdminCached();

    const cachedLastOnline = storageGet(OFFLINE_LAST_KEY) || null;

    const lastOnlineMs = Number(cachedLastOnline || 0);
    const canUseOffline =
      !!cachedOfflineOrg &&
      (lastOnlineMs ? Date.now() - lastOnlineMs <= OFFLINE_GRACE_MS : true);

    if (canUseOffline) {
      return (
        <AppShell
          isAdmin={cachedOfflineAdmin}
          orgId={cachedOfflineOrg}
          userId={session?.user?.id ?? null}
          onLogout={handleLogout}
          userEmail={email}
          startupNotice={startupNotice}
        />
      );
    }

    // Only admins are allowed to create an org
    if (isAdmin) {
      return (
        <CreateOrgPage
          onCreated={(newOrgId) => {
            setOrgId(newOrgId);
            setIsAdmin(true);
          }}
        />
      );
    }

    // Non-admins should NEVER see CreateOrgPage
    return <AccessRevoked adminEmail={adminEmail} onLogout={handleLogout} />;
  }

  return (
    <AppShell
      isAdmin={isAdmin}
      orgId={orgId}
      userId={session?.user?.id ?? null}
      onLogout={handleLogout}
      userEmail={email}
      startupNotice={startupNotice}
    />
  );
}

function AppShell({
  isAdmin,
  orgId,
  userId,
  onLogout,
  userEmail,
  startupNotice,
}: {
  isAdmin: boolean;
  orgId: string | null;
  userId: string | null;
  onLogout: () => void;
  userEmail: string;
  startupNotice?: string | null;
}) {
  // ===============================
  // ROLE GATES (email-based for now)
  // ===============================
  const canEditPricing = isAdmin;
  const canSeeUsersLicenses = isAdmin;
  // In-app confirm/alert for the open and save flows. Native window.confirm /
  // alert break text-field focus in the Windows desktop app (see appDialog).
  const appDialog = useAppDialog();
  // For listeners/effects registered once that still need the dialog.
  const appDialogRef = useRef(appDialog);
  appDialogRef.current = appDialog;
  // "A new build is parked in the service worker's waiting slot." Web-only;
  // see src/swUpdate.ts for why this is a store rather than a prop.
  const swUpdateReady = React.useSyncExternalStore(
    subscribeSwUpdate,
    getSwUpdateReady,
    getSwUpdateReady
  );
  const [proposalSectionsSnapshot, setProposalSectionsSnapshot] = useState<
    any[]
  >([]);
  // -----------------------------------------------------------------
  // TOOLTIP POSITIONING + CLIP FIX
  // .tooltip-box is position:fixed so it escapes ancestor overflow
  // (the .main-content scroller, card clipping, etc.). We compute
  // viewport coords from the wrapper's bounding rect on hover, then
  // re-clamp once it's visible if its right edge would exceed the
  // window. Same anchor side every time, just clamped to fit.
  // -----------------------------------------------------------------
  useEffect(() => {
    const RIGHT_MARGIN = 8;
    const SHOW_DELAY_MS = 2050; // matches .tooltip-box CSS transition-delay
    let activeWrapper: Element | null = null;
    let pendingTimer: ReturnType<typeof setTimeout> | null = null;

    const resetBox = (wrapper: Element | null) => {
      if (!wrapper) return;
      const box = wrapper.querySelector(".tooltip-box") as HTMLElement | null;
      if (box) {
        box.style.left = "";
        box.style.top = "";
      }
    };

    const clearActive = () => {
      if (pendingTimer) {
        clearTimeout(pendingTimer);
        pendingTimer = null;
      }
      resetBox(activeWrapper);
      activeWrapper = null;
    };

    // Place the (fixed-position) box just to the right of the wrapper,
    // mirroring the original CSS offsets (left: 110%, top: -10px).
    const positionTooltip = (wrapper: Element) => {
      const box = wrapper.querySelector(".tooltip-box") as HTMLElement | null;
      if (!box) return;
      const wrect = (wrapper as HTMLElement).getBoundingClientRect();
      box.style.left = `${wrect.left + wrect.width * 1.10}px`;
      box.style.top = `${wrect.top - 10}px`;
    };

    const clampTooltip = (wrapper: Element) => {
      const box = wrapper.querySelector(".tooltip-box") as HTMLElement | null;
      if (!box) return;
      // Re-anchor from the wrapper, then nudge left if it would overflow
      // the right edge of the viewport.
      positionTooltip(wrapper);
      void box.offsetWidth;
      const rect = box.getBoundingClientRect();
      const maxRight = window.innerWidth - RIGHT_MARGIN;
      if (rect.right > maxRight) {
        const overflow = rect.right - maxRight;
        box.style.left = `${rect.left - overflow}px`;
      }
    };

    const onMove = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const wrapper = target.closest(".tooltip-wrapper");
      if (wrapper === activeWrapper) return;
      clearActive();
      if (!wrapper) return;
      activeWrapper = wrapper;
      // Place it immediately so when the CSS reveals it after the delay
      // it appears anchored to the wrapper (not stuck at 0,0).
      positionTooltip(wrapper);
      pendingTimer = setTimeout(() => {
        pendingTimer = null;
        if (activeWrapper === wrapper) clampTooltip(wrapper);
      }, SHOW_DELAY_MS);
    };

    document.body.addEventListener("mouseover", onMove);
    window.addEventListener("scroll", clearActive, true);
    window.addEventListener("resize", clearActive);
    return () => {
      document.body.removeEventListener("mouseover", onMove);
      window.removeEventListener("scroll", clearActive, true);
      window.removeEventListener("resize", clearActive);
      clearActive();
    };
  }, []);


  function BootScreen({ label = "Loading…" }: { label?: string }) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(180deg, #f3f4f6 0%, #eef2ff 100%)",
          fontFamily: "system-ui",
        }}
      >
        <div
          style={{
            width: 420,
            maxWidth: "92vw",
            padding: 22,
            borderRadius: 16,
            background: "white",
            border: "1px solid rgba(0,0,0,0.10)",
            boxShadow: "0 18px 45px rgba(0,0,0,0.12)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: "#111",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 900,
                letterSpacing: 0.5,
              }}
            >
              DU
            </div>

            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 900 }}>
                Deck Estimator
              </div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>Decks Unique</div>
            </div>
          </div>

          <div
            style={{
              marginTop: 16,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div
              style={{
                width: 16,
                height: 16,
                borderRadius: 999,
                border: "3px solid rgba(0,0,0,0.12)",
                borderTopColor: "rgba(0,0,0,0.65)",
                animation: "duSpin 0.9s linear infinite",
              }}
            />
            <div style={{ fontWeight: 800, opacity: 0.8 }}>{label}</div>
          </div>

          <style>
            {`@keyframes duSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
          </style>
        </div>
      </div>
    );
  }

  // ===============================
  // FILE MENU + CONFIRM MODALS
  // ===============================
  const [fileOpen, setFileOpen] = useState(false);
  const [confirmNewOpen, setConfirmNewOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  type EmailDraft = {
    to: string;
    subject: string;
    body: string;
    link: string;
    proposalId?: string;
    sendMeCopy?: boolean;
  };

  const [emailDraft, setEmailDraft] = useState<EmailDraft | null>(null);
  // ===============================
  // STEP 3: SEND FROM EMAIL MODAL
  // - Always enqueue first (offline safe)
  // - If online: flush queue (sends now)
  // - Close modal + clear draft
  // ===============================
  // ===============================
  // HELPER: Convert Blob → base64 (no data: prefix)
  // ===============================
  async function blobToBase64NoPrefix(blob: Blob): Promise<string> {
    const arrayBuffer = await blob.arrayBuffer();
    let binary = "";
    const bytes = new Uint8Array(arrayBuffer);
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
  }
  const handleSendEmailFromModal = async () => {
    if (!emailDraft) return;
    await sendEmailNow();
  };

  const sendEmailNow = async () => {
    if (!emailDraft) return;

    try {
      const to = (emailDraft.to || "").trim();
      const subject = (emailDraft.subject || "").trim();
      const bodyText = emailDraft.body || "";
      const replyTo = (userSettings?.userEmail || "").trim();

      // ✅ default OFF
      const sendMeCopy = !!emailDraft?.sendMeCopy;

      // ✅ Email HTML ("View Proposal" button straight to /review + company
      // footer) and the matching plain-text part
      const emailArgs = {
        bodyText,
        proposalId: (emailDraft as any)?.proposalId || "",
        footer: emailFooterFromSettings(userSettings),
      };
      const html = buildEmailHtml(emailArgs);
      const text = buildEmailText(emailArgs);

      dlog("sendEmailNow -> invoking edge function", {
        to,
        subject,
        replyTo,
        bodyLen: bodyText.length,
        htmlLen: html.length,
      });

  // ✅ get proposal id safely
const proposalId = emailDraft?.proposalId || undefined;

// ✅ get reply-to from Settings (required)

if (!replyTo) {
  alert("Missing Reply-To email. Go to Settings and set your email address.");
  return;
}

// ✅ call Edge Function
const { data, error } = await invokeSendProposalEmail(supabase, {
  to,
  subject,
  html,
  proposalId,
  replyTo,
  text,
  cc: sendMeCopy ? [replyTo] : undefined,
});

if (error) {
  console.error("EDGE FUNCTION ERROR:", error);
  alert("Send failed:\n\n" + JSON.stringify(error, null, 2));
  return;
}

dlog("EDGE FUNCTION SUCCESS:", data);



      if (error) {
        console.error("EDGE FUNCTION ERROR (raw):", error);
        console.error(
          "EDGE FUNCTION ERROR (json):",
          JSON.stringify(error, null, 2)
        );

        const msg =
          (error as any)?.message ||
          (error as any)?.context?.body ||
          JSON.stringify(error, null, 2);

        alert("Send failed (edge function):\n\n" + msg);
        return;
      }

      dlog("EDGE FUNCTION SUCCESS:", data);

      setEmailModalOpen(false);
      setEmailDraft(null);

      showToast("Email sent ✅");
    } catch (err: any) {
      console.error("SEND EMAIL CRASH:", err);
      alert(
        "Send crashed:\n\n" +
          (err?.message ||
            err?.error_description ||
            err?.details ||
            JSON.stringify(err, null, 2))
      );
    }
  };

  // ===============================
  // OFFLINE / ONLINE STATE
  // ===============================
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);

    window.addEventListener("online", on);
    window.addEventListener("offline", off);

    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3500);
  };

  // ===============================
  // PICK A LEAD (Zoho → Customer fields)
  // ===============================
  // One picker (src/components/LeadPickerModal.tsx): this person's saved
  // appointments, today → today+7, kept on the device (useLeadList /
  // src/shared/leadList.ts) so it works offline; online it adds a last-name
  // search and everyone's appointments by day. Downloaded on launch and
  // whenever the app comes back online; another login's list is wiped on
  // read; sign-out and the "no company" lockout wipe it.

  // Zoho access is the allow-list. (The edge function enforces the same
  // allow-list server-side, so this is just UI hygiene.)
  const canImportFromZoho = canImportFromZohoEmail(userEmail);

  const leadListState = useLeadList({
    userId,
    canDownload: canImportFromZoho,
    isOnline,
  });
  const [leadPickerOpen, setLeadPickerOpen] = useState(false);

  // Shown to the Zoho allow-list, or to anyone this device already holds a
  // list for (so it works offline before the email is known).
  const canPickLead = canImportFromZoho || !!leadListState.leadList;

  const clientFieldsHaveData = (): boolean =>
    [
      clientFirstName,
      clientLastName,
      clientLocation,
      clientTown,
      clientState,
      clientZip,
      clientPhone,
      clientEmail,
    ].some((v) => (v || "").trim() !== "");

  // The one place a Zoho contact becomes the Customer fields — saved leads,
  // everyone's appointments and search results all land here.
  const fillCustomerFromZoho = (c: Partial<LeadContact>) => {
    // clientTitle removed from the app — Zoho's title is no longer mapped.
    setClientFirstName(c.firstName || "");
    setClientLastName(c.lastName || "");
    setClientLocation(c.street || "");
    setClientTown(c.city || "");
    setClientState(c.state || "");
    setClientZip(c.zip || "");
    // Reuse the existing customer phone auto-format (prev="" → clean format).
    setClientPhone(formatCustomerPhone(c.phone || "", ""));
    setClientEmail(c.email || "");
  };

  // ===============================
  // EMAIL DRAFT (in-app send window)
  // ===============================
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [sendMeCopy, setSendMeCopy] = useState(false);

  const [recentFiles, setRecentFiles] = useState<RecentFile[]>(() =>
    getRecents()
  );
  const [recentOpen, setRecentOpen] = useState(false);
  const refreshRecents = () => setRecentFiles(getRecents());

  // ===============================
  // STATE: ESTIMATE + UI
  // ===============================
  // ===============================
  // STATE: ESTIMATE + UI
  // ===============================
 type NavKey =
  | "home"
  | "estimator"
  | "proposals"
  | "pricingAdmin"
  | "analytics"
  | "settings"
  | "users"
  | "contract"
  | "visualLibrary";


  // Land on Home, not on a blank estimate. Every path that loads real work —
  // File > Open, a recent, a double-clicked .est, New — sets activeNav to
  // "estimator" itself, so opening an estimate still goes straight to it.
  const [activeNav, setActiveNav] = useState<NavKey>("home");

  // Home renders the recents list, and a save made since mount would otherwise
  // be missing from it — the state is only seeded once at startup.
  useEffect(() => {
    if (activeNav === "home") refreshRecents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeNav]);

  // UL-WIDE body class (full width on Users/Licenses)
  useEffect(() => {
    document.body.classList.toggle("ul-wide", activeNav === "users");
    return () => document.body.classList.remove("ul-wide");
  }, [activeNav]);

  // keep users locked out if not authorized
  useEffect(() => {
    if (activeNav === "users" && !canSeeUsersLicenses) {
      setActiveNav("estimator");
    }
  }, [activeNav, canSeeUsersLicenses]);

  const EMAIL_DOMAINS = [
    "gmail.com",
    "icloud.com",
    "yahoo.com",
    "outlook.com",
    "hotmail.com",
  ];

  function getEmailSuggestions(input: string) {
    const v = (input || "").trim();
    const at = v.indexOf("@");
    if (at < 0) return [];
    const name = v.slice(0, at);
    const partial = v.slice(at + 1).toLowerCase();
    if (!name) return [];
    return EMAIL_DOMAINS.filter((d) => d.startsWith(partial))
      .slice(0, 5)
      .map((d) => `${name}@${d}`);
  }

  // ✅ FIX: this state must live at component level (NOT inside render)
  const [showBreakdown, setShowBreakdown] = useState(false);
const [showDeckingLevels, setShowDeckingLevels] = useState(false);

  // Kept in sync BY HAND with the @container breakpoint in styles.css — the
  // probe below reads it only to label what the CSS is doing, so a drift here
  // mislabels the readout but changes no layout.
  const ADD_ITEM_STACK_BELOW = 616;

  // MEASURED width of .add-items-rows — the element the Add Item container
  // query actually reads. Display scaling and browser zoom make it impossible
  // to infer this from a screenshot (the same screenshot is 1400 CSS px at
  // 100% and 1556 at 90%), so the number is printed in the dev build instead
  // of being guessed at. Dev only: gated on NODE_ENV, so nothing ships.
  const addItemsRowsRef = useRef<HTMLDivElement | null>(null);
  const [addItemsPanelWidth, setAddItemsPanelWidth] = useState<number | null>(
    null
  );

  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    const el = addItemsRowsRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const read = () =>
      setAddItemsPanelWidth(
        Math.round(el.getBoundingClientRect().width * 10) / 10
      );
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeNav]);

  // Misc rows whose proposal-description editor the user has opened while the
  // description is still empty. Purely a UI affordance — the description itself
  // lives on the row, and a row with text always shows the editor — so this is
  // deliberately NOT part of the snapshot.
  const [miscDescOpenRows, setMiscDescOpenRows] = useState<string[]>([]);

  // Decking tiers the user ticked to carry onto the proposal as an
  // alternate-pricing menu. Holds tier KEYS ONLY — the level names from
  // trexLevelsWhatIf's LEVELS list, which is the same identifier the tier
  // pricing looks up by. Deliberately no dollar amounts: totals are recomputed
  // from live pricing + square footage every render, so a saved selection can
  // never print a stale number. Purely presentational — it never feeds
  // computeEstimate, so ticking a tier cannot move the estimate total.
  const [proposalDeckingOptions, setProposalDeckingOptions] = useState<
    string[]
  >([]);

  // Minted here when none is stored, rather than by the backstop effect below
  // a render later: the unsaved-changes baseline is taken from the first
  // render, and an id arriving after it read as an edit nobody made.
  const [estimateId, setEstimateId] = useState<string>(() => {
    try {
      return localStorage.getItem("du_estimate_id") || uid();
    } catch {
      return uid();
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("du_estimate_id", estimateId);
    } catch {}
  }, [estimateId]);
  // Always carry an estimateId, including for a brand-new project (which
  // resets it to ""). Per-estimate scratch state — Notes above all — keys off
  // it, and with both the id and the name blank there was no key to persist
  // under, so notes lived only in React state: they printed fine but arrived
  // blank on the emailed /review page. Stamping one here gives every estimate
  // a stable key from the start. A fresh id has no du_proposal_id:: entry, so
  // saveOrUpdateProposal still inserts a new row rather than reusing one.
  useEffect(() => {
    if (!estimateId) setEstimateId(uid());
  }, [estimateId]);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");

  const [estimateName, setEstimateName] = useState<string>(() => {
    try {
      return localStorage.getItem("du_estimate_name") || "";
    } catch {
      return "";
    }
  });

  // Seed/refresh the displayed proposal link from the SAME unique key the
  // save logic now uses (du_proposal_id::{estimateId}). Keyed on estimateId,
  // not estimateName, so the display can't show a different client's link.
  const getProposalIdForEstimate = (eid: string) => {
    if (!eid) return "";
    try {
      return localStorage.getItem(`du_proposal_id::${eid}`) || "";
    } catch {
      return "";
    }
  };

  const [proposalId, setProposalId] = useState<string>(() =>
    getProposalIdForEstimate(estimateId)
  );

  // Live Notes text, mirrored up from ProposalPage. The /review payload sends
  // this rather than re-reading the localStorage cache, so the customer's
  // proposal shows exactly what the screen (and therefore Print / Save-as-PDF)
  // shows. A blank value falls through to the id-keyed cache read inside
  // buildProposalPayload, which is the same estimate's notes either way.
  const proposalNotesRef = useRef<string>("");

  useEffect(() => {
    const next = getProposalIdForEstimate(estimateId);
    if (next) setProposalId(next);
  }, [estimateId]);

  const [currentFileName, setCurrentFileName] = useState<string | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem("du_estimate_name", estimateName);
    } catch {}
  }, [estimateName]);
  const [estimateNameLocked, setEstimateNameLocked] = useState<boolean>(() => {
    try {
      return localStorage.getItem("du_estimate_name_locked") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    const on = activeNav === "users";
    document.body.classList.toggle("ul-wide", on);

    // TEMP debug:
    dlog("[UL-WIDE]", {
      activeNav,
      on,
      bodyClass: document.body.className,
    });

    return () => {
      // cleanup so it doesn't stick if component unmounts
      document.body.classList.remove("ul-wide");
    };
  }, [activeNav]);

  useEffect(() => {
    try {
      localStorage.setItem(
        "du_estimate_name_locked",
        estimateNameLocked ? "1" : "0"
      );
    } catch {}
  }, [estimateNameLocked]);

  const [clientLastName, setClientLastName] = useState("");
  const [clientLocation, setClientLocation] = useState<string>(""); // street address (now persisted + wired into the form in step 3)
  const [clientTown, setClientTown] = useState(""); // city (unchanged — keeps existing saved data)
  const [clientEmail, setClientEmail] = useState("");
  // Client field expansion (step 1: model + persistence only; form UI lands in step 3).
  const [clientFirstName, setClientFirstName] = useState("");
  const [clientState, setClientState] = useState("");
  const [clientZip, setClientZip] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  useEffect(() => {
    const town = (clientTown || "").trim();
    const last = (clientLastName || "").trim();

    // need both fields filled out
    if (!town || !last) return;

    // if user saved/renamed, do not overwrite
    if (estimateNameLocked) return;

    // ✅ choose your format here:
    // const auto = `${town} ${last}`;     // space version
    const auto = `${town}_${last}`; // underscore version

    // only set if it changed
    if ((estimateName || "").trim() !== auto) {
      setEstimateName(auto);
      try {
        localStorage.setItem("du_estimate_name", auto);
      } catch {}
    }
  }, [clientTown, clientLastName, estimateNameLocked]);

  const [constructionType, setConstructionType] = useState<string>("");
  const [includePermit, setIncludePermit] = useState(false);
  const [msrpMode, setMsrpMode] = useState(false);

  const [selectedDeckingId, setSelectedDeckingId] = useState<string>("");
  const [deckingSqFt, setDeckingSqFt] = useState<number>(0);

  const [selectedRailingId, setSelectedRailingId] = useState<string>("");
  const [railingLf, setRailingLf] = useState<number>(0);

  const [selectedStairsId, setSelectedStairsId] = useState<string>("");
  const [stairsCount, setStairsCount] = useState<number>(0);

  const [selectedFastenerId, setSelectedFastenerId] = useState<string>("");

  const [selectedDemoId, setSelectedDemoId] = useState<string>("");
 
  const [demoQty, setDemoQty] = useState<number>(0);
  const [selectedSkirtingId, setSelectedSkirtingId] = useState<string>("");
  const [skirtingSf, setSkirtingSf] = useState<number>(0);
  const [skirtingCategory, setSkirtingCategory] = useState<
    "" | "Skirting" | "Lattice"
  >("");

  const [miValue, setMiValue] = useState<number>(0);
  const [emailSugOpen, setEmailSugOpen] = useState(false);
  const emailSuggestions = useMemo(
    () => getEmailSuggestions(clientEmail),
    [clientEmail]
  );
  const [skirtingDeckingId, setSkirtingDeckingId] = useState<string>("");
  const [skirtingDeckingTouched, setSkirtingDeckingTouched] = useState(false);
  const lastAutoSkirtingDeckingId = useRef<string>("");
  const [skirtingTypeTouched, setSkirtingTypeTouched] = useState(false);
  // True once the user manually picks a fastener (or a loaded draft already had
  // one) — guards the decking-driven fastener default so it never stomps a
  // deliberate choice. See the auto-pick effect below.
  const [fastenerTouched, setFastenerTouched] = useState(false);

  // ADD ITEM rows
  const [addItems, setAddItems] = useState<AddItemRow[]>([]);

  // Per-estimate Scope of Work text. Empty string means "use the
  // auto-generated default from current selections" (handled in
  // ProposalPage). Round-trips through the snapshot so it saves with the
  // .est file and syncs via cloud-draft.
  const [sowText, setSowText] = useState<string>("");
  // Per-estimate Project Description (one-liner above the Scope of Work).
  // Empty string means "use the auto-generated summary from current
  // selections" (handled in ProposalPage). Round-trips through the snapshot
  // exactly like sowText, so it saves with the .DUest file and syncs via
  // cloud-draft / proposals.data / the review page.
  const [projectDescription, setProjectDescription] = useState<string>("");
  // "User has edited this field" flags. Once true, ProposalPage stops seeding
  // the auto-generated default so an intentionally-empty or custom SOW /
  // Project Description sticks instead of the template snapping back. Persisted
  // in the snapshot so reopening shows the user's version, not the default.
  const [sowTouched, setSowTouched] = useState<boolean>(false);
  const [projectDescriptionTouched, setProjectDescriptionTouched] =
    useState<boolean>(false);
  // Backstop for the startup draft load: set ONLY by these four user actions
  // (never by pricing recalculation or derived state), so a cloud draft that
  // arrives after the user has touched Add Items is not applied over them.
  const addItemsUserEditedRef = useRef(false);
  const addAddItemRow = () => {
    addItemsUserEditedRef.current = true;
    const newRow: AddItemRow = {
      rowId:
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? uid()
          : `${Date.now()}-${Math.random()}`,
      category: "",
      itemId: "",
      qty: 0,
      constructionType: "",
      deckingId: "",
      customName: "",
      customPrice: 0,
    };
    // Append, don't prepend: a new row belongs under the existing ones so the
    // list reads in the order they were added (and matches the ↑/↓ proposal
    // ordering the user sets by hand).
    setAddItems((prev) => [...prev, newRow]);
  };
  const removeAddItemRow = (rowId: string) => {
    addItemsUserEditedRef.current = true;
    setAddItems((prev) => prev.filter((r) => r.rowId !== rowId));
  };
  const updateAddItemRow = (rowId: string, patch: Partial<AddItemRow>) => {
    addItemsUserEditedRef.current = true;
    setAddItems((prev) =>
      prev.map((r) => (r.rowId === rowId ? { ...r, ...patch } : r))
    );
  };
  const moveAddItemRow = (rowId: string, direction: -1 | 1) => {
    addItemsUserEditedRef.current = true;
    setAddItems((prev) => {
      const index = prev.findIndex((r) => r.rowId === rowId);
      if (index < 0) return prev;

      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= prev.length) return prev;

      const next = [...prev];
      const [row] = next.splice(index, 1);
      next.splice(nextIndex, 0, row);
      return next;
    });
  };

  // ===============================
  // DIRTY STATE (unsaved changes)
  // ===============================
  const [isDirty, setIsDirty] = useState(false);
  // A shared proposal-section edit on the Proposal page is unsaved or still
  // saving. Not part of the estimate (it saves to the org template, not the
  // file), but closing the app now would lose it, so the close guards below
  // hold the window for it exactly as for an unsaved estimate.
  const [sectionEditsPending, setSectionEditsPending] = useState(false);
  // Same for the org-level proposal wording (labels, headings, Next steps,
  // validity days — org_settings.proposal_text), which saves through the
  // ordinary settings save below. "saving" covers the 800ms debounce too;
  // "error" still holds the close guard, because the edit is not in the
  // database. proposalTextRevRef counts edits so a save that started before
  // the latest keystroke cannot report "saved" for it.
  const [proposalTextSaveState, setProposalTextSaveState] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const proposalTextRevRef = useRef(0);
  const proposalTextPending =
    proposalTextSaveState === "saving" || proposalTextSaveState === "error";
  // True during boot until pricing is ready (and while a file/draft is being
  // applied). The unsaved-changes baseline follows the state while suspended,
  // and the cloud autosave is held off.
  const dirtySuspendedRef = useRef(true);
  // isDirty = "what Save would write now differs from the baseline" — the
  // fingerprint of what was last saved, loaded or started fresh. See
  // src/shared/dirtyBaseline.ts and the BASELINE COMPARISON effect below.
  const baselineRef = useRef<Fingerprint | null>(null);
  // Set at a load / New: the next render's state becomes the baseline.
  const rebasePendingRef = useRef(true);
  // The exact contract object the Contract page's auto-fill last produced
  // (null once the user edits the contract). Matched by identity in the
  // comparison effect: the page's effects run in the same commit as that
  // effect, before the new contract state has rendered, so a one-shot
  // "the next change is auto" flag was consumed too early and the auto-fill
  // then read as an edit.
  const autoContractValueRef = useRef<any>(null);
  // Contract fingerprint as of the previous comparison.
  const lastContractFpRef = useRef<string>("null");
  // Deliberate changes the fingerprint leaves out (accepting current prices
  // on a frozen file) still need saving.
  const forcedDirtyRef = useRef(false);
  // Set right after a FILE load (Open, Open Recent, Explorer double-click):
  // the baseline the next render takes is that file, so it is recorded as
  // "what's in a file" (FILE_RECORD_KEY). Not set for the cloud draft or New.
  const fileLoadPendingRef = useRef(false);
  // The user put an estimate on screen themselves (Open, Open Recent,
  // Explorer double-click, New). A startup cloud draft that lands after this
  // must not replace it — see the CLOUD DRAFT SYNC effect.
  const userChoseEstimateRef = useRef(false);
  // The "last file" record as it was at launch, before anything opened now
  // overwrites it: tells whether a late-arriving draft is already in a file.
  const startupFileRecordRef = useRef(
    parseFileRecord(storageGet(FILE_RECORD_KEY))
  );
  const recordFileBaseline = (estimateIdForFile: string, fp: Fingerprint) => {
    if (!estimateIdForFile) return;
    storageSet(
      FILE_RECORD_KEY,
      JSON.stringify(makeFileRecord(estimateIdForFile, fp))
    );
  };

  /** A load or New: whatever the next render holds is the clean state. */
  const markCleanAfterLoad = () => {
    rebasePendingRef.current = true;
    forcedDirtyRef.current = false;
    fileLoadPendingRef.current = false;
    setIsDirty(false);
  };
  /** A save: the clean state is exactly what was written to the file. */
  const markCleanAsSaved = (savedSnapshot: Record<string, any>) => {
    const fp = estimateFingerprint(savedSnapshot);
    recordFileBaseline(String(savedSnapshot.estimateId || ""), fp);
    baselineRef.current = fp;
    lastContractFpRef.current = fp.contract;
    rebasePendingRef.current = false;
    forcedDirtyRef.current = false;
    setIsDirty(false);
  };
  const markForcedDirty = () => {
    forcedDirtyRef.current = true;
    setIsDirty(true);
  };

  // Contract data — lifted up so it persists with the .DUest file via buildSnapshot.
  // ContractPage is now a controlled component: see ContractPage.tsx for the shape.
  const [contract, setContract] = useState<any | null>(null);
  const handleContractChange = useCallback(
    (next: any, meta?: { auto?: boolean }) => {
      setContract(next);
      // The Contract page's own auto-fill (client header, spec, ST-124) runs
      // every time the tab opens and is rebuilt from the estimate on every
      // visit, so it is not an edit — only typing on the page is.
      // The comparison effect absorbs it into the baseline if the contract
      // was clean, so it doesn't count as unsaved work.
      autoContractValueRef.current = meta?.auto ? next : null;
    },
    []
  );
  const [lastSavedFileName, setLastSavedFileName] = useState<string | null>(
    null
  );
  const [lastSavedFilePath, setLastSavedFilePath] = useState<string | null>(
    null
  );

  // ===============================
  // USER SETTINGS (for Proposal PDF)
  // ===============================
  // Persistence:
  //   - org_settings + user_settings tables in Supabase (source of truth)
  //   - localStorage (du_user_settings) kept as a cache for offline / fast paint
  // Fetch order on app load:
  //   1) Render from localStorage cache immediately (no waiting on network)
  //   2) Once orgId + auth user id are known, fetch from Supabase and merge in
  //   3) If both Supabase rows are empty AND cache has meaningful data, auto-
  //      migrate the cache up to Supabase (one-time silent migration)
  // State stays loosely typed (any) to match SettingsPage's existing
  // UserSettings shape (which allows nullable fields). Strict typing lives
  // in src/settings.ts.
  const [userSettings, setUserSettings] = useState<any>(
    () => readCachedSettings() ?? { ...DEFAULT_SETTINGS }
  );

  // Track whether the initial Supabase fetch has completed; until then we
  // don't push local changes upstream (avoids overwriting cloud data on a
  // first-render dirty mark while the fetch is still in flight).
  const settingsHydratedRef = useRef(false);

  // Keep a copy of the signature image on this machine whenever we're online,
  // so a contract printed later with no internet still shows it.
  useEffect(() => {
    void saveSignatureCopy(userSettings?.signatureUrl);
  }, [userSettings?.signatureUrl]);

  // Fetch from Supabase once we know orgId + authenticated user
  useEffect(() => {
    if (!orgId || !userId) return;
    let cancelled = false;
    (async () => {
      // Mutable holder (not a nullable let) so the onStatus callback can record
      // per-leg outcome without tripping closure narrowing. Defaults to
      // all-false, so if the fetch throws before reporting we don't hydrate.
      const legStatus = {
        userLegOk: false,
        orgTextLegOk: false,
        logoLegOk: false,
      };
      try {
        const fetched = await fetchSettings(orgId, userId, {
          onStatus: (s) => {
            legStatus.userLegOk = s.userLegOk;
            legStatus.orgTextLegOk = s.orgTextLegOk;
            legStatus.logoLegOk = s.logoLegOk;
          },
        });
        if (cancelled) return;
        // Per-field merge (mirrors MobileApp): never let a blank/empty cloud
        // value overwrite a good in-memory value. A naive `{...prev,...fetched}`
        // spread would wipe name/phone/email or reset the block order when a
        // leg comes back empty on a flaky fetch.
        setUserSettings((prev: any) => {
          const merged: any = { ...prev };
          for (const [k, v] of Object.entries(fetched)) {
            if (v === undefined) continue;
            if (typeof v === "string" && v.trim() === "") continue;
            if (
              Array.isArray(v) &&
              v.length === 0 &&
              Array.isArray(prev?.[k]) &&
              prev[k].length > 0
            )
              continue;
            merged[k] = v;
          }
          return merged;
        });
      } catch (err) {
        console.warn("settings fetch failed; keeping cache:", err);
      } finally {
        // Approach (3): only enable the auto-save write-back when the legs it
        // would clobber (user + org-text) actually succeeded. A failed/flaky
        // fetch leaves hydration false, so no upsert fires this session — a
        // later healthy launch flips it. New-user "empty but successful" reads
        // still report ok, so first-run save still works.
        if (!cancelled && legStatus.userLegOk && legStatus.orgTextLegOk) {
          settingsHydratedRef.current = true;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, userId]);

  // orgs.name. Since 2026-09-23 there is ONE company name: Settings > Company
  // > Company name (org_settings.company_name — the one that prints), which
  // also writes orgs.name. The app shows displayOrgName: the company name,
  // falling back to orgs.name only if it is empty (e.g. a brand-new org).
  const [orgName, setOrgName] = useState<string>("");
  useEffect(() => {
    if (!orgId) {
      setOrgName("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("orgs")
          .select("name")
          .eq("id", orgId)
          .single();
        if (cancelled || error) return;
        setOrgName((data?.name || "").toString());
      } catch {
        /* leave blank on failure — subtitle just hides */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);
  const displayOrgName =
    String((userSettings as any)?.companyName || "").trim() || orgName;

  // Cache every change immediately; debounce-save to Supabase
  const settingsSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Settings keys the user deliberately emptied since the last save. Without
  // this, upsertSettings' blank-guard (which protects good data from a stale
  // blank cache) would restore the old value — you could never clear a field.
  const settingsClearedKeysRef = useRef<Set<string>>(new Set());
  const onSettingsFieldCleared = useCallback((key: string, cleared: boolean) => {
    if (cleared) settingsClearedKeysRef.current.add(key);
    else settingsClearedKeysRef.current.delete(key);
  }, []);
  useEffect(() => {
    writeCachedSettings(userSettings as AppUserSettings);

    if (!orgId || !userId) return;
    if (!settingsHydratedRef.current) return; // wait for initial fetch first

    if (settingsSaveTimerRef.current) clearTimeout(settingsSaveTimerRef.current);
    settingsSaveTimerRef.current = setTimeout(() => {
      const clearKeys = Array.from(settingsClearedKeysRef.current);
      const proposalTextRev = proposalTextRevRef.current;
      upsertSettings(orgId, userId, userSettings as AppUserSettings, {
        clearKeys,
      })
        .then(() => {
          // Saved: those blanks are now the stored values.
          clearKeys.forEach((k) => settingsClearedKeysRef.current.delete(k));
          if (proposalTextRev === proposalTextRevRef.current) {
            setProposalTextSaveState((s) => (s === "idle" ? s : "saved"));
          }
        })
        .catch((err) => {
          console.warn("settings save failed; cache only:", err);
          if (proposalTextRev === proposalTextRevRef.current) {
            setProposalTextSaveState((s) => (s === "idle" ? s : "error"));
          }
        });
    }, 800);

    return () => {
      if (settingsSaveTimerRef.current) {
        clearTimeout(settingsSaveTimerRef.current);
      }
    };
  }, [userSettings, orgId, userId]);

  // ===============================
  // FILE OPEN/SAVE helpers
  // ===============================
  // Real estimate content lives BELOW the client info panel. The unsaved-work
  // warnings (Open / Open Recent / New) gate on this, so a blank estimate — or
  // one with only client name/address typed — never warns; any Scope-of-Work
  // content does. Deliberately EXCLUDES the 8 client-info fields; INCLUDES the
  // Scope of Work text, project notes, and a non-empty contract.
  const hasScopeContent = () =>
    snapshotHasScopeContent({
      constructionType,
      selectedDeckingId,
      deckingSqFt,
      selectedRailingId,
      railingLf,
      selectedStairsId,
      stairsCount,
      selectedFastenerId,
      selectedDemoId,
      demoQty,
      skirtingCategory,
      selectedSkirtingId,
      skirtingSf,
      miValue,
      includePermit,
      msrpMode,
      addItems,
      sowText,
      projectDescription,
      contract,
    });

  /** Would replacing what's on screen (Open, Open Recent, New) lose work?
   *  Only when it has edits since the last save/open, or it was never written
   *  to a file at all (e.g. the restored cloud draft of an unsaved estimate).
   *  See hasUnsavedWork in src/shared/dirtyBaseline.ts. */
  const hasUnsavedWorkNow = () =>
    hasUnsavedWork({
      hasContent: hasScopeContent(),
      isDirty,
      estimateId,
      baseline: baselineRef.current,
      fileRecord: parseFileRecord(storageGet(FILE_RECORD_KEY)),
    });

  // ===============================
  // "DID I FORGET SOMETHING?" GUARD
  // ===============================
  // Every scope category is optional on its own, so nothing used to stop an
  // estimate that quietly shipped with no railing. Leaving the estimator for
  // the proposal now surfaces the blank / half-filled categories and makes the
  // user acknowledge them. Acknowledging is remembered per estimate + per set
  // of gaps, so it never nags on a second trip to the proposal, but it DOES
  // ask again once the gaps change.
  const [missingSections, setMissingSections] = useState<
    MissingSection[] | null
  >(null);
  const ackedMissingRef = useRef<string>("");

  const currentMissingSignature = () =>
    `${estimateId}|${missingSignature(
      findMissingSections({
        constructionType,
        selectedDeckingId,
        deckingSqFt,
        selectedRailingId,
        railingLf,
        selectedStairsId,
        stairsCount,
        selectedFastenerId,
        selectedDemoId,
        demoQty,
        skirtingCategory,
        selectedSkirtingId,
        skirtingSf,
        addItems: addItemsDetailed,
      })
    )}`;

  /** Entry point to the Proposal view — from the topbar button and the
   *  sidebar nav alike, so neither route skips the check. */
  const goToProposal = () => {
    const missing = findMissingSections({
      constructionType,
      selectedDeckingId,
      deckingSqFt,
      selectedRailingId,
      railingLf,
      selectedStairsId,
      stairsCount,
      selectedFastenerId,
      selectedDemoId,
      demoQty,
      skirtingCategory,
      selectedSkirtingId,
      skirtingSf,
      addItems: addItemsDetailed,
    });
    if (missing.length > 0 && ackedMissingRef.current !== currentMissingSignature()) {
      setMissingSections(missing);
      return;
    }
    setActiveNav("proposals");
  };

  /** "Continue anyway" — remember this exact set of gaps for this estimate
   *  and go on to the proposal. */
  const acknowledgeMissing = () => {
    ackedMissingRef.current = currentMissingSignature();
    setMissingSections(null);
    setActiveNav("proposals");
  };

  const handleNewProject = () => {
    // Clear ALL 8 customer fields so no stale client data lingers in the
    // Customer card after starting a new estimate. (Title was removed.)
    setClientLastName("");
    setClientTown("");
    setClientEmail("");
    setClientFirstName("");
    setClientLocation("");
    setClientState("");
    setClientZip("");
    setClientPhone("");

    setConstructionType("");
    setIncludePermit(false);
    setMsrpMode(false);

    setSelectedDeckingId("");
    setDeckingSqFt(0);
    setSelectedRailingId("");
    setRailingLf(0);
    setSelectedStairsId("");
    setStairsCount(0);
    setSelectedFastenerId("");
    setFastenerTouched(false); // fresh estimate → allow decking-driven default

    setSelectedDemoId("");
    setDemoQty(0);

    setSkirtingCategory("");
    setSelectedSkirtingId("");
    setSkirtingSf(0);

    setMiValue(0);
    setAddItems([]);
    setSowText("");
    setProjectDescription("");
    // A fresh estimate is untouched by definition, so clear the "user has
    // edited this" flags alongside the text they guard. Without this the flags
    // survived New Project: the text reset to "" while the flag stayed true, so
    // ProposalPage took the touched branch and rendered empty instead of
    // re-seeding the auto-generated default. Because the flags round-trip
    // through the cloud draft, one edit left every new estimate blank on every
    // device. Only New Project resets them — clearing a field on an EXISTING
    // estimate must still stick.
    setSowTouched(false);
    setProjectDescriptionTouched(false);
    // Same family: guards the decking-driven skirting auto-pick, so leaving it
    // true suppressed that default on the fresh estimate.
    setSkirtingTypeTouched(false);

    setEstimateName("");
    // Forget the previous estimate's file. Save writes back to this path
    // without asking, so keeping it would overwrite the old file with the
    // new estimate.
    setLastSavedFileName(null);
    setLastSavedFilePath(null);
    // Mint the fresh estimate's id HERE rather than resetting to "" and letting
    // the backstop effect fill it in a tick later. Per-estimate scratch state —
    // Notes above all — keys off this id, and during that gap the id and the
    // name were both blank, which is precisely the state that produces a shared
    // key. A new estimate must own a unique key from its very first render.
    const freshEstimateId = uid();
    setEstimateId(freshEstimateId);
    // Fresh estimate → the "I know these are blank" acknowledgement from the
    // previous one must not carry over.
    ackedMissingRef.current = "";
    setProposalId("");
    setEstimateNameLocked(false);
    setContract(null);
    // A fresh estimate starts with empty Notes. The previous estimate's notes
    // stay under ITS id key (reopening it still shows them) — they are simply
    // unreachable from here, because this estimate's key is different.
    proposalNotesRef.current = "";

    try {
      localStorage.removeItem("du_estimate_name");
      localStorage.setItem("du_estimate_id", freshEstimateId);
      localStorage.removeItem("du_estimate_name_locked");
    } catch {}

    setActiveNav("estimator");
    markCleanAfterLoad();
    userChoseEstimateRef.current = true;
    setShowBreakdown(false);

    setPricingFrozenFromFile(false);
    setPricingSnapshotAt(null);
    setLivePricingItems(null);

    // New estimate → drop session undo/redo history.
    resetScopeHistory();
  };

  const requestNewProject = () => {
    if (hasUnsavedWorkNow()) setConfirmNewOpen(true);
    else handleNewProject();
  };

  const cancelNew = () => setConfirmNewOpen(false);
  const discardAndNew = () => {
    setConfirmNewOpen(false);
    handleNewProject();
  };

  const buildSnapshot = () => ({
    estimateId,
    savedAt: new Date().toISOString(),
    estimateName,

    clientLastName,
    clientTown,
    clientEmail,
    // Client field expansion — additive keys; older files simply lack them.
    // clientLocation (street) was not persisted before this change.
    clientFirstName,
    clientLocation,
    clientState,
    clientZip,
    clientPhone,

    constructionType,
    includePermit,
    msrpMode,

    selectedDeckingId,
    deckingSqFt,
    selectedRailingId,
    railingLf,
    selectedStairsId,
    stairsCount,
    selectedFastenerId,

    selectedDemoId,
    demoQty,
    skirtingCategory,
    selectedSkirtingId,
    skirtingSf,
    miValue,

    addItems,
    sowText,
    projectDescription,
    sowTouched,
    projectDescriptionTouched,
    // Tier keys only — see the state declaration. Travels with the .est file,
    // the recents entry and the cloud draft like every other estimate field.
    proposalDeckingOptions,
    // Embed current pricing so opened files use frozen prices.
    // We embed effectiveCatalog, not the raw frozen array: on a frozen file
    // that is the frozen snapshot PLUS any genuinely-new items the user added
    // (live ids absent from the snapshot). This freezes a newly-added item in
    // at its then-current price, so it survives save/reopen and becomes part of
    // the frozen set like everything else. Pre-existing frozen rows are carried
    // through verbatim (effectiveCatalog starts from the frozen array and only
    // appends), so re-saving never changes an existing line's frozen price.
    // On a non-frozen file effectiveCatalog === pricingItems, so behavior is
    // unchanged there.
    pricingItems: effectiveCatalog,
    pricingCategories,
    // When the prices were captured. A frozen file keeps the date it was
    // priced (or last synced) — re-saving it doesn't change a single price, so
    // it must not change the date either. Live pricing is stamped now.
    pricingSnapshotAt: snapshotDateForSave(pricingFrozenFromFile, pricingSnapshotAt),
    // Contract data travels with the file so it survives reopen on any machine.
    contract,
    // Matched product photos (URLs only) frozen in at save time so the proposal
    // appendix renders for unauthenticated /review customers.
    visuals: proposalVisuals,
  });

  const applySnapshot = (snap: any) => {
    setEstimateName(snap.estimateName || "");
    setEstimateId(snap.estimateId || uid());
    // Drop the outgoing estimate's live Notes. ProposalPage re-seeds this from
    // the loaded estimate's own id key on its next render; until it does, the
    // payload must not be able to send the previous estimate's text.
    proposalNotesRef.current = "";

    // clientTitle removed — any value in an old snapshot is simply ignored.
    setClientLastName(snap.clientLastName || "");
    setClientTown(snap.clientTown || "");
    setClientEmail(snap.clientEmail || "");
    // Client field expansion — load blank when absent in older snapshots so
    // pre-existing .DUest files and cloud drafts open without crashing.
    setClientFirstName(snap.clientFirstName || "");
    setClientLocation(snap.clientLocation || "");
    setClientState(snap.clientState || "");
    setClientZip(snap.clientZip || "");
    setClientPhone(snap.clientPhone || "");

    setConstructionType(snap.constructionType || "");
    setIncludePermit(!!snap.includePermit);
    setMsrpMode(!!snap.msrpMode);

    setSelectedDeckingId(snap.selectedDeckingId || "");
    setDeckingSqFt(Number(snap.deckingSqFt || 0));

    setSelectedRailingId(snap.selectedRailingId || "");
    setRailingLf(Number(snap.railingLf || 0));

    setSelectedStairsId(snap.selectedStairsId || "");
    setStairsCount(Number(snap.stairsCount || 0));

    setSelectedFastenerId(snap.selectedFastenerId || "");
    // Treat a saved fastener as a deliberate choice so loading a draft doesn't
    // auto-default over it; an empty one stays open to the decking-driven default.
    setFastenerTouched(!!snap.selectedFastenerId);

    setSelectedDemoId(snap.selectedDemoId || "");
    setDemoQty(Number(snap.demoQty || 0));

    setSkirtingCategory((snap.skirtingCategory as any) || "");
    setSelectedSkirtingId(snap.selectedSkirtingId || "");
    setSkirtingSf(Number(snap.skirtingSf || 0));

    setMiValue(Number(snap.miValue || 0));
    setAddItems(Array.isArray(snap.addItems) ? snap.addItems : []);
    // Always reset SOW from the snapshot, even if the field is missing/empty,
    // so opening a different .est never leaves stale text from the prior file.
    setSowText(typeof snap.sowText === "string" ? snap.sowText : "");
    setProjectDescription(
      typeof snap.projectDescription === "string" ? snap.projectDescription : ""
    );
    // Touched flags default to false for older files (which lack them) — an
    // untouched non-empty SOW still shows its saved text, so nothing regresses.
    setSowTouched(!!snap.sowTouched);
    setProjectDescriptionTouched(!!snap.projectDescriptionTouched);
    // Older files lack the key entirely → no tiers ticked, which is also the
    // default for a new estimate. Strings only, so a malformed array can't
    // reach the proposal renderer.
    setProposalDeckingOptions(
      Array.isArray(snap.proposalDeckingOptions)
        ? snap.proposalDeckingOptions.filter(
            (v: any) => typeof v === "string" && !!v.trim()
          )
        : []
    );

    // If the snapshot contains embedded pricing, use it and prevent live refresh.
    // The frozen prices live in React state only — they must never be written to the
    // shared localStorage cache (PRICING_ITEMS_CACHE_KEY), which always mirrors live Supabase.
    // Backward-compat: files saved on main used pricingItemsSnapshot / pricingCategoriesSnapshot
    // (and pricingCacheTs instead of pricingSnapshotAt) — accept those too.
    const embeddedItems = Array.isArray(snap?.pricingItems)
      ? snap.pricingItems
      : Array.isArray(snap?.pricingItemsSnapshot)
        ? snap.pricingItemsSnapshot
        : null;
    const embeddedCategories = Array.isArray(snap?.pricingCategories)
      ? snap.pricingCategories
      : Array.isArray(snap?.pricingCategoriesSnapshot)
        ? snap.pricingCategoriesSnapshot
        : null;
    const embeddedSnapshotAt =
      typeof snap?.pricingSnapshotAt === "string"
        ? snap.pricingSnapshotAt
        : snap?.pricingCacheTs
          ? new Date(Number(snap.pricingCacheTs)).toISOString()
          : null;

    if (embeddedItems && embeddedCategories) {
      try {
        setPricingItems(embeddedItems as PricingItemRow[]);
        setPricingCategories(embeddedCategories as PricingCategoryRow[]);
        setPricingLoaded(true);
        setPricingError(null);
        setPricingFrozenFromFile(true);
        setPricingSnapshotAt(embeddedSnapshotAt);
      } catch (e) {
        console.warn("Failed to apply embedded pricing from file:", e);
      }
    } else {
      // Legacy file with no embedded pricing — make sure live Supabase prices
      // resume by clearing the freeze flag (which gates the load effect).
      setPricingFrozenFromFile(false);
      setPricingSnapshotAt(null);
    }

    setContract(snap?.contract ?? null);

    markCleanAfterLoad();
    setShowBreakdown(false);

    // Session-only history resets when a different estimate loads — undo must
    // never step back into the previously-open file's Scope of Work.
    resetScopeHistory();
  };

  // ===============================
  // SESSION-ONLY UNDO / REDO — Scope of Work + Add Items only
  // ===============================
  // Tracks ONLY the Scope of Work dropdowns / quantities and the Add Item
  // rows. Client info, estimate name, contract, SOW/description text, permit,
  // MSRP, manual index, pricing, visuals, and settings are deliberately NOT
  // tracked. History lives in React state only (no persistence) — reload or
  // opening another file starts fresh.
  //
  // Rather than wrap the ~15 scattered setters, a single watcher effect
  // observes a scoped snapshot of the tracked fields and records the PREVIOUS
  // value whenever it changes. A debounce collapses a burst — e.g. typing in
  // an SF field, or a decking pick that fires the fastener/skirting auto-pick
  // effects — into one undo step.
  type ScopeSnapshot = {
    constructionType: string;
    selectedDeckingId: string;
    deckingSqFt: number;
    selectedRailingId: string;
    railingLf: number;
    selectedStairsId: string;
    stairsCount: number;
    selectedFastenerId: string;
    selectedDemoId: string;
    demoQty: number;
    skirtingCategory: "" | "Skirting" | "Lattice";
    selectedSkirtingId: string;
    skirtingSf: number;
    addItems: AddItemRow[];
  };

  const [undoStack, setUndoStack] = useState<ScopeSnapshot[]>([]);
  const [redoStack, setRedoStack] = useState<ScopeSnapshot[]>([]);
  // Enables the Undo button during the ~300ms window before an in-progress
  // edit commits to the stack (a ref can't drive rendering on its own).
  const [pendingEdit, setPendingEdit] = useState(false);

  // True while a restore is in flight, so the watcher ignores the resulting
  // state changes instead of recording them as new history.
  const isUndoRedoRef = useRef(false);
  // Last state the watcher has "committed" as a baseline. null forces the
  // watcher to re-seed (used on New / file-open so the load isn't recorded).
  const lastCommittedRef = useRef<ScopeSnapshot | null>(null);
  // Baseline captured at the start of a debounced burst — the value we push
  // when the burst settles.
  const pendingBaselineRef = useRef<ScopeSnapshot | null>(null);
  const historyDebounceRef = useRef<number | null>(null);
  const HISTORY_DEBOUNCE_MS = 300;

  const buildScopeSnapshot = useCallback(
    (): ScopeSnapshot => ({
      constructionType,
      selectedDeckingId,
      deckingSqFt,
      selectedRailingId,
      railingLf,
      selectedStairsId,
      stairsCount,
      selectedFastenerId,
      selectedDemoId,
      demoQty,
      skirtingCategory,
      selectedSkirtingId,
      skirtingSf,
      // Clone the rows so a later in-place edit can't mutate a stored snapshot.
      addItems: addItems.map((r) => ({ ...r })),
    }),
    [
      constructionType,
      selectedDeckingId,
      deckingSqFt,
      selectedRailingId,
      railingLf,
      selectedStairsId,
      stairsCount,
      selectedFastenerId,
      selectedDemoId,
      demoQty,
      skirtingCategory,
      selectedSkirtingId,
      skirtingSf,
      addItems,
    ]
  );

  const scopeSnapshotsEqual = (a: ScopeSnapshot, b: ScopeSnapshot) =>
    JSON.stringify(a) === JSON.stringify(b);

  // Restore a scoped snapshot. Reuses the same setters applySnapshot uses, but
  // ONLY for the tracked subset. Forces the fastener/skirting "touched" guard
  // flags true so the decking-driven auto-pick effects don't immediately
  // re-stomp the restored values.
  const applyScopeSnapshot = useCallback((snap: ScopeSnapshot) => {
    isUndoRedoRef.current = true;
    setConstructionType(snap.constructionType);
    setSelectedDeckingId(snap.selectedDeckingId);
    setDeckingSqFt(snap.deckingSqFt);
    setSelectedRailingId(snap.selectedRailingId);
    setRailingLf(snap.railingLf);
    setSelectedStairsId(snap.selectedStairsId);
    setStairsCount(snap.stairsCount);
    setSelectedFastenerId(snap.selectedFastenerId);
    setSelectedDemoId(snap.selectedDemoId);
    setDemoQty(snap.demoQty);
    setSkirtingCategory(snap.skirtingCategory);
    setSelectedSkirtingId(snap.selectedSkirtingId);
    setSkirtingSf(snap.skirtingSf);
    setAddItems(snap.addItems.map((r) => ({ ...r })));
    // Guard the auto-pick effects (App.tsx decking→fastener / decking→skirting)
    // so a restored decking value doesn't drag fastener/skirting with it.
    setFastenerTouched(true);
    setSkirtingTypeTouched(true);
    lastCommittedRef.current = snap;
  }, []);

  const resetScopeHistory = useCallback(() => {
    if (historyDebounceRef.current !== null) {
      window.clearTimeout(historyDebounceRef.current);
      historyDebounceRef.current = null;
    }
    pendingBaselineRef.current = null;
    isUndoRedoRef.current = false;
    // null → watcher re-seeds its baseline to the freshly-loaded state without
    // recording it.
    lastCommittedRef.current = null;
    setUndoStack([]);
    setRedoStack([]);
    setPendingEdit(false);
  }, []);

  // The watcher. Runs after every render in which a tracked field changed.
  useEffect(() => {
    const current = buildScopeSnapshot();

    // First run after mount / reset: seed the baseline, record nothing.
    if (lastCommittedRef.current === null) {
      lastCommittedRef.current = current;
      return;
    }

    // No tracked change (an unrelated re-render) — nothing to do.
    if (scopeSnapshotsEqual(lastCommittedRef.current, current)) return;

    // Change came from a restore — absorb it, don't record.
    if (isUndoRedoRef.current) {
      isUndoRedoRef.current = false;
      lastCommittedRef.current = current;
      pendingBaselineRef.current = null;
      setPendingEdit(false);
      return;
    }

    // A genuine user edit. Capture the pre-edit baseline once per burst, then
    // (re)arm the debounce so rapid edits collapse into a single history step.
    if (pendingBaselineRef.current === null) {
      pendingBaselineRef.current = lastCommittedRef.current;
      setPendingEdit(true);
    }
    if (historyDebounceRef.current !== null) {
      window.clearTimeout(historyDebounceRef.current);
    }
    const settleBurst = () => {
      // Still typing in a quantity field: keep the burst open so the whole
      // edit becomes one step. Re-arming with this same closure is safe — a
      // newer edit would have cleared this timer and armed its own.
      if (isNumberFieldEditing()) {
        historyDebounceRef.current = window.setTimeout(
          settleBurst,
          HISTORY_DEBOUNCE_MS
        ) as unknown as number;
        return;
      }
      const baseline = pendingBaselineRef.current;
      // `current` here is the latest burst value: this timer belongs to the
      // last edit before the quiet period, so nothing newer exists.
      if (baseline && !scopeSnapshotsEqual(baseline, current)) {
        setUndoStack((s) => [...s, baseline]);
        setRedoStack([]);
      }
      lastCommittedRef.current = current;
      pendingBaselineRef.current = null;
      historyDebounceRef.current = null;
      setPendingEdit(false);
    };
    historyDebounceRef.current = window.setTimeout(
      settleBurst,
      HISTORY_DEBOUNCE_MS
    ) as unknown as number;
  }, [buildScopeSnapshot]);

  const canUndo = undoStack.length > 0 || pendingEdit;
  const canRedo = redoStack.length > 0;

  const handleUndo = useCallback(() => {
    const currentLive = buildScopeSnapshot();

    // An in-progress (uncommitted) burst: undo reverts to its pre-edit
    // baseline and the live edited state goes onto redo. Handled before the
    // committed stack so a fresh edit is always undoable immediately.
    if (historyDebounceRef.current !== null) {
      window.clearTimeout(historyDebounceRef.current);
      historyDebounceRef.current = null;
      const baseline = pendingBaselineRef.current;
      pendingBaselineRef.current = null;
      setPendingEdit(false);
      if (baseline && !scopeSnapshotsEqual(baseline, currentLive)) {
        setRedoStack((r) => [...r, currentLive]);
        applyScopeSnapshot(baseline);
        return;
      }
    }

    if (undoStack.length === 0) return;
    const baseline = undoStack[undoStack.length - 1];
    setUndoStack((u) => u.slice(0, -1));
    setRedoStack((r) => [...r, currentLive]);
    applyScopeSnapshot(baseline);
  }, [undoStack, buildScopeSnapshot, applyScopeSnapshot]);

  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    const currentLive = buildScopeSnapshot();
    setRedoStack((r) => r.slice(0, -1));
    setUndoStack((u) => [...u, currentLive]);
    applyScopeSnapshot(next);
  }, [redoStack, buildScopeSnapshot, applyScopeSnapshot]);

  // Ctrl/Cmd+Z = undo, Ctrl/Cmd+Shift+Z = redo. Estimator page only, and
  // ignored while a text input/textarea has focus so native text-edit undo
  // isn't hijacked (dropdowns/SELECT are fine to shortcut over).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (activeNav !== "estimator") return;
      const key = (e.key || "").toLowerCase();
      if (key !== "z") return;
      if (!(e.ctrlKey || e.metaKey)) return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || el?.isContentEditable) return;
      e.preventDefault();
      if (e.shiftKey) handleRedo();
      else handleUndo();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeNav, handleUndo, handleRedo]);

  // ===============================
  // CLOUD DRAFT SYNC (R2b)
  // Runs alongside the .DUest file flow — file save/load behavior
  // is unchanged. On mount: fetch the user's cloud draft and apply
  // if present. On any tracked field change: upsert the
  // current snapshot to Supabase, debounced. In local-desktop mode
  // (synthetic session) RLS rejects writes and we degrade silently.
  // ===============================
  const cloudDraftLoadedRef = useRef(false);
  // Startup draft load, tracked explicitly so a failure can't pass for "no
  // draft". 'idle' = no user to load for; 'loading' = in flight (Estimator is
  // locked so edits can't race the snapshot); 'loaded' = hit or genuine miss
  // (autosave allowed); 'failed' = error/timeout (autosave stays paused so a
  // blank estimate never overwrites the server draft — reload to retry).
  const [draftLoadState, setDraftLoadState] = useState<
    "idle" | "loading" | "loaded" | "failed"
  >(userId ? "loading" : "idle");

  useEffect(() => {
    if (!userId) {
      dlog("[draft] desktop load skipped — no userId yet");
      return;
    }
    if (cloudDraftLoadedRef.current) return;
    let cancelled = false;
    setDraftLoadState("loading");
    (async () => {
      dlog("[draft] desktop loadCloudDraft start", { userId });
      const result = await loadCloudDraftWithStatus(supabase, userId);
      if (cancelled) return;
      cloudDraftLoadedRef.current = true;
      if (result.status === "failed") {
        console.warn(
          "[draft] desktop draft load failed — autosave paused:",
          result.reason
        );
        setDraftLoadState("failed");
        return;
      }
      if (result.status === "miss") {
        dlog("[draft] desktop loadCloudDraft miss — no cloud row");
        setDraftLoadState("loaded");
        return;
      }
      const snap = result.snapshot;
      if (userChoseEstimateRef.current) {
        // A file was opened (or New) while the draft was still loading — e.g.
        // launching the app by double-clicking an estimate. Never replace the
        // user's choice silently. If the draft is unsaved work, ask; the
        // form stays locked (and autosave off) until they answer.
        if (lateDraftNeedsQuestion(snap, startupFileRecordRef.current)) {
          const draftName =
            [snap.clientLastName, snap.clientTown].filter(Boolean).join(", ") ||
            "Your previous estimate";
          const restore = await appDialogRef.current.confirm({
            title: "Restore your unsaved estimate?",
            message:
              `${draftName} was still loading from autosave when you opened ` +
              "another estimate. It was never saved to a file, so keeping " +
              "the one on screen discards it.\n\n" +
              "The file you opened stays on disk either way.",
            confirmLabel: "Restore unsaved estimate",
            cancelLabel: "Keep what's on screen",
            escapeResult: true,
          });
          if (cancelled) return;
          if (restore) {
            applySnapshot(snap);
            // The opened file's name/path belong to the estimate being
            // replaced; Save must not write the draft over that file.
            setLastSavedFileName(null);
            setLastSavedFilePath(null);
            dirtySuspendedRef.current = false;
            dlog("[draft] late draft restored over the opened estimate");
            setDraftLoadState("loaded");
            return;
          }
        }
        dlog("[draft] late draft not applied — user already opened an estimate");
        setDraftLoadState("loaded");
        return;
      }
      if (addItemsUserEditedRef.current) {
        // The lock should make this unreachable. If it happens anyway, keep
        // the user's edits on screen and pause autosave so neither copy is
        // thrown away.
        console.warn(
          "[draft] Add Items were edited before the cloud draft arrived — " +
            "not applying it; autosave paused so the server draft is kept."
        );
        setDraftLoadState("failed");
        return;
      }
      dlog("[draft] desktop loadCloudDraft hit — applying", {
        estimateId: snap.estimateId,
        clientLastName: snap.clientLastName,
        selectedDeckingId: snap.selectedDeckingId,
        deckingSqFt: snap.deckingSqFt,
      });
      applySnapshot(snap);
      // applySnapshot already resets isDirty; ensure the autosave
      // path is unblocked so subsequent edits write back.
      dirtySuspendedRef.current = false;
      setDraftLoadState("loaded");
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const cloudSaveDebouncedRef = useRef(
    makeDebouncer(
      (uid: string, snap: DraftSnapshot) => {
        // Fire and forget — never blocks UI.
        void saveCloudDraft(supabase, uid, snap);
      },
      800
    )
  );

  // Flush any pending cloud save when the user leaves the page.
  useEffect(() => {
    const handler = () => cloudSaveDebouncedRef.current.flush();
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  // ===============================
  // EMAIL PROPOSAL (JOIST STYLE)
  // 1) Save proposal to Supabase
  // 2) Call Edge Function to SEND email
  // 3) Show success toast
  // ===============================

  const handleEmailProposal = async () => {
    // Sender info gate. Without name + phone + email the proposal
    // signature comes out blank and the edge function has no
    // reply-to — block here so the customer never sees a
    // half-filled signature. Routes the user straight to Settings.
    if (!isSenderInfoComplete(userSettings)) {
      showToast(SENDER_INFO_MISSING_MESSAGE);
      setActiveNav("settings");
      return;
    }

    const to = (clientEmail || "").trim();

    if (!to) {
      showToast("Enter the client email first (Estimator → Client Email).");
      setActiveNav("estimator");
      return;
    }

    const client = {
      clientLastName,
      clientTown,
      clientEmail,
      // Client field expansion — pass the new fields so {{clientFirstName}}
      // (and the others) resolve in email subject/body templates.
      clientFirstName,
      clientLocation,
      clientState,
      clientZip,
      clientPhone,
    };

    // if offline, we cannot create a /review link
    if (!isOnline) {
      // Queue a placeholder email (no share link yet)
      setEmailDraft({
        to,
        subject: renderTemplate(
          userSettings?.emailSubjectTemplate ||
            "Your Proposal – {{clientLastName}}",
          buildEmailVars(client, userSettings)
        ),
        body: "Offline mode: proposal link will be generated and sent automatically when you're back online.\n",
        link: "",
        proposalId: "",
      });

      setEmailModalOpen(true);
      showToast("Offline: draft opened. Click Send to queue it ✅");
      return;
    }

    try {
      // Ensure we have an estimateId for tracking
      const ensuredEstimateId = estimateId || uid();
      if (!estimateId) setEstimateId(ensuredEstimateId);

      // 1) Save proposal to Supabase
      const lineItems = resolveProposalLineItems(pricingItems, {
        selectedDeckingId,
        selectedRailingId,
        selectedStairsId,
        selectedFastenerId,
        selectedDemoId,
        selectedSkirtingId,
        deckingSqFt,
        railingLf,
        stairsCount,
        fastenerQtyAuto,
        skirtingSf,
      });

      // ✅ Ensure emailed/review proposal uses the SAME full sections as Print Proposal
      const sectionsSnapshot = await fetchProposalSectionsSnapshot(
        supabase,
        orgId
      );

      // Freeze the Visual Appendix against the LATEST library, not a possibly
      // stale in-memory cache, so a photo uploaded earlier this session is
      // included in the sent/review proposal without an app reload. Falls back
      // to the live memo if the refetch fails.
      let freshVisuals = proposalVisuals;
      try {
        const freshRecords = await loadVisualRecords();
        freshVisuals = resolveProposalVisualsFrom(freshRecords, {
          decking: selectedDecking,
          railing: selectedRailing,
          skirting: selectedSkirting,
          fastener: selectedFastener,
          skirtingCategory,
          addItems: addItemsDetailed as any[],
          stairsPresent,
          demoPresent,
          gatePresent,
        });
      } catch {
        /* keep the live memo value */
      }

      const payload = buildProposalPayload({
        snapshot: { ...buildSnapshot(), visuals: freshVisuals },
        sectionsSnapshot,
        userSettings,
        estimateName,
        finalEstimate,
        constructionType,
        client,
        computed: {
          deckingSubtotal,
          railingSubtotal,
          stairsSubtotal,
          fastenerSubtotal,
          demoSubtotal,
          skirtingSubtotal,
          addItemsDetailed,
          upliftMultiplier,
          // Frozen at send time, exactly like lineItems: the /review page has
          // no pricing engine to recompute with. The ticked tier KEYS also
          // travel (via ...snapshot), so reopening the estimate still
          // recomputes from live pricing.
          deckingTierOptions: proposalDeckingTiers,
          quotedDeckingTier:
            (trexLevelsWhatIf?.rows || [])
              .map((r: any) => String(r.level))
              .find((lvl: string) => isCurrentDeckingTier(lvl)) ||
            selectedDecking?.name ||
            "",
        },
        lineItems,
        // Send the Notes the user is actually looking at. Blank means "nothing
        // live to hand", not "erase" — buildProposalPayload then falls back to
        // this estimate's own id-keyed cache entry.
        proposalNotesSnapshotOverride: proposalNotesRef.current,
      });

      const { id: savedId, updated } = await saveOrUpdateProposal(
        supabase,
        payload,
        ensuredEstimateId
      );
      if (savedId && !updated) {
        setProposalId(savedId);
      }

      if (!savedId) {
        alert("Could not generate proposal link. Please try again.");
        return;
      }

      const proposalId = savedId;
      const link = buildReviewLink(proposalId);

      // 2) Build subject/body from Settings templates
      const vars = buildEmailVars(client, userSettings);

      const subject = renderTemplate(
        userSettings?.emailSubjectTemplate ||
          "Your Proposal – {{clientLastName}}",
        vars
      );

      // Add the link at the bottom (always)
      const bodyBase = renderEmailBody(
        userSettings?.emailBodyTemplate || "",
        vars
      );

     // Add the link at the bottom (always)

const body = (bodyBase || "").trim();

      // helpful: copy link to clipboard
      try {
        await navigator.clipboard.writeText(link);
      } catch {
        // ignore if browser blocks it
      }
      // 3) Store draft + open in-app send window
setEmailDraft({ to, subject, body, link, proposalId: proposalId || undefined });

      setEmailModalOpen(true);
    } catch (err: any) {
      console.error("EMAIL PROPOSAL ERROR:", err);

      const msg =
        err?.message ||
        err?.error_description ||
        err?.details ||
        JSON.stringify(err, null, 2);

      showToast("Email Proposal failed. Check console for details.");
      console.error("EMAIL PROPOSAL ERROR DETAILS:", msg);
    }
  };

  // ===============================
  // FILE → OPEN
  // ===============================
  const openFileInputRef = useRef<HTMLInputElement | null>(null);
  const estimatorDesktopApi =
    typeof window !== "undefined" ? (window as any).estimator : null;

  const loadSnapshotFromText = (
    text: string,
    fileName: string,
    filePath?: string | null
  ) => {
    const snapshot = JSON.parse(text);
    if (!snapshot || typeof snapshot !== "object") {
      throw new Error("Invalid estimate file.");
    }

    applySnapshot(snapshot);
    fileLoadPendingRef.current = true;
    userChoseEstimateRef.current = true;
    setLastSavedFileName(fileName);
    setLastSavedFilePath(filePath || null);

    const recentName =
      (snapshot.estimateName || "").trim() ||
      fileName
        .replace(/\.json$/i, "")
        .replace(/\.duest$/i, "")
        .replace(/\.est$/i, "");

    pushRecent(recentName, snapshot);
    refreshRecents();

    dirtySuspendedRef.current = false;
    setActiveNav("estimator");
  };

  const onPickOpenFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      loadSnapshotFromText(text, file.name, null);
    } catch (err: any) {
      void appDialog.alert({ title: "Could not open that file.", message: "It may be corrupted." });
      console.error(err);
    } finally {
      e.target.value = "";
    }
  };

  // Desktop double-click / "Open with": load the .est/.DUest file the OS handed
  // us, exactly like the in-app Open dialog. Two arrival paths:
  //   • Cold start — we PULL the queued file via takePendingEstimateFile. Doing
  //     it here (inside the mounted effect) guarantees we're ready, avoiding the
  //     old race where main pushed on did-finish-load before this listener
  //     existed and the message was dropped (the blank-screen bug).
  //   • Already running — main PUSHES via onOpenEstimateFileFromDisk when a
  //     second launch is folded into this instance; the listener is attached.
  // A ref keeps loading bound to the latest loadSnapshot closure.
  const loadSnapshotFromTextRef = useRef(loadSnapshotFromText);
  loadSnapshotFromTextRef.current = loadSnapshotFromText;
  // Same reason: the listener below is registered once, so it must reach the
  // CURRENT unsaved-work check, not the one from the first render.
  const hasUnsavedWorkNowRef = useRef(hasUnsavedWorkNow);
  hasUnsavedWorkNowRef.current = hasUnsavedWorkNow;
  useEffect(() => {
    const api =
      typeof window !== "undefined" ? (window as any).estimator : null;
    if (!api) return;

    type OpenPayload = { text: string; fileName: string; filePath?: string };
    const loadFromDisk = async (
      payload: OpenPayload | null,
      source: string
    ) => {
      if (!payload || !payload.text) return;
      // A double-click in Explorer while the app is running used to replace
      // the open estimate with no warning — unsaved work gone. Same guard and
      // wording as File > Open and Open Recent. (Cold start has no open work
      // to lose: the file IS what the user launched the app to open.)
      if (source === "second-instance" && hasUnsavedWorkNowRef.current()) {
        const ok = await appDialogRef.current.confirm(OPEN_ANYWAY_DIALOG);
        if (!ok) {
          api.logLine?.(`open from disk cancelled (unsaved changes): ${payload.fileName}`);
          return;
        }
      }
      try {
        api.logLine?.(
          `renderer loading estimate (${source}): ${payload.fileName}`
        );
        loadSnapshotFromTextRef.current(
          payload.text,
          payload.fileName,
          payload.filePath || null
        );
      } catch (err) {
        api.logLine?.(`renderer load failed (${source}): ${String(err)}`);
        console.error("Failed to open estimate file from disk", err);
        void appDialogRef.current.alert({ title: "Could not open that file.", message: "It may be corrupted." });
      }
    };

    // Cold start: ask main for any file queued at launch.
    let cancelled = false;
    if (api.takePendingEstimateFile) {
      api
        .takePendingEstimateFile()
        .then((payload: OpenPayload | null) => {
          if (!cancelled) void loadFromDisk(payload, "cold-start");
        })
        .catch((err: unknown) =>
          console.error("takePendingEstimateFile failed", err)
        );
    }

    // Already running: subscribe for pushes from the single-instance handler.
    const unsub = api.onOpenEstimateFileFromDisk
      ? api.onOpenEstimateFileFromDisk((payload: OpenPayload) =>
          void loadFromDisk(payload, "second-instance")
        )
      : undefined;

    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, []);

  // File helpers (EST_EXT / defaultFileName / normalizeFileName /
  // downloadTextFile) live in src/shared/estimateFile.ts so the mobile
  // shell uses the same code path and produces identical .DUest files.
  const buildDefaultFileName = () =>
    defaultFileName({
      town: clientTown,
      lastName: clientLastName,
      estimateName,
    });

  // On save, advance an un-overridden Contract date to the save date so a
  // revised contract prints "today" on both Acceptance-block signature lines
  // and the ST-124 (all of which read hdrDate). A manually-edited date
  // (hdrDateTouched, set from the ContractPage Contract-date field) is left
  // frozen. Uses the same toLocaleDateString() (no-arg) format as
  // defaultContract so a same-day date compares equal and isn't needlessly
  // rewritten. Returns the same reference when nothing changes so callers can
  // skip setContract.
  const withRefreshedContractDate = (contractObj: any) => {
    if (!contractObj || typeof contractObj !== "object") return contractObj;
    if (contractObj.hdrDateTouched) return contractObj;
    const today = new Date().toLocaleDateString();
    if (contractObj.hdrDate === today) return contractObj;
    return { ...contractObj, hdrDate: today };
  };

  /** Resolves true once the file is written, false if cancelled or failed. */
  const handleFileSaveAs = async (): Promise<boolean> => {
    const ensuredEstimateId = estimateId || uid();
    if (!estimateId) setEstimateId(ensuredEstimateId);

    // setContract uses the raw setter (not handleContractChange) so refreshing
    // the date on save doesn't re-flag the file dirty right after we clear it.
    const refreshedContract = withRefreshedContractDate(contract);
    if (refreshedContract !== contract) setContract(refreshedContract);

    const snap = {
      ...buildSnapshot(),
      contract: refreshedContract,
      estimateId: ensuredEstimateId,
    };
    const text = JSON.stringify(snap, null, 2);

    if (estimatorDesktopApi?.isDesktop && estimatorDesktopApi?.saveEstimateFileAs) {
      const result = await estimatorDesktopApi.saveEstimateFileAs({
        defaultPath: lastSavedFilePath || buildDefaultFileName(),
        text,
      });

      if (result?.canceled) return false;
      if (!result?.ok) {
        void appDialog.alert({
          title: "Could not save that estimate file.",
          message: result?.error || undefined,
        });
        return false;
      }

      const savedName = normalizeFileName(result.fileName || buildDefaultFileName());
      setLastSavedFileName(savedName);
      setLastSavedFilePath(result.filePath || null);

      const recentLabel = savedName.replace(new RegExp(`${EST_EXT}$`, "i"), "");
      pushRecent(recentLabel, snap);
      refreshRecents();
      markCleanAsSaved(snap);
      return true;
    }

    const input = prompt("Save As file name:", buildDefaultFileName());
    if (!input) return false;

    const filename = normalizeFileName(input);
    if (!filename) return false;

    const recentLabel = filename.replace(new RegExp(`${EST_EXT}$`, "i"), "");
    pushRecent(recentLabel, snap);
    refreshRecents();

    downloadTextFile(text, filename);
    setLastSavedFileName(filename);
    setLastSavedFilePath(null);
    markCleanAsSaved(snap);
    return true;
  };

  // Save straight into the synced Dropbox folder. Desktop only, and
  // deliberately NOT an API integration: the Dropbox desktop client already
  // syncs a local folder, so writing a normal file into it gets the estimate
  // to Dropbox with no OAuth, no tokens, and no network dependency — it works
  // offline and uploads whenever the machine reconnects.
  //
  // Mirrors handleFileSaveAs: same snapshot, same recents/dirty bookkeeping,
  // and it adopts the saved path so a later plain Save overwrites in place.
  const handleSaveToDropbox = async () => {
    if (!estimatorDesktopApi?.isDesktop || !estimatorDesktopApi?.saveEstimateToDropbox) {
      void appDialog.alert({
        title: "Save to Dropbox is available in the desktop app.",
        message: "On the web, use Save As… and pick your Dropbox folder.",
      });
      return;
    }

    const ensuredEstimateId = estimateId || uid();
    if (!estimateId) setEstimateId(ensuredEstimateId);

    const refreshedContract = withRefreshedContractDate(contract);
    if (refreshedContract !== contract) setContract(refreshedContract);

    const snap = {
      ...buildSnapshot(),
      contract: refreshedContract,
      estimateId: ensuredEstimateId,
    };
    const text = JSON.stringify(snap, null, 2);

    const result = await estimatorDesktopApi.saveEstimateToDropbox({
      text,
      fileName: lastSavedFileName || buildDefaultFileName(),
      preferredDir: storageGet(DROPBOX_DIR_KEY) || "",
    });

    if (result?.canceled) return;

    if (result?.reason === "no-dropbox") {
      void appDialog.alert({
        title: "Couldn't find a Dropbox folder on this computer.",
        message:
          "Install the Dropbox desktop app and sign in, then try again — " +
          "or use Save As… to pick the folder yourself.",
      });
      return;
    }

    if (!result?.ok) {
      void appDialog.alert({
        title: "Could not save that estimate to Dropbox.",
        message: result?.error || undefined,
      });
      return;
    }

    // Remember the folder so the next Dropbox save opens there instead of the
    // Dropbox root — this is what makes repeat saves one click.
    if (result.dir) storageSet(DROPBOX_DIR_KEY, result.dir);

    const savedName = normalizeFileName(result.fileName || buildDefaultFileName());
    setLastSavedFileName(savedName);
    setLastSavedFilePath(result.filePath || null);

    const recentLabel = savedName.replace(new RegExp(`${EST_EXT}$`, "i"), "");
    pushRecent(recentLabel, snap);
    refreshRecents();
    markCleanAsSaved(snap);

    // The dialog lets the user navigate anywhere, so a file can land outside
    // Dropbox. Say so rather than let them assume it will sync.
    if (result.inDropbox === false) {
      void appDialog.alert({
        title: `Saved to ${result.dir}.`,
        message:
          "Heads up: that folder is outside Dropbox, so this file won't sync.",
      });
    }
  };

  // Duplicate as new estimate: keep ALL current data (client, line items,
  // SOW, notes, pricing) but fork a brand-new identity. This is the supported
  // way to make a second variant (e.g. different materials) for the same
  // client that gets its OWN independent /review link instead of overwriting
  // the original's proposal record.
  //
  // It mints a fresh estimateId, drops the link to the original's proposal
  // (proposalId), and detaches from the original's saved file so the next
  // Save won't overwrite it. The new link is created the next time the
  // duplicate is sent (a blank stored proposalId for the new id forces an
  // insert, not an update).
  const handleDuplicateAsNew = () => {
    const ok = window.confirm(
      "Make an independent copy of this estimate?\n\n" +
        "All your current data is kept, but the copy becomes a separate " +
        "estimate with its own proposal link. Use this for a second variant " +
        "(e.g. different materials) for the same client.\n\n" +
        "Use “Save As…” afterward to save it under a new file name."
    );
    if (!ok) return;

    const newId = uid();
    setEstimateId(newId);
    try {
      localStorage.setItem("du_estimate_id", newId);
    } catch {}

    // Break the tie to the original estimate's proposal record + saved file.
    setProposalId("");
    setLastSavedFileName(null);
    setLastSavedFilePath(null);
    markForcedDirty();
  };

  const handleFileSave = async () => {
    const refreshedContract = withRefreshedContractDate(contract);
    if (refreshedContract !== contract) setContract(refreshedContract);

    const snap = { ...buildSnapshot(), contract: refreshedContract };
    const text = JSON.stringify(snap, null, 2);

    if (estimatorDesktopApi?.isDesktop && estimatorDesktopApi?.saveEstimateFile) {
      if (!lastSavedFilePath) {
        await handleFileSaveAs();
        return;
      }

      // No "Overwrite existing file?": lastSavedFilePath is the file this
      // estimate was opened from or last saved to (New and Duplicate clear
      // it), so Save writing back to it is what the user asked for. Save As
      // onto a different existing file still gets the OS's Replace prompt.
      const result = await estimatorDesktopApi.saveEstimateFile({
        filePath: lastSavedFilePath,
        text,
      });

      if (!result?.ok) {
        void appDialog.alert({
          title: "Could not save that estimate file.",
          message: result?.error || undefined,
        });
        return;
      }

      const savedName = normalizeFileName(result.fileName || lastSavedFileName || buildDefaultFileName());
      setLastSavedFileName(savedName);
      setLastSavedFilePath(result.filePath || lastSavedFilePath);

      const recentLabel = savedName.replace(new RegExp(`${EST_EXT}$`, "i"), "");
      pushRecent(recentLabel, snap);
      refreshRecents();
      markCleanAsSaved(snap);
      return;
    }

    // Web: same rule. No file name yet → Save As; otherwise download again
    // under this estimate's own name without asking.
    if (!lastSavedFileName) {
      await handleFileSaveAs();
      return;
    }

    const recentLabel = lastSavedFileName.replace(
      new RegExp(`${EST_EXT}$`, "i"),
      ""
    );
    pushRecent(recentLabel, snap);
    refreshRecents();

    downloadTextFile(text, lastSavedFileName);
    setLastSavedFilePath(null);
    markCleanAsSaved(snap);
  };

  const handleFileOpen = async () => {
    if (hasUnsavedWorkNow()) {
      const ok = await appDialog.confirm(OPEN_ANYWAY_DIALOG);
      if (!ok) return;
    }

    if (estimatorDesktopApi?.isDesktop && estimatorDesktopApi?.openEstimateFile) {
      try {
        const result = await estimatorDesktopApi.openEstimateFile();
        if (result?.canceled) return;
        if (result?.error) {
          void appDialog.alert({
            title: "Could not open that file.",
            message: result.error || undefined,
          });
          return;
        }
        loadSnapshotFromText(
          String(result?.text || ""),
          String(result?.fileName || "Estimate"),
          result?.filePath || null
        );
      } catch (err) {
        void appDialog.alert({ title: "Could not open that file.", message: "It may be corrupted." });
        console.error(err);
      }
      return;
    }

    openFileInputRef.current?.click();
  };

  const openRecent = async (rf: RecentFile) => {
    if (hasUnsavedWorkNow()) {
      const ok = await appDialog.confirm(OPEN_ANYWAY_DIALOG);
      if (!ok) return;
    }

    try {
      applySnapshot(rf.json);
      // Recents hold the copy that was last saved or opened.
      fileLoadPendingRef.current = true;
      userChoseEstimateRef.current = true;
      setLastSavedFileName(rf.name);
      setLastSavedFilePath(null);
      pushRecent(rf.name, rf.json);
      refreshRecents();
      setRecentOpen(false);
      setFileOpen(false);
      setActiveNav("estimator");
    } catch (e) {
      console.error("Failed to open recent:", e);
      void appDialog.alert({ title: "Could not open that recent file." });
    }
  };
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isSaveShortcut = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s";
      if (!isSaveShortcut) return;
      e.preventDefault();
      if (e.shiftKey) {
        void handleFileSaveAs();
      } else {
        void handleFileSave();
      }
    };

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirty && !sectionEditsPending && !proposalTextPending) return;
      e.preventDefault();
      e.returnValue = "";
    };

    // Skip the beforeunload guard in Electron. Electron silently cancels
    // window close when a renderer sets preventDefault/returnValue with no
    // native confirm dialog — so the X button does nothing once the file is
    // dirty. TODO: replace with a main-process intercept (window.on('close')
    // + dialog.showMessageBox) so we keep the unsaved-changes warning on
    // desktop too. Browsers still get the native prompt.
    const isDesktop =
      !!(window as any).estimator?.isDesktop ||
      navigator.userAgent.includes("Electron");

    window.addEventListener("keydown", onKeyDown);
    if (!isDesktop) {
      window.addEventListener("beforeunload", onBeforeUnload);
    }

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      if (!isDesktop) {
        window.removeEventListener("beforeunload", onBeforeUnload);
      }
    };
  }, [
    handleFileSave,
    handleFileSaveAs,
    isDirty,
    sectionEditsPending,
    proposalTextPending,
  ]);

  // Desktop half of the unsaved-changes guard. The renderer cannot veto its own
  // close — Electron cancels a beforeunload preventDefault without ever showing
  // a dialog, which is why the effect above skips desktop — so instead we push
  // the dirty flag to the main process, which owns the window 'close' event and
  // can show a real native prompt. Web is unaffected (no window.estimator).
  useEffect(() => {
    try {
      (window as any).estimator?.setDirty?.(
        isDirty || sectionEditsPending || proposalTextPending
      );
    } catch {
      /* the guard is best-effort; never break the app over it */
    }
  }, [isDirty, sectionEditsPending, proposalTextPending]);

  const buildDefaultEstimateName = () => {
    const town = (clientTown || "").trim();
    const last = (clientLastName || "").trim();
    const baseName = `${town} ${last}`.trim();

    if (!town || !last) return "";

    const counterKey = `du_estimate_counter::${baseName.toLowerCase()}`;
    const current = Number(localStorage.getItem(counterKey) || "0");
    const next = current + 1;
    localStorage.setItem(counterKey, String(next));

    return `${baseName} Est${next}`;
  };

  // Save As (forced prompt + default filename), then New — but only once the
  // file is actually written. It used to start New without waiting, so
  // cancelling the Save dialog still wiped the estimate.
  const saveAndNew = async () => {
    const saved = await handleFileSaveAs();
    setConfirmNewOpen(false);
    if (saved) handleNewProject();
  };

  // ===============================
  // ✅ PRICING STATE (FIXED)
  // ===============================
  const [pricingItems, setPricingItems] = useState<PricingItemRow[]>([]);
  useEffect(() => {
    // Only do this when user selected "Skirting"
    if (skirtingCategory !== "Skirting") {
      lastAutoSkirtingDeckingId.current = "";
      setSkirtingTypeTouched(false); // reset for next time
      return;
    }

    // Need a decking selected to match against
    if (!selectedDeckingId) return;

    // If user already manually chose a skirting type, don't override
    if (skirtingTypeTouched) return;

    // If we already auto-set for this same decking id, do nothing
    if (lastAutoSkirtingDeckingId.current === selectedDeckingId) return;

    // Find the selected decking record
    const deckRow = pricingItems.find(
      (p: any) => String(p.id) === String(selectedDeckingId)
    );
    const deckName = (deckRow?.name || "").trim().toLowerCase();
    if (!deckName) return;

    // Pull skirting options (your category might be "Skirting" or "Skirting_options")
    const skirtingOptions = pricingItems.filter((p: any) =>
      String(p.category || "")
        .toLowerCase()
        .includes("skirting")
    );

    // Match by name containing the decking name (your naming convention)
    const match = skirtingOptions.find((p: any) =>
      String(p.name || "")
        .trim()
        .toLowerCase()
        .includes(deckName)
    );

    if (match) {
      setSelectedSkirtingId(String(match.id));
      lastAutoSkirtingDeckingId.current = selectedDeckingId;
    }
  }, [skirtingCategory, selectedDeckingId, pricingItems, skirtingTypeTouched]);

  const [pricingCategories, setPricingCategories] = useState<
    PricingCategoryRow[]
  >([]);
  const [pricingLoaded, setPricingLoaded] = useState(false);
  const [pricingError, setPricingError] = useState<string | null>(null);

  // ------------------------------
  // LOAD PRICING (CACHE FIRST, REFRESH FAST, FAIL FAST OFFLINE)
  // ------------------------------
  const [pricingFrozenFromFile, setPricingFrozenFromFile] = useState(false);
  const [pricingSnapshotAt, setPricingSnapshotAt] = useState<string | null>(null);
  const [livePricingItems, setLivePricingItems] = useState<PricingItemRow[] | null>(null);
  // Live categories fetched alongside livePricingItems on the frozen path.
  // Chunk 2 will source the Add Item category dropdown from this so newly-added
  // Pricing Admin categories appear in old/frozen estimates. Not consumed yet.
  const [livePricingCategories, setLivePricingCategories] = useState<
    PricingCategoryRow[] | null
  >(null);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  useEffect(() => {
    const loadPricing = async () => {
      if (pricingFrozenFromFile) {
        startupLog("pricing frozen from file - skipping live refresh");
        return;
      }
      const startedAt = performance.now();
      setPricingLoaded(false);
      setPricingError(null);

      const applyPricing = (itemsRaw: any[], catsRaw: any[]) => {
        const cleanedItems = cleanPricingRows(itemsRaw);

        setPricingItems(cleanedItems as PricingItemRow[]);
        setPricingCategories((catsRaw || []) as PricingCategoryRow[]);
        setPricingLoaded(true);
        dirtySuspendedRef.current = false;
      };

      let hadCache = false;
      try {
        const rawItems = localStorage.getItem(PRICING_ITEMS_CACHE_KEY);
        const rawCats = localStorage.getItem(PRICING_CATS_CACHE_KEY);

        if (rawItems && rawCats) {
          const cachedItems = JSON.parse(rawItems);
          const cachedCats = JSON.parse(rawCats);
          applyPricing(cachedItems, cachedCats);
          hadCache = true;
          startupLog("pricing cache applied", {
            items: cachedItems.length || 0,
            categories: cachedCats.length || 0,
            cacheAgeMs: Math.max(0, Date.now() - Number(localStorage.getItem(PRICING_CACHE_TS_KEY) || 0)),
            elapsedMs: Math.round(performance.now() - startedAt),
          });
        }
      } catch (error) {
        startupLog("pricing cache read failed", error);
      }

      if (typeof navigator !== "undefined" && !navigator.onLine) {
        if (hadCache) {
          setPricingError("Offline mode: using cached pricing.");
        } else {
          setPricingError("Offline mode: no cached pricing yet. Using empty pricing lists.");
          setPricingLoaded(true);
        }
        startupLog("pricing refresh skipped: offline", {
          hadCache,
          elapsedMs: Math.round(performance.now() - startedAt),
        });
        return;
      }

      try {
        const [itemsRes, catsRes] = await withTimeout(
          Promise.all([
            supabase
              .from("pricing_items2")
              .select("*")
              .order("sort_order", { ascending: true })
              .order("name", { ascending: true }),
            supabase
              .from("pricing_categories")
              .select("id, name, is_active")
              .order("name", { ascending: true }),
          ]),
          STARTUP_FETCH_TIMEOUT_MS,
          "pricing bootstrap"
        );

        if (itemsRes.error) throw itemsRes.error;
        if (catsRes.error) throw catsRes.error;

        const items = itemsRes.data || [];
        const cats = catsRes.data || [];

        try {
          localStorage.setItem(PRICING_ITEMS_CACHE_KEY, JSON.stringify(items));
          localStorage.setItem(PRICING_CATS_CACHE_KEY, JSON.stringify(cats));
          localStorage.setItem(PRICING_CACHE_TS_KEY, String(Date.now()));
        } catch {}

        applyPricing(items, cats);
        setPricingError(null);
        startupLog("pricing refresh success", {
          items: items.length,
          categories: cats.length,
          elapsedMs: Math.round(performance.now() - startedAt),
        });
      } catch (err: any) {
        startupLog("pricing refresh fallback", {
          message: err?.message || String(err),
          hadCache,
          elapsedMs: Math.round(performance.now() - startedAt),
        });
        if (!hadCache) {
          setPricingError(
            err?.message ||
              "Offline mode: no cached pricing yet. Using empty pricing lists."
          );
          setPricingLoaded(true);
        } else {
          setPricingError("Offline mode: using cached pricing. Reconnect to refresh.");
        }
      }
    };

    loadPricing();
  }, [pricingFrozenFromFile]);

  // ------------------------------
  // BACKGROUND LIVE-PRICE FETCH (for diff detection on frozen files)
  // Also fetches live categories (livePricingCategories) so Chunk 2 can
  // source the Add Item dropdowns from the current catalog while frozen
  // prices stay put. Both are read-only mirrors of live Supabase — they
  // never touch pricingItems/pricingCategories, so frozen prices cannot
  // drift from loading them.
  // ------------------------------
  useEffect(() => {
    if (!pricingFrozenFromFile) {
      setLivePricingItems(null);
      setLivePricingCategories(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [itemsRes, catsRes] = await Promise.all([
          supabase
            .from("pricing_items2")
            .select("*")
            .order("sort_order", { ascending: true }),
          supabase
            .from("pricing_categories")
            .select("id, name, is_active")
            .order("name", { ascending: true }),
        ]);
        if (cancelled) return;
        if (!itemsRes.error && Array.isArray(itemsRes.data)) {
          setLivePricingItems(itemsRes.data as PricingItemRow[]);
        }
        if (!catsRes.error && Array.isArray(catsRes.data)) {
          setLivePricingCategories(catsRes.data as PricingCategoryRow[]);
        }
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [pricingFrozenFromFile, estimateId]);

  // ------------------------------
  // LIVE PRICE REFRESH after Pricing Admin changes (no restart needed)
  //
  // Pricing Admin calls onPricingSaved after every successful save. While the
  // user is still on Pricing Admin we only note it; when they leave (or when
  // a save lands after they've left — the last field saves on the way out)
  // we refetch. Silent on purpose: unlike the boot loader it never sets
  // pricingLoaded=false (that would unmount the Estimator form) and never
  // touches dirtySuspendedRef. A frozen file keeps its frozen prices — only
  // the live mirrors are refreshed, which feed the "prices changed" check
  // and the new-items merge.
  // ------------------------------
  const { onPricingSaved, refreshTick: pricingRefreshTick } =
    usePricingRefreshTrigger(activeNav);

  useEffect(() => {
    if (pricingRefreshTick === 0) return;
    if (typeof navigator !== "undefined" && !navigator.onLine) return;
    let cancelled = false;
    (async () => {
      try {
        const [itemsRes, catsRes] = await Promise.all([
          supabase
            .from("pricing_items2")
            .select("*")
            .order("sort_order", { ascending: true })
            .order("name", { ascending: true }),
          supabase
            .from("pricing_categories")
            .select("id, name, is_active")
            .order("name", { ascending: true }),
        ]);
        if (cancelled || itemsRes.error || catsRes.error) return;
        const items = itemsRes.data || [];
        const cats = catsRes.data || [];
        try {
          localStorage.setItem(PRICING_ITEMS_CACHE_KEY, JSON.stringify(items));
          localStorage.setItem(PRICING_CATS_CACHE_KEY, JSON.stringify(cats));
          localStorage.setItem(PRICING_CACHE_TS_KEY, String(Date.now()));
        } catch {}
        if (pricingFrozenFromFile) {
          setLivePricingItems(items as PricingItemRow[]);
          setLivePricingCategories(cats as PricingCategoryRow[]);
        } else {
          setPricingItems(cleanPricingRows(items) as PricingItemRow[]);
          setPricingCategories(cats as PricingCategoryRow[]);
        }
        showToast("Price list updated from Pricing Admin.");
      } catch {
        // Keep the prices already on screen; the next launch refreshes.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingRefreshTick]);

  // ===============================
  // ESTIMATOR DERIVED DATA
  // ===============================
  const constructionTypeRef = useRef<HTMLSelectElement | null>(null);
  const skirtingCategoryRef = useRef<HTMLSelectElement | null>(null);

  useEffect(() => {
    if (activeNav === "estimator" && pricingLoaded) {
      constructionTypeRef.current?.focus();
    }
  }, [activeNav, pricingLoaded]);

  // ===============================
  // EFFECTIVE CATALOG — frozen-wins merge for pricing (Chunk 3)
  //
  // On a frozen file, computeEstimate/priceAddItem price against this merged
  // catalog instead of the raw frozen snapshot. The merge is ADDITIVE ONLY:
  // we start from the frozen array (so every frozen id keeps its frozen row,
  // found first by `.find`) and APPEND only live rows whose id is absent from
  // the snapshot. A frozen id is therefore never replaced and its price can
  // never drift to live. Genuinely-new ids — e.g. a Footing created in Pricing
  // Admin after this file was saved — resolve against the appended live row and
  // price at the real live price instead of $0.
  //
  // We deliberately EXCLUDE global_multiplier rows from the appended set. They
  // drive the permit / small-deck / finance / perceived AGGREGATE scans in
  // pricing.ts (getPermitTierForTotal / getSmallJobTierForTotal filter the
  // whole catalog), so appending a new one could move a frozen estimate's
  // uplift math. They are also never user-addable line items (blocked from the
  // Add Item categories), so excluding them never prevents pricing a real new
  // item — it just guarantees the uplift/permit math stays byte-identical to
  // the frozen snapshot.
  //
  // When not frozen (or live not yet loaded / offline), this is the frozen/live
  // pricingItems array unchanged — the live path and offline fallback are
  // untouched.
  // ===============================
  const effectiveCatalog = useMemo(() => {
    // Only merge on a frozen file; otherwise pricingItems is already live.
    // The frozen-wins merge rule itself lives in pricing.ts (mergeFrozenWithLive)
    // so it can be unit-tested directly.
    if (!pricingFrozenFromFile || !livePricingItems) return pricingItems;
    return mergeFrozenWithLive(pricingItems, livePricingItems);
  }, [pricingFrozenFromFile, livePricingItems, pricingItems]);

  // ===============================
  // PRICING — single source of truth is src/shared/pricing.ts.
  // computeEstimate is pure; both desktop and mobile call it.
  // The destructure binds locals with the same names downstream code
  // already uses, so JSX/handlers don't change.
  // ===============================
  const estimate = computeEstimate({
    pricingItems: effectiveCatalog,
    constructionType,
    selectedDeckingId,
    selectedRailingId,
    selectedStairsId,
    selectedFastenerId,
    selectedDemoId,
    selectedSkirtingId,
    skirtingCategory,
    deckingSqFt,
    railingLf,
    stairsCount,
    demoQty,
    skirtingSf,
    addItems,
    miValue,
    msrpMode,
    includePermit,
  });

  const {
    deckingOptions,
    railingOptions,
    stairsOptions,
    fastenerOptions,
    demoOptions,
    skirtingOnlyOptions,
    latticeOnlyOptions,
    skirtingLatticeOptions,
    constructionOptions,
    selectedDecking,
    selectedRailing,
    selectedFastener,
    selectedDemo,
    selectedSkirting,
    selectedConstruction,
    selectedStairsRow,
    selectedStairOption,
    constructionAdj,
    baseDeckingUnit,
    adjustedDeckingUnit,
    deckingSubtotal,
    baseRailingUnit,
    railingSubtotal,
    stairsCalc,
    baseStairsUnit,
    effectiveStairsRate,
    stairsSubtotal,
    baseFastenerUnit,
    fastenerQtyAuto,
    fastenerSubtotal,
    baseDemoUnit,
    demoSubtotal,
    skirtingCalc,
    effectiveSkirtingRate,
    skirtingSubtotal,
    addItemsDetailed,
    addItemsSubtotalUpliftable,
    addItemsSubtotalFixed,
    baseProjectTotal,
    displayedBasePriceForThresholds,
    financeRow,
    perceivedRow,
    financeMultiplier,
    perceivedMultiplier,
    permitTier,
    permitMultiplier,
    permitThreshold,
    smallTier,
    smallProjectMultiplier,
    rawFinancePercent,
    rawPerceivedPercent,
    financePercent,
    perceivedPercent,
    miPercent,
    smallJobPercent,
    permitPercent,
    totalUpliftPercent,
    upliftMultiplier,
    totalUpliftDollars,
    financeUpliftDollars,
    perceivedUpliftDollars,
    miUpliftDollars,
    permitUpliftDollars,
    smallJobUpliftDollars,
    projectTotalWithUplift,
    finalEstimate,
  } = estimate;

  // Main Stairs dropdown hides the add-on-only rows (Platform stairs, Mitered
  // Treads, Curved Step). They remain "Stair options" +Add Items, and pricing
  // still resolves against the unfiltered stairsOptions above.
  const mainStairsOptions = useMemo(
    () => excludeHiddenMainStairs(stairsOptions),
    [stairsOptions]
  );

  // Visual Library records for this org, loaded once. Used to resolve the
  // photos for the current selections and freeze them into the proposal
  // snapshot (see proposalVisuals below) so unauthenticated /review customers
  // can render the appendix without reading the org-scoped table.
  const [visualRecords, setVisualRecords] = useState<VisualLibraryRecord[]>([]);
  // Fetch the org's visual_library records into state. Reused by the initial
  // load, the nav-return refresh, and the send path (which also needs the rows
  // returned synchronously to freeze a current appendix).
  const loadVisualRecords = useCallback(async (): Promise<VisualLibraryRecord[]> => {
    if (!orgId) return [];
    const rows = await listVisualsForOrg(orgId);
    setVisualRecords(rows);
    return rows;
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    let alive = true;
    listVisualsForOrg(orgId)
      .then((rows) => {
        if (alive) setVisualRecords(rows);
      })
      .catch(() => {
        /* non-fatal: proposal simply renders no appendix */
      });
    return () => {
      alive = false;
    };
  }, [orgId]);

  // Mid-session freshness: when the user leaves the Visual Library for any other
  // tab, refetch so the estimator/proposal preview reflects photos just added or
  // removed — without an app reload. (The send path refetches again at freeze
  // time as a belt-and-suspenders guard against a race.)
  const prevNavRef = useRef(activeNav);
  useEffect(() => {
    const prev = prevNavRef.current;
    prevNavRef.current = activeNav;
    if (prev === "visualLibrary" && activeNav !== "visualLibrary") {
      void loadVisualRecords().catch(() => {});
    }
  }, [activeNav, loadVisualRecords]);

  // Stairs are "in the job" when the main Stairs card is configured OR any
  // "Stair options" add-item is present. Gates the Stairs appendix photo.
  const stairsPresent = useMemo(() => {
    if (!!selectedStairsId && Number(stairsCount) > 0) return true;
    return (addItemsDetailed as any[]).some(
      (r) =>
        (r?.category || "")
          .toLowerCase()
          .trim()
          .replace(/[\s-]+/g, "_") === "stair_options"
    );
  }, [selectedStairsId, stairsCount, addItemsDetailed]);

  // Demo is "in the job" when a demo type is chosen or a demo qty is entered.
  const demoPresent = !!selectedDemoId || Number(demoQty) > 0;

  // A Gate add-item is present when any +Add Item row is in the "gate" category.
  // Gates the Gate appendix photo (keyed to the selected railing).
  const gatePresent = useMemo(
    () =>
      (addItemsDetailed as any[]).some(
        (r) =>
          (r?.category || "")
            .toLowerCase()
            .trim()
            .replace(/[\s-]+/g, "_") === "gate"
      ),
    [addItemsDetailed]
  );

  // Resolve the matched visuals for the current selections (live preview).
  const proposalVisuals = useMemo<ProposalVisual[]>(
    () =>
      resolveProposalVisualsFrom(visualRecords, {
        decking: selectedDecking,
        railing: selectedRailing,
        skirting: selectedSkirting,
        fastener: selectedFastener,
        skirtingCategory,
        addItems: addItemsDetailed as any[],
        stairsPresent,
        demoPresent,
        gatePresent,
      }),
    [visualRecords, selectedDecking, selectedRailing, selectedSkirting, selectedFastener, skirtingCategory, addItemsDetailed, stairsPresent, demoPresent, gatePresent]
  );

  // Auto-pick the sensible default fastener for the chosen decking, unless the
  // user has manually picked one (fastenerTouched). PT Pine decking is
  // face-nailed → "Galv Nails"; composite/grooved boards → "Hidden Clips".
  // Re-runs on decking change so switching decking re-applies the correct
  // default, but never overrides a manual (or loaded) fastener choice.
  // Lives in the component because it dispatches a state update.
  useEffect(() => {
    if (!selectedDeckingId) return;
    if (fastenerTouched) return; // never stomp a deliberate choice
    const deckName = normalizeName(selectedDecking?.name || "");
    const isPtPine = deckName.includes("pt pine");
    const target = fastenerOptions.find((it) => {
      const n = normalizeName(it.name || "");
      return isPtPine ? n.includes("galv") : n.includes("hidden clips");
    });
    if (target?.id != null) {
      setSelectedFastenerId(String(target.id));
    }
  }, [selectedDeckingId, selectedDecking, fastenerTouched, fastenerOptions]);


  // ===============================
  // ADD ITEMS — categories + per-row dropdown (pure helpers from shared/pricing)
  //
  // The Add Item dropdowns enumerate the LIVE catalog so products added to
  // Pricing Admin after an old estimate was saved still show up and can be
  // added. On a frozen file we read livePricingItems/livePricingCategories
  // (Chunk 1's read-only mirrors); on a new/unfrozen file those are null and
  // pricingItems/pricingCategories are already live. Fall back to the frozen
  // arrays if the live mirror hasn't loaded yet or we're offline, so the
  // dropdowns are never empty.
  //
  // IMPORTANT: this only changes what the dropdowns ENUMERATE. computeEstimate
  // and priceAddItem still read the frozen pricingItems/pricingCategories, so
  // frozen prices do not move. A newly-picked item that isn't in the frozen
  // snapshot may price at $0 until Chunk 3 merges live prices for new items.
  // ===============================
  const dropdownCatalogItems =
    pricingFrozenFromFile && livePricingItems ? livePricingItems : pricingItems;
  const dropdownCatalogCategories =
    pricingFrozenFromFile && livePricingCategories
      ? livePricingCategories
      : pricingCategories;

  const addItemCategories = useMemo(
    () => buildAddItemCategories(dropdownCatalogCategories),
    [dropdownCatalogCategories]
  );

  // Thin wrapper so JSX call sites can stay (row) => options.
  const addItemOptionsForRow = (row: AddItemRow) =>
    addItemOptionsForRowPure(dropdownCatalogItems, row);
  // (selectedStairsRow is now provided by computeEstimate destructure above)

  // ------------------------------
  // FROZEN PRICE DIFF — items used in this estimate whose live price differs
  // ------------------------------
  const usedPricingItemIds = useMemo(() => {
    const ids = new Set<string>();

    // 1) Direct selections (ID-based)
    [
      selectedDeckingId,
      selectedRailingId,
      selectedStairsId,
      selectedFastenerId,
      selectedDemoId,
      selectedSkirtingId,
    ].forEach((id) => {
      if (id) ids.add(String(id));
    });
    addItems.forEach((r) => {
      if (r.itemId && !r.isFixedPrice) ids.add(String(r.itemId));
    });

    // 2) Construction adjustment (matched by NAME against constructionType,
    //    not stored as a *Id field) — its cost feeds the decking subtotal.
    if (selectedConstruction?.id) {
      ids.add(String(selectedConstruction.id));
    }

    // 3) Base stair row — name-matched in category "stair" against the
    //    selected decking name. Consulted by both main stairs and stair_options add-items.
    const stairAddItemPresent = addItems.some(
      (r) => normalizeCat(r.category || "") === "stair_options" || normalizeCat(r.category || "").includes("stair")
    );
    if (selectedDecking && (Number(stairsCount || 0) > 0 || stairAddItemPresent)) {
      const deckNm = normalizeName(selectedDecking.name || "");
      const baseStairRow = pricingItems.find((it) => {
        if (it.active === false) return false;
        if ((it.deleted_at ?? null) != null) return false;
        if (normalizeCat(it.category || "") !== "stair") return false;
        return normalizeName(it.name || "") === deckNm;
      });
      if (baseStairRow?.id) ids.add(String(baseStairRow.id));
    }

    // 4) Global multipliers — always consulted for uplift / permit math.
    //    Covers Finance, Perceived Value, and the various Permit Deck rows.
    pricingItems.forEach((p) => {
      const u = (p.unit || "").toLowerCase().trim();
      if (u === "global_multiplier") ids.add(String(p.id));
    });

    // 5) Add-item by-name lookups (mirrors the lookup logic in addItemsDetailed)
    addItems.forEach((row) => {
      // 5a) Construction-type add-item — looks up a decking_adjustment row by name
      if (isConstructionTypeCategory(row.category || "")) {
        const ctLabel = getConstructionTypeLabel(row.category || "") || "";
        const dbAdjRow = pricingItems.find((it) => {
          if ((it.unit || "").toLowerCase().trim() !== "decking_adjustment") return false;
          const cn = normalizeName(it.name || "");
          const ct = normalizeName(ctLabel || "");
          return (
            cn === ct ||
            (ct && cn.includes(ct)) ||
            (cn && ct.includes(cn)) ||
            cn.startsWith(ct) ||
            ct.startsWith(cn)
          );
        });
        if (dbAdjRow?.id) ids.add(String(dbAdjRow.id));
        if (row.deckingId) ids.add(String(row.deckingId));
      }

      // 5b) Bench add-item — base row keyed by "<decking> bench" / "<decking> bench 18in"
      if (normalizeCat(row.category || "") === "bench" && selectedDecking) {
        const baseRow = findBenchBaseRow(
          pricingItems,
          selectedDecking.name || "",
          String(row.benchType || "12_flat")
        );
        if (baseRow?.id) ids.add(String(baseRow.id));
      }
    });

    return ids;
  }, [
    selectedDeckingId,
    selectedRailingId,
    selectedStairsId,
    selectedFastenerId,
    selectedDemoId,
    selectedSkirtingId,
    addItems,
    selectedConstruction,
    selectedDecking,
    stairsCount,
    pricingItems,
  ]);

  const frozenPriceDiffs = useMemo(() => {
    if (!pricingFrozenFromFile || !livePricingItems) return [];
    const frozenById = new Map(
      pricingItems.map((i) => [String(i.id), i])
    );
    const diffs: Array<{
      id: string;
      name: string;
      unit: string;
      oldCost: number;
      newCost: number;
      delta: number;
    }> = [];
    livePricingItems.forEach((live) => {
      if (!usedPricingItemIds.has(String(live.id))) return;
      const frozen = frozenById.get(String(live.id));
      if (!frozen) return;
      const oldCost = Number(frozen.cost ?? 0);
      const newCost = Number(live.cost ?? 0);
      if (Math.abs(oldCost - newCost) < 0.005) return;
      diffs.push({
        id: String(live.id),
        name: String(live.name || frozen.name || "Unnamed"),
        unit: String(live.unit || frozen.unit || ""),
        oldCost,
        newCost,
        delta: newCost - oldCost,
      });
    });
    return diffs;
  }, [pricingFrozenFromFile, livePricingItems, pricingItems, usedPricingItemIds]);

  const handleAcceptFrozenSync = () => {
    if (!livePricingItems) return;
    setPricingItems(livePricingItems);
    setPricingSnapshotAt(new Date().toISOString());
    markForcedDirty();
    setSyncModalOpen(false);
    showToast("Prices updated to current. Save the file to lock in the new prices.");
  };

  // (addItemsDetailed + the two subtotals are now provided by computeEstimate
  //  destructure above. Per-row pricing rules live in priceAddItem in
  //  src/shared/pricing.ts.)

  // BASELINE COMPARISON — runs after every render. The fingerprint comes from
  // buildSnapshot itself, so every field the save writes is compared except
  // the few listed in NOT_COMPARED_KEYS. Cheap: the price list (the only big
  // part of the snapshot) is left out before stringifying.
  useEffect(() => {
    const current = estimateFingerprint(buildSnapshot());
    const autoContract =
      contract !== null && contract === autoContractValueRef.current;
    if (
      dirtySuspendedRef.current ||
      rebasePendingRef.current ||
      !baselineRef.current
    ) {
      baselineRef.current = current;
      lastContractFpRef.current = current.contract;
      rebasePendingRef.current = false;
      if (fileLoadPendingRef.current) {
        // The baseline is the file just opened. While suspended it keeps
        // following the state, so keep the record following it too.
        recordFileBaseline(String(buildSnapshot().estimateId || ""), current);
        if (!dirtySuspendedRef.current) fileLoadPendingRef.current = false;
      }
      if (!forcedDirtyRef.current) setIsDirty(false);
      return;
    }
    baselineRef.current = absorbAutoContract(
      baselineRef.current,
      lastContractFpRef.current,
      current,
      autoContract
    );
    lastContractFpRef.current = current.contract;
    setIsDirty(
      forcedDirtyRef.current || differsFromBaseline(baselineRef.current, current)
    );
  });

  // CLOUD AUTOSAVE — fires on the estimate fields below. Skipped until
  // the initial cloud-draft load has settled (so we don't overwrite
  // cloud with a half-hydrated local state during boot). Also
  // skipped while the dirty-suspended flag is set (during file open
  // / pricing bootstrap).
  useEffect(() => {
    if (!userId) return;
    // Only after a load that succeeded (hit or genuine miss). A failed or
    // timed-out load must never be followed by an autosave.
    if (draftLoadState !== "loaded") return;
    if (dirtySuspendedRef.current) return;
    const snap = buildSnapshot() as DraftSnapshot;
    cloudSaveDebouncedRef.current(userId, snap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userId,
    clientLastName,
    clientTown,
    clientEmail,
    clientFirstName,
    clientLocation,
    clientState,
    clientZip,
    clientPhone,
    constructionType,
    includePermit,
    msrpMode,
    selectedDeckingId,
    deckingSqFt,
    selectedRailingId,
    railingLf,
    selectedStairsId,
    stairsCount,
    selectedFastenerId,
    selectedDemoId,
    demoQty,
    skirtingCategory,
    selectedSkirtingId,
    skirtingSf,
    miValue,
    JSON.stringify(addItems),
    JSON.stringify(proposalDeckingOptions),
    contract,
    // Proposal-page text. Edited only on the Proposal page, so without these
    // an edit to either field never marked the estimate dirty or reached the
    // cloud draft — closing the app lost it with no warning.
    sowText,
    projectDescription,
    sowTouched,
    projectDescriptionTouched,
  ]);

  // (Uplift / final-estimate scalars now come from computeEstimate destructure
  //  above. The upliftBreakdown useMemo below stays — it computes rounded
  //  display values consumed by the Analytics page.)
    const upliftBreakdown = useMemo(() => {
    const money0 = (n: number) =>
      (n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

    const basePrice =
      (Number(deckingSubtotal) || 0) +
      (Number(railingSubtotal) || 0) +
      (Number(stairsSubtotal) || 0) +
      (Number(fastenerSubtotal) || 0) +
      (Number(demoSubtotal) || 0) +
      (Number(skirtingSubtotal) || 0) +
      (addItemsDetailed as any[])
        .filter((r) => r && r.picked && Number(r.lineBase || 0) !== 0)
        .reduce((sum, r) => sum + (Number(r.lineBase) || 0), 0);

    const permitAmt = (basePrice * (Number(permitPercent) || 0)) / 100;
    const smallJobAmt = (basePrice * (Number(smallJobPercent) || 0)) / 100;
    const msrp = basePrice + permitAmt + smallJobAmt;

        const perceivedAmt = (basePrice * (Number(perceivedPercent) || 0)) / 100;
    const financeAmt = (basePrice * (Number(financePercent) || 0)) / 100;
    const miAmt = (basePrice * (Number(miPercent) || 0)) / 100;
    const upliftSubtotal = perceivedAmt + financeAmt + miAmt;
    const totalCost = msrp + perceivedAmt + financeAmt + miAmt;

    const r0 = (n: number) => Math.round(Number(n) || 0);

    return {
      money0,
           basePrice: r0(basePrice),
      permitAmt: r0(permitAmt),
      smallJobAmt: r0(smallJobAmt),
      msrp: r0(msrp),
      perceivedAmt: r0(perceivedAmt),
      financeAmt: r0(financeAmt),
      miAmt: r0(miAmt),
      upliftSubtotal: r0(upliftSubtotal),
      totalCost: r0(totalCost),

    };
  }, [
    deckingSubtotal,
    railingSubtotal,
    stairsSubtotal,
    fastenerSubtotal,
    demoSubtotal,
    skirtingSubtotal,
    addItemsDetailed,
    permitPercent,
    smallJobPercent,
    perceivedPercent,
    financePercent,
    miPercent,
  ]);
// ===============================
// Trex Decking Price Levels (what-if totals)
// Only swaps the decking unit price; keeps the rest identical.
// ===============================
  const trexLevelsWhatIf = useMemo(() => {
 const LEVELS = [
  "Trex Enhance Basics",
  "Trex Enhance Naturals",
  "Trex Select",
  "Trex Transcend",
  "Trex Lineage",
  "Trex Signature",
] as const;


  const curName = (selectedDecking?.name || "").toString();
  const isTrex = curName.toLowerCase().includes("trex");
  if (!isTrex) return null;

  const fmt0 = (n: number) =>
    (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

  const curFinal = Number(finalEstimate) || 0;

  // If we don’t have enough info yet, return rows with blanks
  const safeBaseProjectTotal = Number(baseProjectTotal) || 0;
  const safeDeckingSubtotal = Number(deckingSubtotal) || 0;
const safeSkirtingSubtotal = Number(skirtingSubtotal) || 0;
const safeSkirtingSf = Number(skirtingSf ?? 0) || 0;
  const safeUpliftMultiplier = Number(upliftMultiplier) || 0;
  const safeFixed = Number(addItemsSubtotalFixed) || 0;
  const safeSqFt = Number(deckingSqFt) || 0;
  const safeConstructionAdj = Number(constructionAdj) || 0;

  // We need sq ft and multipliers to compute anything meaningful
  const canCompute =
    safeSqFt > 0 && safeUpliftMultiplier > 0 && safeBaseProjectTotal >= 0;

  // Helper: find the pricing item row for a given Trex level name
const findTrexRow = (levelName: string) => {
  const DECK_CATS = new Set(["decking", "composite_decking", "ipe"]);

  const currentCat = String((selectedDecking as any)?.category || "")
    .toLowerCase()
    .trim();

  const preferredCat = currentCat && DECK_CATS.has(currentCat) ? currentCat : null;

  const curName = (selectedDecking?.name || "").toString().toLowerCase();

  // Try to carry over “profile keywords” so we pick the comparable item
  // (ex: “grooved”, “square edge”, “scalloped”, “flat top”, etc.)
  const KEYWORDS = [
    "grooved",
    "square",
    "square edge",
    "scalloped",
    "flat top",
    "solid",
    "boards",
    "plank",
    "edge",
  ];

  const curKw = KEYWORDS.filter((k) => curName.includes(k));

  const needleExact = levelName.toLowerCase().trim();          // "trex enhance"
const needleLoose = needleExact.replace(/^trex\s+/, "");     // "enhance"
const mustContain = needleLoose;                             // force level keyword


  // Collect candidates (decking categories only)
  const candidates = pricingItems.filter((it) => {
    if (it.active === false) return false;
    if ((it.deleted_at ?? null) != null) return false;

    const cat = String((it as any).category || "").toLowerCase().trim();
    if (!DECK_CATS.has(cat)) return false;

    const nm = (it.name || "").toString().trim().toLowerCase();

   // Must be a Trex row AND must contain the specific level keyword ("basics", "enhance", etc.)
if (!nm.includes("trex")) return false;
if (!nm.includes(mustContain)) return false;

// Exact match or contains the full "trex <level>" phrase
if (nm === needleExact) return true;
if (nm.includes(needleExact)) return true;

// Otherwise allow it if it clearly matches the level keyword (already enforced above)
return true;

  });

  if (candidates.length === 0) return undefined;

  // Prefer same category as selected decking
  let pool = candidates;
  if (preferredCat) {
    const sameCat = candidates.filter(
      (it) =>
        String((it as any).category || "").toLowerCase().trim() === preferredCat
    );
    if (sameCat.length > 0) pool = sameCat;
  }

  // Score candidates: more keyword overlap wins, shorter name wins (more “base”)
  const score = (it: any) => {
    const nm = (it.name || "").toString().toLowerCase();
    const kwHits = curKw.reduce((s, k) => s + (nm.includes(k) ? 1 : 0), 0);
    const lengthPenalty = Math.min(nm.length / 100, 2); // small penalty for super long names
    return kwHits * 10 - lengthPenalty;
  };

  pool.sort((a, b) => score(b) - score(a));
  return pool[0];
};



  const rows = LEVELS.map((level) => {
    const row = findTrexRow(level);
    const altBaseDeckingUnit = Number((row as any)?.cost || 0);

    if (!canCompute || altBaseDeckingUnit <= 0) {
      return {
        level,
        total: null as number | null,
        diff: null as number | null,
        labelTotal: "—",
        labelDiff: "—",
      };
    }

    const altAdjustedDeckingUnit = altBaseDeckingUnit + safeConstructionAdj;
    const altDeckingSubtotal = altAdjustedDeckingUnit * safeSqFt;
    // Recompute skirting using THIS decking level (skirting is keyed off selectedDecking cost)
const simSkirtingCalc = computeEffectiveSkirtingRate({
  selectedDeckingUnit: altAdjustedDeckingUnit,
skirtingRow: selectedSkirting || undefined,
});

const simSkirtingRate = Number(simSkirtingCalc.effectiveRate) || 0;
const simSkirtingSubtotal = simSkirtingRate * safeSkirtingSf;

// Recompute stairs using THIS decking level (stairs are keyed off selectedDecking.name)
const altStairsCalc = computeEffectiveStairsRate({
  pricingItems,
  selectedDecking: row as any, // this Trex level row
  stairOptionRow: selectedStairOption || null,
});

const altStairsSubtotal =
  (Number(altStairsCalc.effectiveRate) || 0) * (Number(stairsCount ?? 0) || 0);
  // Recompute ONLY the deck-based Add Items using this decking level cost.
// (This makes the panel match what happens when you change the main decking dropdown.)
const curDeckSf = Number(selectedDecking?.cost || 0);
const altDeckSf = Number((row as any)?.cost || 0);

const altAddItemsSubtotalUpliftable = (addItemsDetailed as any[])
  .filter(
    (r) =>
      r &&
      r.picked &&
      !r.isFixedPrice &&
      !r.optional &&
      Number(r.lineBase || 0) !== 0
  )
  .reduce((sum, r) => {
    const qty = Number(r.qty || 0);
    if (qty <= 0) return sum;

    const picked = r.picked || {};
    const pickedUnit = String(picked.unit || r.unit || r.unitLabel || "")
      .toLowerCase()
      .trim();

    const nameLc = String(r.displayName || picked.name || "").toLowerCase();
    const catLc = String(r.category || "").toLowerCase();

    const isPicFrame =
      nameLc.includes("pic frame") ||
      nameLc.includes("picture frame") ||
      nameLc.includes("picframe");

    const isPlanter = nameLc.includes("planter");
    const isRamp = nameLc.includes("ramp");

    const referencesDecking =
      catLc.includes("deck") ||
      catLc === "decking_options" ||
      nameLc.includes("deck") ||
      nameLc.includes("stair landing") ||
      nameLc.includes("diagonal") ||
      nameLc.includes("picture frame") ||
      nameLc.includes("pic frame") ||
      nameLc.includes("planter") ||
      nameLc.includes("ramp") ||
      nameLc.includes("board over board");

    // if this add-item doesn't depend on decking, keep its existing lineBase
    if (!referencesDecking) return sum + (Number(r.lineBase) || 0);

    // If we don't have deck $/sf data, fall back safely to existing lineBase
    if (!(curDeckSf > 0 && altDeckSf > 0)) return sum + (Number(r.lineBase) || 0);

    // Mirror your exact add-item rules for deck-based items:
    const pickedCost = Number(picked.cost || 0);

    // Pic Frame Border: 0.75 × decking $/sf
    if (isPicFrame) {
      const unitCost = altDeckSf * 0.75;
      return sum + unitCost * qty;
    }

    // Planter: 0.90 × decking $/sf
    if (isPlanter) {
      const unitCost = altDeckSf * 0.9;
      return sum + unitCost * qty;
    }

    // Ramp: (picked multiplier) × decking $/sf
    if (isRamp) {
      const mult = pickedCost;
      const unitCost = altDeckSf * mult;
      return sum + unitCost * qty;
    }

    // multiplier: base × mult
    if (pickedUnit === "multiplier") {
      const unitCost = altDeckSf * pickedCost;
      return sum + unitCost * qty;
    }

    // addon_sf / addon_lf: base + add
    if (pickedUnit === "addon_sf" || pickedUnit === "addon_lf") {
      const unitCost = altDeckSf + pickedCost;
      return sum + unitCost * qty;
    }

    // Default deck-based fallback: scale linearly with decking cost
    // (keeps behavior stable for any other deck-tied lines)
    const scaled = (Number(r.lineBase) || 0) * (altDeckSf / curDeckSf);
    return sum + scaled;
  }, 0);

const altBaseTotal =
  safeBaseProjectTotal -
  safeDeckingSubtotal -
  safeSkirtingSubtotal -
  (Number(stairsSubtotal) || 0) -
  (Number(addItemsSubtotalUpliftable) || 0) +
  altDeckingSubtotal +
  simSkirtingSubtotal +
  altStairsSubtotal +
  altAddItemsSubtotalUpliftable;



    const altFinal = altBaseTotal * safeUpliftMultiplier + safeFixed;
    const diff = altFinal - curFinal;

    const diffLabel =
      diff === 0
        ? "$0"
        : diff > 0
        ? `+$${fmt0(diff)}`
        : `-$${fmt0(Math.abs(diff))}`;

    return {
      level,
      total: altFinal,
      diff,
      labelTotal: `$${fmt0(altFinal)}`,
      labelDiff: diffLabel,
    };
  });

  // Identify the “current level” row by name match
  const curLevel = LEVELS.find((lvl) =>
    curName.toLowerCase().includes(lvl.toLowerCase().replace("trex ", ""))
  );

  return { rows, curLevel };
}, [
  pricingItems,
  selectedDecking,
  finalEstimate,
  baseProjectTotal,
  deckingSubtotal,
  upliftMultiplier,
  addItemsSubtotalFixed,
    addItemsDetailed,
  addItemsSubtotalUpliftable,
  deckingSqFt,
  constructionAdj,
  selectedStairOption,
  stairsCount,
  stairsSubtotal,
    skirtingSubtotal,
  selectedSkirting,
  skirtingSf,

]);

  // ONE definition of "this tier is what the estimate is currently quoting",
  // shared by the panel's CURRENT marker and the proposal's "(as quoted)"
  // label. They used to be able to drift apart; now changing the decking
  // selection moves both, together, by construction.
  const isCurrentDeckingTier = (level: string) =>
    (selectedDecking?.name || "")
      .toLowerCase()
      .includes((level || "").toLowerCase());

  // The ticked tiers, resolved to their CURRENT totals and sorted cheapest
  // first — the shape the proposal renders. Recomputed from trexLevelsWhatIf
  // on every render, so a change to pricing, square footage or the decking
  // selection flows straight through to the printed figures.
  //
  // A tier whose total didn't compute (missing pricing row) is dropped rather
  // than printed as "—": this block is client-facing.
  const proposalDeckingTiers = useMemo(() => {
    const picked = new Set(proposalDeckingOptions);
    if (picked.size === 0) return [];

    return ((trexLevelsWhatIf?.rows || []) as any[])
      .filter(
        (r) =>
          picked.has(r.level) &&
          typeof r.total === "number" &&
          Number.isFinite(r.total)
      )
      .map((r) => ({
        level: String(r.level),
        total: Number(r.total),
        // The delta the Decking Tier Pricing panel itself computed
        // (altFinal - finalEstimate). Passed through rather than recomputed on
        // the proposal side, so the two surfaces cannot drift apart.
        diff: Number.isFinite(Number(r.diff)) ? Number(r.diff) : null,
        isCurrent: isCurrentDeckingTier(String(r.level)),
      }))
      .sort((a, b) => a.total - b.total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalDeckingOptions, trexLevelsWhatIf, selectedDecking]);

  const prettyCat = (cat: string) =>
    (cat || "").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

  const catItemLabel = (cat: string, item: string) => {
    const c = prettyCat(cat);
    const i = (item || "").trim();
    if (!c) return i || "";
    if (!i) return c;
    return `${c} — ${i}`;
  };

  // ===============================
  // RENDER
  // ===============================
  return (
    <div className="app-shell">
      {/* hidden open input */}
      <input
        ref={openFileInputRef}
        type="file"
        accept=".est,.json,.DUest,.duest"
        style={{ display: "none" }}
        onChange={onPickOpenFile}
      />

      {/* LEFT SIDEBAR */}
      <aside className="sidebar-left">
        <div className="sidebar-logo">
          <img
            className="sidebar-logo-mark"
            src={`${process.env.PUBLIC_URL}/icon-192.png`}
            alt="Estimator 2.0"
          />
          <div className="sidebar-logo-text">
            <div className="sidebar-logo-title">ESTIMATOR 2.0</div>
            {displayOrgName ? (
              <div className="sidebar-logo-subtitle">{displayOrgName}</div>
            ) : null}
          </div>
        </div>

        <div className="sidebar-file">
          <button
            type="button"
            className={`sidebar-nav-item sidebar-file-trigger ${
              fileOpen ? "is-open" : ""
            }`}
            onClick={() => setFileOpen((v) => !v)}
          >
            <span className="sidebar-nav-dot" />
            <span className="sidebar-file-label">File</span>

            <span className="sidebar-file-caret">{fileOpen ? "▾" : "▸"}</span>
          </button>

          {fileOpen && (
            <div className="sidebar-file-menu">
              <button
                type="button"
                className="sidebar-file-item"
                onClick={() => {
                  setFileOpen(false);
                  requestNewProject();
                }}
              >
                New
              </button>

              <button
                type="button"
                className="sidebar-file-item"
                onClick={() => {
                  handleFileOpen();
                  setTimeout(() => setFileOpen(false), 0);
                }}
              >
                Open…
              </button>

              <button
                type="button"
                className="sidebar-file-item"
                onClick={() => {
                  refreshRecents();
                  setRecentOpen((v) => !v);
                }}
              >
                Open Recent {recentOpen ? "▾" : "▸"}
              </button>

              {recentOpen && (
                <div
                  style={{ paddingLeft: 10, paddingTop: 6, paddingBottom: 6 }}
                >
                  {recentFiles.length === 0 ? (
                    <div
                      style={{
                        fontSize: 12,
                        opacity: 0.7,
                        padding: "6px 10px",
                      }}
                    >
                      No recent files yet.
                    </div>
                  ) : (
                    recentFiles.map((rf) => (
                      <button
                        key={rf.ts + rf.name}
                        type="button"
                        className="sidebar-file-item"
                        style={{ fontSize: 12, opacity: 0.95 }}
                        onClick={() => openRecent(rf)}
                        title={new Date(rf.ts).toLocaleString()}
                      >
                        {rf.name}
                      </button>
                    ))
                  )}

                  {recentFiles.length > 0 && (
                    <button
                      type="button"
                      className="sidebar-file-item"
                      style={{ fontSize: 12, opacity: 0.8 }}
                      onClick={() => {
                        clearRecents();
                        refreshRecents();
                        setRecentOpen(false);
                      }}
                    >
                      Clear Recent
                    </button>
                  )}
                </div>
              )}

              {/* SAVE */}
              <button
                type="button"
                className="sidebar-file-item"
                onClick={() => {
                  handleFileSave();
                  setFileOpen(false);
                }}
              >
                Save
              </button>

              {/* SAVE AS */}
              <button
                type="button"
                className="sidebar-file-item"
                onClick={() => {
                  handleFileSaveAs();
                  setFileOpen(false);
                }}
              >
                Save As…
              </button>

              {/* SAVE TO DROPBOX — desktop only. Hidden on web rather than
                  shown-and-disabled, since the web app has no way to reach a
                  local Dropbox folder and Save As… already covers it there. */}
              {!!(window as any).estimator?.isDesktop && (
                <button
                  type="button"
                  className="sidebar-file-item"
                  title="Save this estimate into your synced Dropbox folder."
                  onClick={() => {
                    handleSaveToDropbox();
                    setFileOpen(false);
                  }}
                >
                  Save to Dropbox
                </button>
              )}

              {/* DUPLICATE AS NEW ESTIMATE — fork identity, keep data */}
              <button
                type="button"
                className="sidebar-file-item"
                title="Creates a separate copy with its own link — use this to send a client a second version (e.g. different materials) without overwriting the original proposal."
                onClick={() => {
                  setFileOpen(false);
                  handleDuplicateAsNew();
                }}
              >
                Duplicate as new estimate
              </button>

              <button
                type="button"
                className="sidebar-file-item"
                onClick={() => {
                  setFileOpen(false);
                  onLogout();
                }}
              >
                Log out
              </button>
            </div>
          )}
        </div>

        <nav className="sidebar-nav">
          <SidebarNavItem
            label="Home"
            icon={NAV_ICONS.home}
            isActive={activeNav === "home"}
            onClick={() => setActiveNav("home")}
          />
          <SidebarNavItem
            label="Estimator"
            icon={NAV_ICONS.estimator}
            isActive={activeNav === "estimator"}
            onClick={() => setActiveNav("estimator")}
          />
          <SidebarNavItem
            label="Proposal"
            icon={NAV_ICONS.proposal}
            isActive={activeNav === "proposals"}
            onClick={goToProposal}
          />
          <SidebarNavItem
            label="Contract"
            icon={NAV_ICONS.contract}
            isActive={activeNav === "contract"}
            onClick={() => setActiveNav("contract")}
          />
          <SidebarNavItem
            label="Visual Library"
            icon={NAV_ICONS.visualLibrary}
            isActive={activeNav === "visualLibrary"}
            onClick={() => setActiveNav("visualLibrary")}
          />

          {canEditPricing && (
            <SidebarNavItem
              label="Pricing Admin"
              icon={NAV_ICONS.pricing}
              isActive={activeNav === "pricingAdmin"}
              onClick={() => setActiveNav("pricingAdmin")}
            />
          )}

          <SidebarNavItem
            label="Analytics"
            icon={NAV_ICONS.analytics}
            isActive={activeNav === "analytics"}
            onClick={() => setActiveNav("analytics")}
          />
          <SidebarNavItem
            label="Settings"
            icon={NAV_ICONS.settings}
            isActive={activeNav === "settings"}
            onClick={() => setActiveNav("settings")}
          />

          {canSeeUsersLicenses && (
            <SidebarNavItem
              label="Users / Licenses"
              icon={NAV_ICONS.users}
              isActive={activeNav === "users"}
              onClick={() => setActiveNav("users")}
            />
          )}
        </nav>

        {/* Offline indicator (sidebar) */}
        {(!isOnline ||
          (pricingError || "").toLowerCase().includes("offline mode")) && (
          <div className="sidebar-offline">
            <span className="sidebar-offline-dot" />
            Offline Mode
          </div>
        )}

        <div className="sidebar-footer">
          <div className="sidebar-footer-title">Estimator2.0</div>
          <div className="sidebar-footer-version">{`v${APP_VERSION} · ${BUILD_STAMP}`}</div>
          {/* Desktop-only: web build has no window.estimator, so this never
              renders on the web app. Outcomes are reported via native dialogs
              from the Electron main process. */}
          {!!(window as any).estimator?.isDesktop && (
            <button
              type="button"
              className="sidebar-footer-update"
              onClick={() => {
                void (window as any).estimator?.checkForUpdates?.();
              }}
              style={{
                marginTop: 4,
                background: "none",
                border: "none",
                padding: 0,
                font: "inherit",
                fontSize: 11,
                color: "var(--accent, #60a5fa)",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              Check for Updates
            </button>
          )}
          {/* Web-only counterpart to the button above. The desktop app updates
              through electron-updater and never runs a service worker at all
              (file:// origin), so this pill would be dead weight there. */}
          {swUpdateReady && !(window as any).estimator?.isDesktop && (
            <button
              type="button"
              className="sidebar-footer-update"
              title="A new version has been downloaded and is ready to load."
              onClick={() => {
                applySwUpdate();
              }}
              style={{
                marginTop: 6,
                background: "rgba(96,165,250,0.15)",
                border: "1px solid var(--accent, #60a5fa)",
                borderRadius: 999,
                padding: "3px 10px",
                font: "inherit",
                fontSize: 11,
                color: "var(--accent, #60a5fa)",
                cursor: "pointer",
              }}
            >
              Update available — Refresh
            </button>
          )}
        </div>
        {/* ROLE LABEL (bottom of sidebar) */}
        <div
          style={{
            marginTop: 16,
            padding: "12px 12px",
            fontSize: 12,
            opacity: 0.8,
            borderTop: "1px solid rgba(255,255,255,0.10)",
          }}
        >
          <div style={{ fontWeight: 800 }}>{isAdmin ? "Admin" : "User"}</div>
          <div style={{ fontSize: 11, opacity: 0.85 }}>{userEmail}</div>
        </div>
      </aside>

      <main
        className={
          "main-content " +
          (activeNav === "pricingAdmin" ? "main-content--full" : "")
        }
      >
        {/* APP TOPBAR (Tesla-style): breadcrumbs left, actions right */}
        <div className="app-topbar">
          <div className="app-topbar__left">
            <div className="app-topbar__title">
              {activeNav === "home" && "Home"}
              {activeNav === "estimator" && "Deck Estimate"}
              {activeNav === "proposals" && "Proposal"}
              {activeNav === "pricingAdmin" && "Pricing Administration"}
              {activeNav === "analytics" && "Analytics"}
              {activeNav === "settings" && "Settings"}
              {activeNav === "users" && "Users / Licenses"}
              {activeNav === "visualLibrary" && "Visual Library"}
              {activeNav === "contract" && "Contract"}
            </div>
            {activeNav === "estimator" && (
              <span className="customer-chip">
                <span className="chip-name">
                  {(clientLastName || "").trim() || "New estimate"}
                </span>
              </span>
            )}
          </div>
          <div className="top-actions">
            <span className="theme-toggle-wrap">
              <ThemeToggle />
              <span className="theme-toggle-label" aria-hidden="true">
                Theme
              </span>
            </span>
            {activeNav === "estimator" && (
              <>
                <button
                  type="button"
                  className="tesla-btn tesla-btn-icon"
                  onClick={() => handleFileSave()}
                  title="Save draft"
                  aria-label="Save draft"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                    <polyline points="17 21 17 13 7 13 7 21" />
                    <polyline points="7 3 7 8 15 8" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="tesla-btn tesla-btn-primary"
                  onClick={goToProposal}
                >
                  Generate proposal
                </button>
              </>
            )}
          </div>
        </div>

        {/* Frozen-prices indicator (Estimator/Proposal only). The page title
            now lives in the topbar; this thin bar renders only when relevant so
            there's no empty header/gap on other pages. */}
        {pricingFrozenFromFile &&
          (activeNav === "estimator" || activeNav === "proposals") && (
            <div className="page-badge-bar">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginLeft: "auto",
                }}
              >
                <div
                  className="frozen-badge"
                  title={
                    "This estimate keeps the prices it was saved with. " +
                    "They only change when you click Sync prices."
                  }
                >
                  <svg
                    aria-hidden
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="4" y="11" width="16" height="10" rx="2" />
                    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                  </svg>
                  <span>{pricesAsOfLabel(pricingSnapshotAt)}</span>
                </div>
                {frozenPriceDiffs.length > 0 && (
                  <button
                    type="button"
                    className="frozen-sync-btn"
                    onClick={() => setSyncModalOpen(true)}
                  >
                    Sync prices ({frozenPriceDiffs.length})
                  </button>
                )}
              </div>
            </div>
          )}

        {toast && <div className="du-toast">{toast}</div>}

        <LeadPickerModal
          open={leadPickerOpen}
          onClose={() => setLeadPickerOpen(false)}
          isOnline={isOnline}
          canUseZoho={canImportFromZoho}
          leads={leadListState}
          confirmReplace={(prompt) =>
            !clientFieldsHaveData() || window.confirm(prompt)
          }
          onFill={(contact, message) => {
            fillCustomerFromZoho(contact);
            showToast(message);
          }}
          onError={showToast}
        />

        {draftLoadState === "failed" && (
          <div className="banner banner-error draft-load-banner" role="status">
            Couldn't load your last saved estimate — autosave is paused. Reload
            to try again.
          </div>
        )}

        <div
          className={
            "main-grid " +
            (activeNav === "home" ||
            activeNav === "analytics" ||
            activeNav === "proposals" ||
            activeNav === "settings" ||
            activeNav === "visualLibrary"
              ? "main-grid--single "
              : "") +
            (activeNav === "pricingAdmin" ? "main-grid--pricing " : "")
          }
        >
          {activeNav === "analytics" && (
  <section className="analytics-page">
    <AnalyticsPage
      finalEstimate={finalEstimate}
      permitPercent={permitPercent}
      smallJobPercent={smallJobPercent}
      perceivedPercent={perceivedPercent}
      financePercent={financePercent}
      miPercent={miPercent}
      deckingSubtotal={deckingSubtotal}
      railingSubtotal={railingSubtotal}
      stairsSubtotal={stairsSubtotal}
      fastenerSubtotal={fastenerSubtotal}
      demoSubtotal={demoSubtotal}
      skirtingSubtotal={skirtingSubtotal}
      addItemsDetailed={addItemsDetailed}
    />
  </section>
)}

          {/* ====== USERS / LICENSES (ADMIN ONLY) ====== */}
          {activeNav === "users" &&
            (canSeeUsersLicenses ? (
              <section className="users-licenses-page">
                <UsersLicensesPage orgId={orgId} />
              </section>
            ) : (
              <section style={{ padding: 16 }}>
                <div style={{ fontWeight: 800, fontSize: 16 }}>
                  Not authorized
                </div>
                <div style={{ opacity: 0.75, marginTop: 6 }}>
                  You don’t have permission to view Users / Licenses.
                </div>
              </section>
            ))}

          {/* ====== HOME ====== */}
          {activeNav === "home" && (
            <HomePage
              userName={userSettings?.userName || ""}
              orgName={displayOrgName}
              recents={recentFiles}
              currentClientName={clientLastName}
              currentTown={clientTown}
              // Only offer "continue" when there is scope worth returning to.
              // A freshly-restored empty draft would otherwise render a
              // Continue card that leads to a blank estimate.
              hasWorkInProgress={hasScopeContent()}
              canEditPricing={canEditPricing}
              onResume={() => setActiveNav("estimator")}
              onNewEstimate={requestNewProject}
              onOpenFile={handleFileOpen}
              onOpenRecent={openRecent}
              onNavigate={(target) => {
                if (target === "proposals") {
                  goToProposal();
                  return;
                }
                setActiveNav(target);
              }}
            />
          )}

          {/* ====== ESTIMATOR ====== */}
          {activeNav === "estimator" && (
            <section className="estimator-pane">
              <div>
                {!pricingLoaded && !pricingError && (
                  <div className="banner banner-info">
                    Loading pricing from Supabase…
                  </div>
                )}

                {draftLoadState === "loading" && (
                  <div className="banner banner-info" role="status">
                    Loading your last estimate…
                  </div>
                )}

                {/* Locked while the startup draft load is in flight, so
                    nothing typed here can be overwritten when it lands. */}
                <fieldset
                  className="draft-lock-fieldset"
                  disabled={draftLoadState === "loading"}
                >
                {pricingLoaded && (
                  <>
                    {startupNotice && (
                      <div className="banner banner-info">
                        {startupNotice}
                      </div>
                    )}

                    {/* ===== UPLIFT / MSRP ROW ===== */}
                    <div className="msrp-pill-row">
                      {/* Left: MSRP toggle pill */}
                      <span
                        className={`pill pill--uplift msrp-pill ${
                          msrpMode ? "on" : "off"
                        }`}
                        onClick={() => setMsrpMode(!msrpMode)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setMsrpMode(!msrpMode);
                          }
                        }}
                        aria-pressed={msrpMode}
                        title={
                          msrpMode
                            ? "MSRP mode ON (uplifts disabled)"
                            : "MSRP mode OFF"
                        }
                      />
                     
                    


                      {/* Right: Uplift multiplier pill (1.17) + hover breakdown */}
{(() => {
  const money0 = (n: number) =>
    (n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

  const basePrice =
    (Number(deckingSubtotal) || 0) +
    (Number(railingSubtotal) || 0) +
    (Number(stairsSubtotal) || 0) +
    (Number(fastenerSubtotal) || 0) +
    (Number(demoSubtotal) || 0) +
    (Number(skirtingSubtotal) || 0) +
    (addItemsDetailed as any[])
      .filter((r) => r && r.picked && Number(r.lineBase || 0) !== 0)
      .reduce((sum, r) => sum + (Number(r.lineBase) || 0), 0);

  const permitAmt = (basePrice * (Number(permitPercent) || 0)) / 100;
  const smallJobAmt = (basePrice * (Number(smallJobPercent) || 0)) / 100;
  const msrp = basePrice + permitAmt + smallJobAmt;

  const perceivedAmt = (msrp * (Number(perceivedPercent) || 0)) / 100;
  const financeAmt = (msrp * (Number(financePercent) || 0)) / 100;
  const miAmt = (msrp * (Number(miPercent) || 0)) / 100;
  const upliftSubtotal = perceivedAmt + financeAmt + miAmt;
  const totalCost = msrp + perceivedAmt + financeAmt + miAmt;

  return (
    <div className="pill-wrapper uplift-hover">
      <span className="pill pill--uplift">{upliftMultiplier.toFixed(2)}</span>

      <div className="uplift-hover-box">
  <div style={{ fontWeight: 800, marginBottom: 8 }}>Uplift Breakdown</div>

  <div style={{ fontWeight: 800, marginBottom: 6 }}>
    Base Price: ${upliftBreakdown.money0(upliftBreakdown.basePrice)}
  </div>

  <div>
    Permit: {Math.round(permitPercent)}% (${upliftBreakdown.money0(upliftBreakdown.permitAmt)})
  </div>
  <div>
    Small Job: {Math.round(smallJobPercent)}% (${upliftBreakdown.money0(upliftBreakdown.smallJobAmt)})
  </div>

  <div style={{ marginTop: 10, fontWeight: 800 }}>
    MSRP: ${upliftBreakdown.money0(upliftBreakdown.msrp)}
  </div>

  <div style={{ marginTop: 8 }}>
    Perceived Value: {Math.round(perceivedPercent)}% (${upliftBreakdown.money0(upliftBreakdown.perceivedAmt)})
  </div>
  <div>
    Finance: {Math.round(financePercent)}% (${upliftBreakdown.money0(upliftBreakdown.financeAmt)})
  </div>
  <div>
    Manual Index: {Math.round(miPercent)}% (${upliftBreakdown.money0(upliftBreakdown.miAmt)})
 <div style={{ marginTop: 4, fontStyle: "italic", opacity: 0.85 }}>
  Sub Total Uplift: ${upliftBreakdown.money0(upliftBreakdown.upliftSubtotal)}
</div>
  </div>

  <div style={{ marginTop: 10, fontWeight: 900 }}>
    Total Cost: ${upliftBreakdown.money0(upliftBreakdown.totalCost)}
  </div>
</div>

    </div>
  );
})()}


                    </div>

                    {/* ===== Client Info (Tesla card wrapper) ===== */}
                    <div className="customer-card">
                      <div className="section-head">
                        <div className="section-head__titles">
                          <h3>Customer</h3>
                          <span className="section-meta section-meta--required">
                            Required
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          {canPickLead && (
                            <button
                              type="button"
                              className="du-btn du-btn-secondary"
                              onClick={() => setLeadPickerOpen(true)}
                              title="Your Zoho appointments for the next week — works without internet. Online: search Zoho or see everyone's appointments by day."
                            >
                              Pick a lead
                            </button>
                          )}
                        </div>
                      </div>
                    <section className="estimator-section estimator-section--no-bottom">
                      <div className="estimator-section-body">
                        <div
                          className="client-info-row"
                          style={{
                            gridTemplateColumns: "repeat(24, 1fr)",
                            rowGap: 12,
                            minHeight: "auto",
                          }}
                        >
                          <div className="client-field" style={{ gridColumn: "span 12" }}>
                            <label>First Name</label>
                            <input
                              type="text"
                              value={clientFirstName}
                              onChange={(e) => setClientFirstName(e.target.value)}
                              placeholder="First name"
                            />
                          </div>

                          <div className="client-field" style={{ gridColumn: "span 12" }}>
                            <label>Last Name</label>
                            <input
                              type="text"
                              value={clientLastName}
                              onChange={(e) =>
                                setClientLastName(e.target.value)
                              }
                              placeholder="Last name"
                            />
                          </div>

                          <div className="client-field" style={{ gridColumn: "span 12" }}>
                            <label>Street Address</label>
                            <input
                              type="text"
                              value={clientLocation}
                              onChange={(e) => setClientLocation(e.target.value)}
                              placeholder="Street address"
                            />
                          </div>

                          <div className="client-field" style={{ gridColumn: "span 6" }}>
                            <label>City</label>
                            <input
                              type="text"
                              value={clientTown}
                              onChange={(e) => setClientTown(e.target.value)}
                              placeholder="City"
                            />
                          </div>

                          <div className="client-field" style={{ gridColumn: "span 3" }}>
                            <label>State</label>
                            <input
                              type="text"
                              value={clientState}
                              onChange={(e) => setClientState(e.target.value)}
                              placeholder="State"
                            />
                          </div>

                          <div className="client-field" style={{ gridColumn: "span 3" }}>
                            <label>Zip</label>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={clientZip}
                              onChange={(e) => setClientZip(e.target.value)}
                              placeholder="Zip"
                            />
                          </div>

                          <div className="client-field" style={{ gridColumn: "span 7" }}>
                            <label>Phone</label>
                            <input
                              type="tel"
                              value={clientPhone}
                              onChange={(e) =>
                                setClientPhone(
                                  formatCustomerPhone(e.target.value, clientPhone)
                                )
                              }
                              placeholder="(516) 302-4510"
                            />
                          </div>

                          <div
                            className="client-field"
                            style={{ position: "relative", gridColumn: "span 17" }}
                          >
                            <label>Email</label>
                            <input
                              type="email"
                              value={clientEmail}
                              onChange={(e) => {
                                const v = e.target.value;
                                setClientEmail(v);
                                setEmailSugOpen(v.includes("@"));
                              }}
                              onFocus={() => {
                                if (clientEmail.includes("@"))
                                  setEmailSugOpen(true);
                              }}
                              onBlur={() => {
                                // let click register on a suggestion
                                window.setTimeout(
                                  () => setEmailSugOpen(false),
                                  120
                                );
                              }}
                              placeholder="Email"
                              autoComplete="off"
                            />

                            {emailSugOpen && emailSuggestions.length > 0 && (
                              <div className="email-suggest">
                                {emailSuggestions.map((s) => (
                                  <button
                                    key={s}
                                    type="button"
                                    className="email-suggest-item"
                                    onMouseDown={(e) => e.preventDefault()} // prevent blur before click
                                    onClick={() => {
                                      setClientEmail(s);
                                      setEmailSugOpen(false);
                                    }}
                                  >
                                    {s}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </section>
                    </div>{/* /.customer-card */}

                    {/* ===== Scope of Work (Tesla card wrapper) ===== */}
                    <div className="scope-card">
                      <div className="section-head">
                        <h3>Scope of Work</h3>
                        <div className="scope-head-right">
                          <div className="scope-history-controls">
                            <button
                              type="button"
                              className="tesla-btn tesla-btn--history"
                              onClick={handleUndo}
                              disabled={!canUndo}
                              title="Undo (Ctrl+Z)"
                              aria-label="Undo scope change"
                            >
                              ↶ Undo
                            </button>
                            <button
                              type="button"
                              className="tesla-btn tesla-btn--history"
                              onClick={handleRedo}
                              disabled={!canRedo}
                              title="Redo (Ctrl+Shift+Z)"
                              aria-label="Redo scope change"
                            >
                              ↷ Redo
                            </button>
                          </div>
                          <span className="section-meta">6 categories</span>
                        </div>
                      </div>

                    {/* ===== Decking + Construction Type ===== */}
                    <section className="estimator-section">
                      <header className="estimator-section-header">
                        <div className="decking-header-row">
                          <div className="form-field form-field--category">
                            <select
                              ref={constructionTypeRef}
                              className="form-select"
                              value={constructionType}
                              onChange={(e) =>
                                setConstructionType(e.target.value)
                              }
                            >
                              <option value="" disabled hidden>
                                Construction Type
                              </option>
                              {constructionOptions.map((opt) => (
                                <option key={opt.id} value={opt.name}>
                                  {opt.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="permit-control">
                            <span className="permit-title">Permit</span>
                            <button
                              type="button"
                              className={`permit-switch ${
                                includePermit ? "is-on" : "is-off"
                              }`}
                              onClick={() => setIncludePermit((v) => !v)}
                              aria-pressed={includePermit}
                            >
                              <span className="permit-switch-knob" />
                            </button>
                          </div>
                        </div>
                      </header>

                      <div className="estimator-section-body">
                        <div className="form-row form-row--4">
                          {/* Decking Type */}
                          <div className="form-field">
                            <select
                              className="form-select"
                              value={selectedDeckingId}
                              onChange={(e) =>
                                setSelectedDeckingId(e.target.value)
                              }
                            >
                              <option value="" disabled hidden>
                                Decking Type
                              </option>
                              {deckingOptions.map((opt) => (
                                <option key={opt.id} value={opt.id}>
                                  {opt.name}
                                </option>
                              ))}
                            </select>
                          </div>


                          {/* MI */}
                          <div className="form-field">
                            <ExpressionNumberInput
                              className="form-input no-spinner mi-input"
                              placeholder="MI"
                              value={miValue}
                              onValueChange={setMiValue}
                              ariaLabel="Manual uplift index"
                              displayDecimals={1}
                            />
                          </div>

                          {/* SF + tooltip */}
                          <div className="tooltip-wrapper">
                            <ExpressionNumberInput
                              className="form-input no-spinner"
                              placeholder="SF"
                              value={deckingSqFt}
                              onValueChange={setDeckingSqFt}
                            />

                            <div className="tooltip-box">
                              <div>
                                Base decking: {baseDeckingUnit.toFixed(2)} / sf
                              </div>
                              <div>
                                Construction ({constructionType || "None"}):{" "}
                                {constructionAdj >= 0 ? "+" : ""}
                                {constructionAdj.toFixed(2)} / sf
                              </div>
                              <div
                                style={{ marginTop: "4px", fontWeight: 600 }}
                              >
                                Adjusted rate: {adjustedDeckingUnit.toFixed(2)}{" "}
                                / sf
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* ===== RAILING ===== */}
                    <section className="estimator-section">
                      <div className="estimator-section-body">
                        <div className="form-row form-row--3">
                          <div className="form-field">
                            <select
                              className="form-select"
                              value={selectedRailingId}
                              onChange={(e) =>
                                setSelectedRailingId(e.target.value)
                              }
                            >
                              <option value="" disabled hidden>
                                Railing Type
                              </option>
                              {railingOptions.map((opt) => (
                                <option key={opt.id} value={String(opt.id)}>
                                  {opt.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="tooltip-wrapper">
                            <ExpressionNumberInput
                              className="form-input no-spinner"
                              placeholder="LF"
                              value={railingLf}
                              onValueChange={setRailingLf}
                            />
                            <div className="tooltip-box">
                              <div>
                                {selectedRailing
                                  ? `${selectedRailing.name}`
                                  : "Select railing"}{" "}
                                · ${(baseRailingUnit || 0).toFixed(2)} / lf
                              </div>
                              <div style={{ marginTop: 4, fontWeight: 600 }}>
                                Subtotal: $
                                {railingSubtotal.toLocaleString(undefined, {
                                  maximumFractionDigits: 0,
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* ===== STAIRS ===== */}
                    <section className="estimator-section">
                      <div className="estimator-section-body">
                        <div className="form-row form-row--3">
                          <div className="form-field">
                            <select
                              className="form-select"
                              value={selectedStairsId}
                              onChange={(e) =>
                                setSelectedStairsId(e.target.value)
                              }
                            >
                              <option value="" disabled hidden>
                                Stair Options
                              </option>
                              {mainStairsOptions.map((opt) => (
                                <option key={opt.id} value={String(opt.id)}>
                                  {opt.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="tooltip-wrapper">
                            <ExpressionNumberInput
                              className="form-input no-spinner"
                              placeholder="lf of treads"
                              value={stairsCount}
                              onValueChange={setStairsCount}
                            />
                            <div className="tooltip-box">
                              <div>
                                Base: ${baseStairsUnit.toFixed(2)} · Effective:
                                ${effectiveStairsRate.toFixed(2)}
                              </div>
                              <div style={{ marginTop: 4, fontWeight: 600 }}>
                                Subtotal: $
                                {stairsSubtotal.toLocaleString(undefined, {
                                  maximumFractionDigits: 0,
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* ===== FASTENERS ===== */}
                    <section className="estimator-section">
                      <div className="estimator-section-body">
                        <div className="form-row form-row--3">
                          <div className="form-field">
                            <select
                              className="form-select"
                              value={selectedFastenerId}
                              onChange={(e) => {
                                setSelectedFastenerId(e.target.value);
                                setFastenerTouched(true); // manual choice — don't auto-default over it
                              }}
                            >
                              <option value="" disabled hidden>
                                Fastener Type
                              </option>
                              {fastenerOptions.map((opt) => (
                                <option key={opt.id} value={String(opt.id)}>
                                  {opt.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="tooltip-wrapper">
                            <input
                              type="number"
                              className="form-input no-spinner"
                              placeholder="Auto Qty"
                              value={
                                fastenerQtyAuto === 0 ? "" : fastenerQtyAuto
                              }
                              readOnly
                            />
                            <div className="tooltip-box">
                              <div>
                                Auto qty = Decking SF ({deckingSqFt || 0})
                              </div>
                              <div>
                                Rate: ${baseFastenerUnit.toFixed(2)} / ea
                              </div>
                              <div style={{ marginTop: 4, fontWeight: 600 }}>
                                Subtotal: $
                                {fastenerSubtotal.toLocaleString(undefined, {
                                  maximumFractionDigits: 0,
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* ===== DEMOLITION ===== */}
                    <section className="estimator-section">
                      <div className="estimator-section-body">
                        <div className="form-row form-row--3">
                          <div className="form-field">
                            <select
                              className="form-select"
                              value={selectedDemoId}
                              onChange={(e) =>
                                setSelectedDemoId(e.target.value)
                              }
                            >
                              <option value="" disabled hidden>
                                Demo Type
                              </option>
                              {demoOptions.map((opt) => (
                                <option key={opt.id} value={String(opt.id)}>
                                  {opt.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="tooltip-wrapper">
                            <ExpressionNumberInput
                              className="form-input no-spinner"
                              placeholder="Qty"
                              value={demoQty}
                              onValueChange={setDemoQty}
                            />
                            <div className="tooltip-box">
                              <div>
                                Rate: ${baseDemoUnit.toFixed(2)} · Unit:{" "}
                                {selectedDemo?.unit || "ea"}
                              </div>
                              <div style={{ marginTop: 4, fontWeight: 600 }}>
                                Subtotal: $
                                {demoSubtotal.toLocaleString(undefined, {
                                  maximumFractionDigits: 0,
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* ===== SKIRTING / LATTICE ===== */}
                    <section className="estimator-section">
                      <div className="estimator-section-body">
                        <div className="form-row form-row--3">
                          <div className="form-field">
                            <select
                              ref={skirtingCategoryRef}
                              className="form-select"
                              value={skirtingCategory}
                              onChange={(e) => {
                                const next = e.target.value as
                                  | ""
                                  | "Skirting"
                                  | "Lattice";
                                setSkirtingCategory(next);

                                // reset skirting type when switching category
                                setSelectedSkirtingId("");
                                setSkirtingTypeTouched(false);
                              }}
                            >
                              <option value="" disabled hidden>
                                Skirting / Lattice
                              </option>
                              <option value="Skirting">Skirting</option>
                              <option value="Lattice">Lattice</option>
                            </select>
                          </div>

                          <div className="form-field">
                            <select
                              className="form-select"
                              value={selectedSkirtingId}
                              onChange={(e) => {
                                setSkirtingTypeTouched(true);
                                setSelectedSkirtingId(e.target.value);
                              }}
                              disabled={!skirtingCategory}
                            >
                              <option value="" disabled hidden>
                                Type
                              </option>
                              {skirtingLatticeOptions.map((opt) => (
                                <option key={opt.id} value={String(opt.id)}>
                                  {opt.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="tooltip-wrapper">
                            <ExpressionNumberInput
                              className="form-input no-spinner"
                              placeholder="SF"
                              value={skirtingSf}
                              onValueChange={setSkirtingSf}
                            />
                            <div className="tooltip-box">
                              <div>
Rate: ${(effectiveSkirtingRate || 0).toFixed(2)} / sf
                              </div>
                              <div style={{ marginTop: 4, fontWeight: 600 }}>
                                Subtotal: $
                                {skirtingSubtotal.toLocaleString(undefined, {
                                  maximumFractionDigits: 0,
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>
                    </div>{/* /.scope-card */}

                    {/* ===== ADD ITEMS ===== */}
                    <div className="estimator-section-body">
                      <div className="add-items-header-row">
                        <button
                          type="button"
                          className="tesla-btn"
                          onClick={addAddItemRow}
                        >
                          + Add Item
                        </button>
                        <div className="add-items-order-hint">
                          Use ↑ ↓ to set proposal order
                          {process.env.NODE_ENV !== "production" &&
                          addItemsPanelWidth !== null ? (
                            <span className="add-items-width-probe">
                              panel {addItemsPanelWidth}px ·{" "}
                              {addItemsPanelWidth <= ADD_ITEM_STACK_BELOW
                                ? "stacked"
                                : "one line"}
                            </span>
                          ) : null}
                        </div>
                      </div>

                      <div className="add-items-rows" ref={addItemsRowsRef}>
                        {/* Column header. One hairline row on the same seven
                            tracks as the item rows (both read --item-cols from
                            this container), so the headings sit exactly over
                            the controls they name. Hidden entirely in the
                            narrow layout, where each field carries its own
                            caption instead. */}
                        {addItemsDetailed.length > 0 && (
                          <div className="add-items-colhead" aria-hidden="true">
                            <span />
                            <span>Category</span>
                            <span>Detail</span>
                            <span className="is-num">Qty</span>
                            <span className="is-num">Price</span>
                            <span className="is-center">Optional</span>
                            <span className="is-num">Order</span>
                          </div>
                        )}

                        {addItemsDetailed.length === 0 ? (
                          <div className="section-placeholder">
                            Click <strong>+ Add Item</strong> to add benches,
                            lighting, columns, etc.
                          </div>
                        ) : (
                          addItemsDetailed.map((row: any, index: number) => {
                            const options = addItemOptionsForRow(row);

                            // A SAVED row may carry a category that is no
                            // longer offered — "Construction Options" is
                            // main-section-only and is filtered out of the
                            // Add Item list. Re-add it for THIS row only, so
                            // the legacy row still shows (and prices as) what
                            // was saved instead of falling back to a blank
                            // select. It never appears on any other row, and it
                            // disappears from this one the moment the category
                            // is changed, so it can't be newly selected.
                            const rowCat = String(row.category || "").trim();
                            const showLegacyCatOption =
                              !!rowCat &&
                              !isConstructionTypeCategory(rowCat) &&
                              !addItemCategories.some(
                                (c) => normalizeCat(c) === normalizeCat(rowCat)
                              );

                            const isFirstRow = index === 0;
                            const isLastRow = index === addItemsDetailed.length - 1;
                            const renderAddItemControls = () => (
                              <div className="additem-cell additem-cell--order additem-controls">
                                <button
                                  type="button"
                                  className="additem-order"
                                  onClick={() => moveAddItemRow(row.rowId, -1)}
                                  aria-label="Move item up"
                                  title="Move up"
                                  disabled={isFirstRow}
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  className="additem-order"
                                  onClick={() => moveAddItemRow(row.rowId, 1)}
                                  aria-label="Move item down"
                                  title="Move down"
                                  disabled={isLastRow}
                                >
                                  ↓
                                </button>
                              </div>
                            );

                            // The first of the seven columns. It used to be
                            // absolutely positioned in a left gutter; now it is
                            // an ordinary grid cell like every other control,
                            // which is what keeps it on the same x as the
                            // remove button of every other row.
                            const renderAddItemRemove = () => (
                              <button
                                type="button"
                                className="additem-cell additem-cell--remove additem-remove"
                                onClick={() => removeAddItemRow(row.rowId)}
                                aria-label="Remove item"
                                title="Remove"
                              >
                                ✕
                              </button>
                            );

                            const catNorm = normalizeCat(row.category || "");
                            const isMiscRow = catNorm === "misc";
                            const isConstructionRow = isConstructionTypeCategory(
                              row.category || ""
                            );
                            const isBenchRow =
                              !isConstructionRow && !isMiscRow && catNorm === "bench";

                            // PRICE column on catalog rows. lineBase is already
                            // computed for this row by computeEstimate — read,
                            // never recalculated here. Nothing priced yet → the
                            // muted em-dash the spec asks for.
                            const lineBaseNum = Number(row.lineBase) || 0;
                            // Bare comma-grouped number — Decks Unique documents
                            // and UI carry no currency symbol.
                            const catalogPriceText =
                              lineBaseNum > 0
                                ? lineBaseNum.toLocaleString(undefined, {
                                    maximumFractionDigits: 0,
                                  })
                                : "—";

                            // Misc description: present → always shown; absent →
                            // the "+ Add proposal description" button until the
                            // user opens it for this row.
                            const hasDesc = !!String(
                              row.customDescription || ""
                            ).trim();
                            const descOpen =
                              hasDesc || miscDescOpenRows.includes(row.rowId);

                            const renderDetailCell = () => {
                              if (isConstructionRow) {
                                return (
                                  <select
                                    className="form-select"
                                    aria-label="Decking type"
                                    value={row.deckingId || ""}
                                    onChange={(e) =>
                                      updateAddItemRow(row.rowId, {
                                        deckingId: e.target.value,
                                      })
                                    }
                                  >
                                    {/* Placeholder is NOT `hidden`: a hidden
                                        option is out of layout, so an unpicked
                                        row would have no content to size to. */}
                                    <option value="" disabled>
                                      Decking Type
                                    </option>
                                    {deckingOptions.map((opt) => (
                                      <option key={opt.id} value={String(opt.id)}>
                                        {opt.name}
                                      </option>
                                    ))}
                                  </select>
                                );
                              }

                              if (isMiscRow) {
                                return (
                                  <input
                                    type="text"
                                    className="form-input"
                                    aria-label="Title for proposal page"
                                    placeholder="Title for proposal page"
                                    value={row.customName || ""}
                                    onChange={(e) =>
                                      updateAddItemRow(row.rowId, {
                                        customName: e.target.value,
                                        qty: 1,
                                      })
                                    }
                                  />
                                );
                              }

                              if (isBenchRow) {
                                return (
                                  <select
                                    className="form-select"
                                    aria-label="Bench type"
                                    value={row.benchType || "12_flat"}
                                    onChange={(e) => {
                                      const next = e.target.value;
                                      const benchLabel =
                                        BENCH_TYPES.find((x) => x.value === next)?.label || "";
                                      const deckName = (selectedDecking?.name || "").toString().trim();
                                      const deckNameLc = deckName.toLowerCase();

                                      const benchId = pricingItems.find((pItem) => {
                                        if (normalizeCat(pItem.category || "") !== "bench") return false;
                                        const name = String(pItem.name || "").trim();
                                        const nameLc = name.toLowerCase();

                                        // prefer bench row that includes selected decking name
                                        if (deckNameLc && !nameLc.includes(deckNameLc)) return false;

                                        return name === benchLabel;
                                      })?.id;

                                      updateAddItemRow(row.rowId, {
                                        benchType: next,
                                        itemId: benchId ? String(benchId) : "", // map to Pricing Admin bench row
                                      });
                                    }}
                                    disabled={!row.category}
                                  >
                                    <option value="" disabled hidden>
                                      Bench Type
                                    </option>
                                    {BENCH_TYPES.map((bt) => (
                                      <option key={bt.value} value={bt.value}>
                                        {bt.label}
                                      </option>
                                    ))}
                                  </select>
                                );
                              }

                              return (
                                <select
                                  className="form-select"
                                  aria-label="Item type"
                                  value={row.itemId || ""}
                                  onChange={(e) =>
                                    updateAddItemRow(row.rowId, {
                                      itemId: e.target.value,
                                      customName: "",
                                      customPrice: 0,
                                      constructionType: "",
                                      deckingId: "",
                                    })
                                  }
                                  disabled={!row.category}
                                >
                                  {/* NOT `hidden`: with no category picked the
                                      option list is empty, and a hidden
                                      placeholder left the select nothing to
                                      size to. */}
                                  <option value="" disabled>
                                    Type…
                                  </option>
                                  {options.map((opt) => (
                                    <option key={opt.id} value={String(opt.id)}>
                                      {opt.name}
                                    </option>
                                  ))}
                                </select>
                              );
                            };

                            return (
                              <div
                                key={row.rowId}
                                className={`add-item-row add-item-row--grid ${
                                  isMiscRow ? "add-item-row--misc" : ""
                                } ${row.optional ? "is-optional" : ""}`}
                              >
                                {/* 1 — REMOVE */}
                                {renderAddItemRemove()}

                                {/* 2 — CATEGORY */}
                                <div className="additem-cell additem-cell--category additem-category-field">
                                  <select
                                    className="form-select"
                                    aria-label="Item category"
                                    value={row.category || ""}
                                    onChange={(e) => {
                                      const nextCat = e.target.value;
                                      updateAddItemRow(row.rowId, {
                                        category: nextCat,
                                        itemId: "",
                                        qty: 0,
                                        customName: "",
                                        customPrice: 0,
                                        constructionType: "",
                                        deckingId: "",
                                        benchType:
                                          normalizeCat(nextCat) === "bench"
                                            ? "12_flat"
                                            : "",
                                      });
                                    }}
                                  >
                                    <option value="" disabled hidden>
                                      Category
                                    </option>

                                    {/* Normal Add Item categories */}
                                    {addItemCategories.map((cat) => (
                                      <option key={cat} value={cat}>
                                        {cat.replace(/_/g, " ")}
                                      </option>
                                    ))}

                                    {/* Legacy-only: this row's saved category */}
                                    {showLegacyCatOption && (
                                      <option value={rowCat}>
                                        {rowCat.replace(/_/g, " ")}
                                      </option>
                                    )}

                                    {/* Divider */}
                                    <option disabled value="__divider__">
                                      ─────────────
                                    </option>

                                    {/* Construction Types at bottom */}
                                    {CONSTRUCTION_TYPES.filter(
                                      (t) => !!t.value
                                    ).map((t) => (
                                      <option key={t.value} value={t.value}>
                                        {t.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                {/* 3 — DETAIL */}
                                <div className="additem-cell additem-cell--detail">
                                  {renderDetailCell()}
                                </div>

                                {/* 4 — QTY. Keeps .additem-qty-wrap: the hover
                                    tooltip is its ::after, driven by
                                    data-tooltip. */}
                                <div
                                  className="additem-cell additem-cell--qty additem-qty-wrap"
                                  data-tooltip={
                                    isMiscRow
                                      ? ""
                                      : row.tooltip || "Enter quantity"
                                  }
                                >
                                  <span className="additem-caption" aria-hidden="true">
                                    Qty
                                  </span>
                                  {isMiscRow ? (
                                    <input
                                      type="text"
                                      className="form-input"
                                      aria-label="Quantity with units"
                                      placeholder="Qty"
                                      title="Quantity with units — e.g., 400 sf"
                                      value={(row as any).customQtyText || ""}
                                      onChange={(e) =>
                                        updateAddItemRow(
                                          row.rowId,
                                          {
                                            customQtyText: e.target.value,
                                            qty: 1,
                                          } as any
                                        )
                                      }
                                    />
                                  ) : (
                                    <ExpressionNumberInput
                                      className="form-input no-spinner"
                                      ariaLabel="Quantity"
                                      placeholder={isConstructionRow ? "SF" : "Qty"}
                                      value={row.qty || 0}
                                      onValueChange={(val) =>
                                        updateAddItemRow(row.rowId, { qty: val })
                                      }
                                    />
                                  )}
                                </div>

                                {/* 5 — PRICE */}
                                <div className="additem-cell additem-cell--price">
                                  <span className="additem-caption" aria-hidden="true">
                                    Price
                                  </span>
                                  {isMiscRow ? (
                                    <div className="additem-money">
                                      <ExpressionNumberInput
                                        className="form-input no-spinner additem-qty-input"
                                        ariaLabel="Price"
                                        placeholder="Price"
                                        value={row.customPrice || 0}
                                        onValueChange={(val) =>
                                          updateAddItemRow(row.rowId, {
                                            customPrice: val,
                                            qty: 1,
                                          })
                                        }
                                      />
                                    </div>
                                  ) : (
                                    <span className="additem-price-readonly">
                                      {catalogPriceText}
                                    </span>
                                  )}
                                </div>

                                {/* 6 — OPTIONAL. Same switch as the Permit
                                    toggle in the scope section, one size down.
                                    Absolutely positioned knob + fixed track, so
                                    flipping it cannot change the row height. */}
                                <div className="additem-cell additem-cell--optional">
                                  <span className="additem-caption" aria-hidden="true">
                                    Optional
                                  </span>
                                  <button
                                    type="button"
                                    className={`permit-switch permit-switch--sm ${
                                      row.optional ? "is-on" : "is-off"
                                    }`}
                                    onClick={() =>
                                      updateAddItemRow(row.rowId, {
                                        optional: !row.optional,
                                      } as any)
                                    }
                                    aria-pressed={!!row.optional}
                                    aria-label="Optional — price out but exclude from the estimate total"
                                    title="Price out but EXCLUDE from main estimate total. Shows under Optional Add-Ons on the proposal."
                                  >
                                    <span className="permit-switch-knob" />
                                  </button>
                                </div>

                                {/* 7 — ORDER */}
                                {renderAddItemControls()}

                                {/* Misc only: the proposal description, on a
                                    second grid line under the title field. It
                                    spans DETAIL→PRICE so it starts on the same
                                    x as the title it belongs to. */}
                                {isMiscRow ? (
                                  <div className="additem-descrow">
                                    {descOpen ? (
                                      <textarea
                                        className="form-input additem-desc-input"
                                        aria-label="Proposal description"
                                        placeholder="Proposal description (optional)"
                                        rows={2}
                                        value={row.customDescription || ""}
                                        onChange={(e) =>
                                          updateAddItemRow(row.rowId, {
                                            customDescription: e.target.value,
                                            qty: 1,
                                          })
                                        }
                                      />
                                    ) : (
                                      <button
                                        type="button"
                                        className="additem-desc-add"
                                        onClick={() =>
                                          setMiscDescOpenRows((prev) =>
                                            prev.includes(row.rowId)
                                              ? prev
                                              : [...prev, row.rowId]
                                          )
                                        }
                                      >
                                        + Add proposal description
                                      </button>
                                    )}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </>
                )}
                </fieldset>
              </div>
            </section>
          )}
          {activeNav === "pricingAdmin" &&
            (canEditPricing ? (
              <PricingAdmin readOnly={false} onSaved={onPricingSaved} />
            ) : (
              <section style={{ padding: 16 }}>
                <div style={{ fontWeight: 800, fontSize: 16 }}>
                  Not authorized
                </div>
                <div style={{ opacity: 0.75, marginTop: 6 }}>
                  You don’t have permission to view Pricing Admin.
                </div>
              </section>
            ))}

          {activeNav === "settings" && (
            <SettingsPage
              userSettings={userSettings}
              setUserSettings={setUserSettings}
              orgId={orgId}
              isAdmin={isAdmin}
              onFieldCleared={onSettingsFieldCleared}
              onOrgNameSynced={setOrgName}
            />
          )}

          {activeNav === "visualLibrary" && (
            <VisualLibraryPage
              productOptionsByCategory={{
                Decking: deckingOptions.map((item) => ({ id: String(item.id), value: item.name || "", label: item.name || "" })),
                Railing: railingOptions.map((item) => ({ id: String(item.id), value: item.name || "", label: item.name || "" })),
                // Gate: one card per railing product (same list as Railing), but
                // the id is namespaced "gate:<railingId>" so a gate photo NEVER
                // collides with the Railing photo for the same railing (photo
                // identity keys on coalesce(product_id, product_key), no category).
                Gate: railingOptions.map((item) => ({ id: `gate:${String(item.id)}`, value: item.name || "", label: item.name || "" })),
                // Fasteners key on their stable pricing_items2 id (one card per
                // option, rename-safe), the same as Decking/Railing/Lattice.
                Fasteners: (fastenerOptions || []).map((item) => ({ id: String(item.id), value: item.name || "", label: item.name || "" })),
                // Skirting/Lattice collapse many SKUs to a style and key on the
                // canonical style (product_key), not a single id — so id is null.
                Skirting: Array.from(
                  new Map(
                    (skirtingOnlyOptions || [])
                      .map((item) => {
                        const raw = (item.name || "").trim();
                        const style = raw.split(" - ")[0]; // collapse to style regardless of material
                        return [style.toLowerCase(), { id: null, value: style, label: style }];
                      })
                  ).values()
                ),
                // Lattice keys on its stable pricing_items2 id (one card per
                // option, rename-safe), the same as Decking/Railing.
                Lattice: (latticeOnlyOptions || []).map((item) => ({ id: String(item.id), value: item.name || "", label: item.name || "" })),
              }}
            />
          )}

         <div style={{ display: activeNav === "proposals" ? "block" : "none" }}>
            <ProposalPage
              orgId={orgId}
              proposalId={proposalId || undefined}
              constructionType={constructionType}
              userSettings={userSettings}
              estimateName={estimateName}
              estimateId={estimateId}
              onProposalNotesChange={(notes) => {
                proposalNotesRef.current = notes;
              }}
              finalEstimate={finalEstimate}
              includePermit={includePermit}
              clientFirstName={clientFirstName}
              clientLastName={clientLastName}
              clientTown={clientTown}
              clientState={clientState}
              clientEmail={clientEmail}
              deckingSubtotal={deckingSubtotal}
              railingSubtotal={railingSubtotal}
              stairsSubtotal={stairsSubtotal}
              fastenerSubtotal={fastenerSubtotal}
              demoSubtotal={demoSubtotal}
              skirtingSubtotal={skirtingSubtotal}
              deckingType={selectedDecking?.name || ""}
              railingType={selectedRailing?.name || ""}
              stairsType={selectedStairOption?.name || ""}
              fastenerType={selectedFastener?.name || ""}
              demoType={selectedDemo?.name || ""}
              skirtingType={selectedSkirting?.name || ""}
              skirtingCategory={skirtingCategory || ""}
              visuals={proposalVisuals}
              deckingDescription={selectedDecking?.proposal_description || ""}
              railingDescription={selectedRailing?.proposal_description || ""}
              stairsDescription={
                selectedStairOption?.proposal_description || ""
              }
              fastenerDescription={selectedFastener?.proposal_description || ""}
              demoDescription={selectedDemo?.proposal_description || ""}
              skirtingDescription={selectedSkirting?.proposal_description || ""}
              deckingQty={deckingSqFt}
              deckingUnit="sf"
              railingQty={railingLf}
              railingUnit="lf"
              stairsQty={stairsCount ?? 0}
              stairsUnit="lf"
              fastenerQty={fastenerQtyAuto}
              fastenerUnit="ea"
              demoQty={demoQty}
              demoUnit="ea"
              skirtingQty={skirtingSf}
              skirtingUnit="sf"
              addItemsDetailed={addItemsDetailed as any}
              deckingTierOptions={proposalDeckingTiers}
              quotedDeckingTier={
                (trexLevelsWhatIf?.rows || [])
                  .map((r: any) => String(r.level))
                  .find((lvl: string) => isCurrentDeckingTier(lvl)) ||
                selectedDecking?.name ||
                ""
              }
              upliftMultiplier={upliftMultiplier}
              onEmailProposal={handleEmailProposal}
              sowText={sowText}
              onSowTextChange={(next: string) => {
                setSowText(next);
                setSowTouched(true);
              }}
              sowTouched={sowTouched}
              projectDescription={projectDescription}
              onProjectDescriptionChange={(next: string) => {
                setProjectDescription(next);
                setProjectDescriptionTouched(true);
              }}
              projectDescriptionTouched={projectDescriptionTouched}
              // Same startup lock as the Estimator form: the in-place editors
              // are view-only until the cloud draft has been applied.
              editingLocked={draftLoadState === "loading"}
              // Shared proposal sections: editable in place by org admins only
              // (org_members.role — the rule RLS policy ops_write_admins
              // enforces), saved through the same writes as Settings.
              canEditSections={isAdmin}
              onLayoutOrderChange={(order) =>
                setUserSettings((prev: any) => ({
                  ...prev,
                  proposalLayoutOrder: order,
                }))
              }
              onSectionEditsPendingChange={setSectionEditsPending}
              // Org-level wording (labels, headings, Next steps, validity
              // days): admin only, same settings save as the section order.
              // An all-default object is a deliberate clear, so the save's
              // blank-guard must not restore the old wording.
              onProposalTextChange={(next: ProposalText) => {
                proposalTextRevRef.current += 1;
                setProposalTextSaveState("saving");
                onSettingsFieldCleared(
                  "proposalText",
                  isProposalTextEmpty(next)
                );
                setUserSettings((prev: any) => ({
                  ...prev,
                  proposalText: next,
                }));
              }}
              proposalTextSaveState={proposalTextSaveState}
            />
          </div>
  
{activeNav === "contract" && (
  <ContractPage
    orgId={orgId}
    estimateId={estimateId}
    // LIVE settings: the signature, signer name and company details print
    // from what Settings holds now, not a cache copy taken at page open.
    userSettings={userSettings}
    contract={contract}
    onContractChange={handleContractChange}

    finalEstimate={finalEstimate}
    selectedDecking={selectedDecking}
    selectedRailing={selectedRailing}
    selectedStairOption={selectedStairOption}
    selectedSkirting={selectedSkirting}
    selectedFastener={selectedFastener}
    selectedConstruction={selectedConstruction}
    constructionType={constructionType}
    addItemsDetailed={addItemsDetailed as any}
    clientFirstName={clientFirstName}
    clientLastName={clientLastName}
    clientStreet={clientLocation}
    clientCity={clientTown}
    clientState={clientState}
    clientZip={clientZip}
    clientPhone={clientPhone}
    clientEmail={clientEmail}
   demoType={
  demoOptions.find((d: any) => String(d.id) === String(selectedDemoId))?.name ??
  ""
}
    demoDescription={
  demoOptions.find((d: any) => String(d.id) === String(selectedDemoId))
    ?.proposal_description ?? ""
}
  />
)}

          {/* RIGHT: ESTIMATE SUMMARY */}
          {activeNav === "estimator" && (
            <aside className="estimate-summary tesla-estimate-card">
              <div className="estimate-panel">
                {(() => {
                  const money0 = (n: number) =>
                    (n || 0).toLocaleString(undefined, {
                      maximumFractionDigits: 0,
                    });
                  const deckingRow = pricingItems.find(
                    (p) => String(p.id) === String(selectedDeckingId)
                  );

                  const railingRow = pricingItems.find(
                    (p) => String(p.id) === String(selectedRailingId)
                  );

                  const stairsRow = pricingItems.find(
                    (p) => String(p.id) === String(selectedStairsId)
                  );

                  const fastenerRow = pricingItems.find(
                    (p) => String(p.id) === String(selectedFastenerId)
                  );

                  const demoRow = pricingItems.find(
                    (p) => String(p.id) === String(selectedDemoId)
                  );

                  const skirtingRow = pricingItems.find(
                    (p) => String(p.id) === String(selectedSkirtingId)
                  );
                  const line = (label: string, amount: number) => {
                    const neg = amount < 0;
                    const abs = Math.abs(amount);

                    return (
                      <div className="estimate-panel__row" key={label}>
                        <span>{label}</span>
                        <span>
                          {neg ? `–$${money0(abs)}` : `$${money0(abs)}`}
                        </span>
                      </div>
                    );
                  };

                  return (
                    <>
                      <div className="estimate-panel__header">
                        <div className="estimate-panel__title">ESTIMATE</div>
                        <div className="estimate-panel__total">
                          {money0(finalEstimate)}
                        </div>
                      </div>

                      <div
                        className="estimate-panel__disclosure"
                        role="button"
                        tabIndex={0}
                        onClick={() => setShowBreakdown((v) => !v)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setShowBreakdown((v) => !v);
                          }
                        }}
                        aria-expanded={showBreakdown}
                      >
                        <span className="estimate-panel__disclosure-label">
                          {showBreakdown ? "less" : "...more"}
                        </span>
                        <span
                          className={`estimate-panel__chev ${
                            showBreakdown ? "is-open" : ""
                          }`}
                          aria-hidden="true"
                        >
                          ▾
                        </span>
                      </div>

                      {showBreakdown && (
                        <div className="estimate-panel__rows">
                          {deckingSubtotal > 0 &&
                            line(
                              catItemLabel("Decking", deckingRow?.name || ""),
                              deckingSubtotal * upliftMultiplier
                            )}
                          {railingSubtotal > 0 &&
                            line(
                              catItemLabel("Railing", railingRow?.name || ""),
                              railingSubtotal * upliftMultiplier
                            )}
                          {stairsSubtotal > 0 &&
                            line(
                              catItemLabel("Stairs", stairsRow?.name || ""),
                              stairsSubtotal * upliftMultiplier
                            )}
                          {fastenerSubtotal > 0 &&
                            line(
                              catItemLabel(
                                "Fasteners",
                                fastenerRow?.name || ""
                              ),
                              fastenerSubtotal * upliftMultiplier
                            )}

                          {demoSubtotal > 0 &&
                            line(
                              catItemLabel("Demolition", demoRow?.name || ""),
                              demoSubtotal * upliftMultiplier
                            )}

                          {skirtingSubtotal > 0 &&
                            line(
                              catItemLabel(
                                skirtingCategory || "Skirting / Lattice",
                                skirtingRow?.name || ""
                              ),
                              skirtingSubtotal * upliftMultiplier
                            )}
                          {(addItemsDetailed as any[])
                            .filter(
                              (r) =>
                                r.picked &&
                                Number(r.lineBase || 0) !== 0 &&
                                !r.optional
                            )

                            .map((r) =>
                              line(
                                catItemLabel(
                                  r.category || "Add Item",
                                  r.picked?.name || ""
                                ),
                                r.isFixedPrice
                                  ? r.lineBase
                                  : r.lineBase * upliftMultiplier
                              )
                            )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              {/* Decking Price Levels (Trex only) */}
{(() => {
 const isTrex =
  ((selectedDecking?.name || "") as string).toLowerCase().includes("trex");


  if (!isTrex) return null;

  return (
    <div className="estimate-panel" style={{ marginTop: 12 }}>
      <div
        className="estimate-panel__disclosure"
        role="button"
        tabIndex={0}
        onClick={() => setShowDeckingLevels((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setShowDeckingLevels((v) => !v);
          }
        }}
        aria-expanded={showDeckingLevels}
      >
        <span className="estimate-panel__disclosure-label">
          Decking Tier Pricing
        </span>
        <span
          className={`estimate-panel__chev ${showDeckingLevels ? "is-open" : ""}`}
          aria-hidden="true"
        >
          ▾
        </span>
      </div>

     {showDeckingLevels && (() => {
  // Shared 3-column grid so the header and every data row land on the exact
  // same columns. The label cell is min-width:0 so a long name (or the
  // "(current)" marker) wraps instead of pushing the numbers past the card
  // edge, where .tesla-estimate-card's overflow:hidden would clip them.
  //
  // The leading 18px track is the show-on-proposal checkbox. Total (84px) and
  // Δ (72px) keep their exact widths and the label cell stays the only
  // flexible one, so the checkbox costs the label ~22px and the price columns
  // nothing — measured 114px → 92px at a 1200px window, same wrapped height.
  const tierGrid: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "18px minmax(0, 1fr) 84px 72px",
    columnGap: 8,
    alignItems: "center",
    padding: "10px 24px",
  };
  const tierNum: React.CSSProperties = {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    minWidth: 0,
  };

  const toggleTier = (level: string) =>
    setProposalDeckingOptions((prev) =>
      prev.includes(level)
        ? prev.filter((l) => l !== level)
        : [...prev, level]
    );

  return (
  <div className="estimate-panel__rows">
    {/* What the checkboxes are for. Sits inside the panel so it only appears
        once the list is expanded. */}
    <div
      style={{
        padding: "8px 24px 0",
        fontSize: 11,
        lineHeight: 1.35,
        color: "var(--ink-3)",
      }}
    >
      Check tiers to show on proposal
    </div>

    {/* header row — same grid + same 24px inset as the data rows */}
    <div
      style={{
        ...tierGrid,
        paddingTop: 4,
        paddingBottom: 8,
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: "var(--ink-3)",
      }}
    >
      <div aria-hidden="true" />
      <div style={{ minWidth: 0 }}>Level</div>
      <div style={tierNum}>Total</div>
      <div style={tierNum}>Δ</div>
    </div>

    {(trexLevelsWhatIf?.rows || []).map((r) => {
      const isCurrent = isCurrentDeckingTier(r.level);
      // Only a tier we couldn't price at all is unpickable — it would have
      // nothing to print. Cheaper tiers are selectable like any other: the
      // proposal lists the ladder in both directions.
      const canShowOnProposal =
        typeof r.total === "number" && Number.isFinite(r.total);
      const isPicked = proposalDeckingOptions.includes(r.level);

      return (
        <div
          key={r.level}
          style={{
            ...tierGrid,
            borderTop: "1px solid var(--border)",
            fontSize: 13,
            fontWeight: isCurrent ? 700 : 500,
            color: isCurrent ? "var(--ink)" : "var(--ink-2)",
          }}
        >
          {/* Show-on-proposal flag. Independent of the decking SELECTION —
              it feeds the proposal only, never computeEstimate, so ticking
              a box cannot move the estimate total. The CURRENT tier is
              checkable too; it prints with an "as quoted" badge. */}
          <input
            type="checkbox"
            checked={isPicked}
            disabled={!canShowOnProposal}
            onChange={() => toggleTier(r.level)}
            aria-label={`Show ${r.level} on the proposal`}
            style={{
              width: 14,
              height: 14,
              margin: 0,
              cursor: canShowOnProposal ? "pointer" : "not-allowed",
            }}
          />

          <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
            {r.level}
            {isCurrent ? (
              <div
                style={{
                  marginTop: 2,
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "var(--ink-3)",
                }}
              >
                Current
              </div>
            ) : null}
          </div>

          <div style={tierNum}>{r.labelTotal}</div>
          <div style={{ ...tierNum, color: "var(--ink-3)" }}>
            {r.labelDiff}
          </div>
        </div>
      );
    })}

    {/* If rows didn't compute (missing data) */}
    {trexLevelsWhatIf && (trexLevelsWhatIf.rows || []).length === 0 && (
      <div
        style={{
          ...tierGrid,
          borderTop: "1px solid var(--border)",
          fontSize: 13,
          color: "var(--ink-3)",
        }}
      >
        <div aria-hidden="true" />
        <div style={{ minWidth: 0 }}>—</div>
        <div style={tierNum}>—</div>
        <div style={tierNum}>—</div>
      </div>
    )}
  </div>
  );
})()}

    </div>
  );
})()}

            </aside>
          )}
          
        </div>
      </main>

      {appDialog.element}

      <ConfirmNewProjectModal
        open={confirmNewOpen}
        onCancel={cancelNew}
        onDiscard={discardAndNew}
        onSave={saveAndNew}
      />

      <MissingFieldsModal
        missing={missingSections}
        onGoBack={() => setMissingSections(null)}
        onContinue={acknowledgeMissing}
      />

      <FrozenSyncModal
        open={syncModalOpen}
        diffs={frozenPriceDiffs}
        snapshotAt={pricingSnapshotAt}
        onCancel={() => setSyncModalOpen(false)}
        onAccept={handleAcceptFrozenSync}
      />

      {/* =============================== */}
      {/* EMAIL MODAL (TEMP TEST) */}
      {/* =============================== */}
      {emailModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={() => setEmailModalOpen(false)}
        >
          <div
            style={{
              width: "min(720px, 95vw)",
              background: "#fff",
              borderRadius: 12,
              padding: 16,
              boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 8 }}>
              Send Estimate
            </div>

            <div style={{ fontSize: 13, marginBottom: 8 }}>
              <div>
                <b>To:</b>{" "}
                {(emailDraft?.to || clientEmail || "").trim() || "(empty)"}
              </div>

              <div>
                <b>Subject:</b> {emailDraft?.subject || "(empty)"}
              </div>

              {/* ✅ STEP 2C: Show CC only when toggle is ON */}
              {emailDraft?.sendMeCopy &&
              (userSettings?.userEmail || "").trim() ? (
                <div>
                  <b>CC:</b> {(userSettings?.userEmail || "").trim()}
                </div>
              ) : null}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                margin: "10px 0 12px",
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                Send me a copy
              </span>

              <label
                style={{
                  position: "relative",
                  display: "inline-block",
                  width: 34,
                  height: 18,
                }}
              >
                <input
                  type="checkbox"
                  checked={sendMeCopy}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSendMeCopy(checked);
                    setEmailDraft((d) =>
                      d ? { ...d, sendMeCopy: checked } : d
                    );
                  }}
                />

                <span
                  style={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: sendMeCopy ? "#16a34a" : "#d1d5db",
                    borderRadius: 999,
                    transition: "0.2s",
                  }}
                />

                {/* Knob */}
                <span
                  style={{
                    position: "absolute",
                    height: 14,
                    width: 14,
                    left: sendMeCopy ? 18 : 2,
                    top: 2,
                    backgroundColor: "#fff",
                    borderRadius: "50%",
                    transition: "0.2s",
                  }}
                />
              </label>
            </div>

            <textarea
              value={emailDraft?.body || ""}
              onChange={(e) =>
                setEmailDraft((d) => (d ? { ...d, body: e.target.value } : d))
              }
              style={{
                fontFamily:
                  'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
                fontSize: "14px",
                lineHeight: "1.5",
                padding: "10px",
                resize: "none",
                height: "180px",
                width: "100%",
              }}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 12,
              }}
            >
              <button onClick={() => setEmailModalOpen(false)}>Cancel</button>

              <button
                onClick={handleSendEmailFromModal}
                style={{ fontWeight: 700 }}
                disabled={!emailDraft}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===============================
// CONFIRM MODAL
// ===============================
// ===============================
// MISSING-FIELDS ALERT
// ===============================
// Fires on the way to the proposal when a scope category is blank or
// half-filled. Deliberately NOT a blocker — plenty of jobs really have no
// stairs or no demo — but the user has to look at the list and choose.
function MissingFieldsModal({
  missing,
  onGoBack,
  onContinue,
}: {
  missing: MissingSection[] | null;
  onGoBack: () => void;
  onContinue: () => void;
}) {
  if (!missing || missing.length === 0) return null;

  const partials = missing.filter((m) => m.kind === "partial").length;

  return ReactDOM.createPortal(
    <div
      className="du-modal-overlay"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="du-missing-title"
    >
      <div className="du-modal-card">
        <div className="du-modal-title" id="du-missing-title">
          {missing.length === 1
            ? "1 thing to check"
            : `${missing.length} things to check`}
        </div>
        <div className="du-modal-subtitle">
          {partials > 0
            ? "Check these before the proposal goes out — a half-filled category prices as $0."
            : "Check these before the proposal goes out. If the job really doesn't include them, continue."}
        </div>

        <ul className="du-missing-list">
          {missing.map((m) => (
            <li
              key={m.key}
              className={
                m.kind === "partial"
                  ? "du-missing-item du-missing-item--partial"
                  : "du-missing-item"
              }
            >
              <span className="du-missing-label">{m.label}</span>
              <span className="du-missing-detail">{m.detail}</span>
            </li>
          ))}
        </ul>

        <div className="du-modal-actions">
          <button
            type="button"
            className="du-btn du-btn-ghost"
            onClick={onContinue}
          >
            Continue anyway
          </button>

          <div className="du-modal-actions-right">
            <button
              type="button"
              className="du-btn du-btn-primary"
              onClick={onGoBack}
              autoFocus
            >
              Go back and fix
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ConfirmNewProjectModal({
  open,
  onCancel,
  onDiscard,
  onSave,
}: {
  open: boolean;
  onCancel: () => void;
  onDiscard: () => void;
  onSave: () => void;
}) {
  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="du-modal-overlay" role="dialog" aria-modal="true">
      <div className="du-modal-card">
        <div className="du-modal-title">Start a new estimate?</div>
        <div className="du-modal-subtitle">
          You have unsaved changes. Choose what to do before starting a new
          estimate.
        </div>

        <div className="du-modal-actions">
          <button
            type="button"
            className="du-btn du-btn-ghost"
            onClick={onCancel}
          >
            Cancel
          </button>

          <div className="du-modal-actions-right">
            <button
              type="button"
              className="du-btn du-btn-secondary"
              onClick={onDiscard}
            >
              Discard &amp; New
            </button>

            <button
              type="button"
              className="du-btn du-btn-primary"
              onClick={onSave}
            >
              Save &amp; New
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ===============================
// FROZEN PRICE SYNC MODAL
// ===============================
function FrozenSyncModal({
  open,
  diffs,
  snapshotAt,
  onCancel,
  onAccept,
}: {
  open: boolean;
  diffs: Array<{
    id: string;
    name: string;
    unit: string;
    oldCost: number;
    newCost: number;
    delta: number;
  }>;
  snapshotAt: string | null;
  onCancel: () => void;
  onAccept: () => void;
}) {
  if (!open) return null;

  const fmt = (n: number) =>
    `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return ReactDOM.createPortal(
    <div className="du-modal-overlay" role="dialog" aria-modal="true">
      <div
        className="du-modal-card"
        style={{ maxWidth: 720, width: "92vw" }}
      >
        <div className="du-modal-title">Update frozen prices?</div>
        <div className="du-modal-subtitle">
          This file uses prices captured
          {snapshotAt
            ? ` on ${new Date(snapshotAt).toLocaleDateString()}`
            : " at save time"}
          . The items below have changed in the live pricing list. Accepting
          will replace this file's frozen prices with the current values — the
          file remains frozen at the new prices once you save.
        </div>

        <div
          style={{
            margin: "12px 0 8px",
            maxHeight: 360,
            overflowY: "auto",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13,
            }}
          >
            <thead>
              <tr style={{ background: "#f9fafb", textAlign: "left" }}>
                <th style={{ padding: "8px 10px", borderBottom: "1px solid #e5e7eb" }}>
                  Item
                </th>
                <th
                  style={{
                    padding: "8px 10px",
                    borderBottom: "1px solid #e5e7eb",
                    textAlign: "right",
                  }}
                >
                  Frozen
                </th>
                <th
                  style={{
                    padding: "8px 10px",
                    borderBottom: "1px solid #e5e7eb",
                    textAlign: "right",
                  }}
                >
                  Current
                </th>
                <th
                  style={{
                    padding: "8px 10px",
                    borderBottom: "1px solid #e5e7eb",
                    textAlign: "right",
                  }}
                >
                  Δ
                </th>
              </tr>
            </thead>
            <tbody>
              {diffs.map((d) => (
                <tr key={d.id}>
                  <td style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9" }}>
                    <div style={{ fontWeight: 600 }}>{d.name}</div>
                    {d.unit && (
                      <div style={{ fontSize: 11, color: "#6b7280" }}>
                        per {d.unit}
                      </div>
                    )}
                  </td>
                  <td
                    style={{
                      padding: "8px 10px",
                      borderBottom: "1px solid #f1f5f9",
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {fmt(d.oldCost)}
                  </td>
                  <td
                    style={{
                      padding: "8px 10px",
                      borderBottom: "1px solid #f1f5f9",
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {fmt(d.newCost)}
                  </td>
                  <td
                    style={{
                      padding: "8px 10px",
                      borderBottom: "1px solid #f1f5f9",
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      color: d.delta > 0 ? "#b91c1c" : "#047857",
                      fontWeight: 700,
                    }}
                  >
                    {d.delta > 0 ? "+" : ""}
                    {fmt(d.delta)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="du-modal-actions">
          <button
            type="button"
            className="du-btn du-btn-ghost"
            onClick={onCancel}
          >
            Keep frozen prices
          </button>

          <div className="du-modal-actions-right">
            <button
              type="button"
              className="du-btn du-btn-primary"
              onClick={onAccept}
            >
              Update to current prices
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ===============================
// ANALYTICS DASHBOARD (your original)
// ===============================
type CategoryRow = { category: string; before: number; after: number };
type AnalyticsDashboardProps = {
  permitThreshold: number | null;
  baseTotal: number;
  finalTotal: number;
  upliftMultiplier: number;

  financePct: number;
  perceivedPct: number;
  miPct: number;
  permitPct: number;
  smallJobPct: number;

  financeDollars: number;
  perceivedDollars: number;
  miDollars: number;
  permitDollars: number;
  smallJobDollars: number;

  totalUpliftDollars: number;
  categoryRows: CategoryRow[];
};
function AnalyticsDashboard(props: AnalyticsDashboardProps) {
  return (
    <>
      {/*
{upliftCards.map((c) => (
  <div key={c.label} className="tesla-card">
    <div className="tesla-kicker">{c.label}</div>
    <div className="tesla-big">{money0(c.value)}</div>
  </div>
))}

<div className="tesla-card tesla-card--hero">
  <div className="tesla-kicker">Final Estimate</div>
  <div className="tesla-big">{money0(finalTotal)}</div>

  <div className="tesla-sub">
    Total Uplift{" "}
    <span className="tesla-mono">{money0(totalUpliftDollars)}</span>
  </div>

  <div className="tesla-sub" style={{ opacity: 0.7 }}>
    Category total check:{" "}
    <span className="tesla-mono">{money0(totalAfter)}</span>
  </div>
</div>
*/}
    </>
  );
}
export default App;
